import React, { useEffect, useMemo, useRef, useState } from 'react';
import AppShell from '@/components/AppShell';
import CompanyGate from '@/components/CompanyGate';
import { api } from '@/lib/api';
import { compressImageBlob, blobToFile } from '@/lib/imageCompress';
import { useAuth } from '@/lib/auth';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
// Wave 3.49 — portal-wide carat/piece validation (Rule 1 + Rule 2).
import { validateCaratMovement } from '@/lib/caratValidation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  ArrowLeft, ChevronRight, Gem, Coins, Plus, Upload, Trash2, Pencil,
  CheckCircle2, AlertCircle, ArrowUpFromLine, ArrowDownToLine, Sparkles,
  Circle as CircleIcon, MessageSquare, Send, Shield, Activity, ChevronDown, ChevronUp,
  FileText, CalendarDays, Hammer, Diamond, MoreHorizontal, PauseCircle, PlayCircle, Copy,
  Eye, EyeOff, ArrowRightLeft, AtSign, X as XIcon, Timer, AlertTriangle,
  Package, Bell, BellOff, Users as UsersIcon, Loader2, XCircle, Component, Flag,
  Cpu, Lock, FileSpreadsheet,
  StickyNote, Scale,
  Image as ImageIcon,
} from 'lucide-react';
import { TransferMaterialDialog } from '@/components/orders/TransferMaterialDialog';
import { RepairReceiveDialog } from '@/components/repairs/RepairReceiveDialog';
import { ChangeKarigarDialog } from '@/components/orderDetail/ChangeKarigarDialog';
import { ChangeCustomerDialog } from '@/components/orderDetail/ChangeCustomerDialog';
import { ChangeGoldsmithDialog } from '@/components/orderDetail/ChangeGoldsmithDialog';
import { ReferenceEditor } from '@/components/orderDetail/ReferenceEditor';import { OrderDesignSection } from '@/components/orderDetail/OrderDesignSection';import { ScrollToTopButton } from '@/components/ScrollToTopButton';
import { StickySectionNav } from '@/components/StickySectionNav';
import { KarigarFormDialog } from '@/components/orderDetail/KarigarFormDialog';
import { ProductionChecklistSection } from '@/components/orderDetail/ProductionChecklistSection';
import { EditOrderSlaDialog } from '@/components/production/EditOrderSlaDialog';
import { formatINR, formatCarats, formatGrams, formatDateTime, formatDate, daysSinceIST, formatDaysSince, formatRequirementDropdownLabel } from '@/lib/format';
import { sortDiamondItems, hierarchyRank as diamondHierarchyRank } from '@/lib/diamondHierarchy';
import { StageBadge } from './Orders';
import { extractError } from '@/lib/errors';
import { showGoldShortfallToast, computeGoldShortfallClient, reconcileGoldFromCad } from '@/lib/goldShortfall';
import DeleteConfirmDialog from '@/components/DeleteConfirmDialog';
import ReturnDialog from '@/components/phase7/ReturnDialog';
import MemoLink from '@/components/phase7/MemoLink';
import ReturnMemoLink from '@/components/phase7/ReturnMemoLink';
import { PdfDownloadDialog } from '@/components/phase11/PdfDownloadDialog';
import { PurityFormDialog } from '@/components/phase10/PurityFormDialog';
import { ClassificationFormDialog } from '@/components/phase10/ClassificationFormDialog';
import { OrderCommentsProvider, useOrderComments } from '@/components/orderDetail/OrderCommentsContext';
import { CommentCalloutBadge, InlineCommentList } from '@/components/orderDetail/CommentCalloutBadge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
// Wave 3.99n — Section tint helper (coloured mat + matching left strip)
// used to visually distinguish long-page sections.
import { sectionMat } from '@/lib/sectionTints';

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { effectiveCompanyId } = useAuth();
  return (
    <AppShell
      title="Order Detail"
      subtitle="Stage progression, materials, requirements, and history."
      right={<Button variant="outline" className="h-9" onClick={() => navigate(-1)} data-testid="order-back-button"><ArrowLeft size={14} className="mr-1" /> Back</Button>}
    >
      <CompanyGate>
        <OrderCommentsProvider orderId={id}>
          <OrderDetailContent id={id} companyId={effectiveCompanyId} />
        </OrderCommentsProvider>
      </CompanyGate>
      {/* Wave 3.99j — Global back-to-top pill. Fixed to bottom-right,
          visible only when the page has scrolled down (and is actually
          scrollable). Lives outside CompanyGate so the FAB is only
          rendered after the page has actual content behind it. */}
      <ScrollToTopButton />
    </AppShell>
  );
}

function OrderDetailContent({ id, companyId }) {
  const { user: currentUser, isSuperAdmin, isCompanyAdmin, isSalesRole, materialScope, hasPermission } = useAuth();
  // Wave 3.99l — Aggregate material action scopes so we can hide top-level
  // Issue / Return affordances when the operator lacks issue rights on
  // every material.
  const issueScope = React.useMemo(() => materialScope('issue'), [materialScope]);
  const canIssueAnyMaterial = issueScope.length > 0;
  // Wave 3.99m — Aggregate see scope so we can suppress material-only
  // chips (Procurement / Requirements / Ledger) when the user has zero
  // material visibility.
  const seeScope = React.useMemo(() => materialScope('see'), [materialScope]);
  const canSeeAnyMaterial = seeScope.length > 0;
  // Wave 3.99o — Gate the per-order Activity Log section by
  // ``logs.view`` so users without the Activity Logs permission
  // don't see the section, the chip, or fetch the endpoint.
  const canViewLogs = hasPermission('logs.view');
  // Wave 3.99aa — Composite "can edit this order form" gate reused for
  // title / jewellery type / customer edit affordances. Users with the
  // Edit Order Forms permission (or admin roles) get access; the
  // per-editor components additionally block once the order is
  // Completed / Cancelled so the fields are frozen for audit.
  const canEditOrderForm = !!(
    isSuperAdmin || isCompanyAdmin || hasPermission('orders.edit') || hasPermission('orders.edit_header')
  );
  // Wave 3.99m — Comments count comes from the shared comments context
  // populated by OrderCommentsProvider (mounted in the outer component).
  const { comments: allComments } = useOrderComments();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [stagesMeta, setStagesMeta] = useState(null);
  const [stageOpen, setStageOpen] = useState(false);
  const [stageForm, setStageForm] = useState({ stage: '', substage: '', pending_reason: '', note: '' });
  const [holdOpen, setHoldOpen] = useState(false);
  const [holdReason, setHoldReason] = useState('');
  const [returnOpen, setReturnOpen] = useState(false);
  const [orderPdfDialog, setOrderPdfDialog] = useState({ open: false, mode: 'download' });
  const [rmlPdfDialog, setRmlPdfDialog] = useState({ open: false, mode: 'download' });
  // Wave 3.21 — Controlled state for the Multi-Row Issue dialog so it can be
  // launched from the consolidated Actions dropdown.
  const [issueOpen, setIssueOpen] = useState(false);
  // Wave 3.45 — Repair-type orders open a separate "Receive Repaired Piece"
  // dialog (strict reconciliation worksheet) instead of the normal
  // /receive-jewellery wizard.
  const [repairReceiveOpen, setRepairReceiveOpen] = useState(false);
  const [repairReceiveItem, setRepairReceiveItem] = useState(null);
  const [repairLoading, setRepairLoading] = useState(false);
  // Wave 3.22 — Duplicate Order Form confirmation dialog state + busy flag.
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  // Wave 3.71 — Change Karigar dialog state. Hidden entirely if any
  // materials are already issued or the current user isn't CA/SA.
  const [changeKarigarOpen, setChangeKarigarOpen] = useState(false);
  // Edit Order Header — Change Goldsmith (in-place reassignment) dialog
  // state. Gated by `canEditOrderForm` and hidden the moment any raw
  // material is issued (mirrors the backend guard).
  const [changeGoldsmithOpen, setChangeGoldsmithOpen] = useState(false);
  // ERP attachment size cap (KB) — Super-Admin configurable; default 500.
  const [erpCapKb, setErpCapKb] = useState(500);
  // Delete Order Form dialog state (CA/SA only, blocked once materials issued).
  const [deleteOrderOpen, setDeleteOrderOpen] = useState(false);
  const [deletingOrder, setDeletingOrder] = useState(false);
  // Wave 3.99i — Change Customer dialog (gated to SA/CA + not-completed).
  const [changeCustomerOpen, setChangeCustomerOpen] = useState(false);
  // Wave 3.78 — Edit Production SLA dialog.
  const [slaEditOpen, setSlaEditOpen] = useState(false);
  // Wave 1.1 — value of goods currently with this goldsmith attributed to THIS order
  const [goldsmithValueOnOrder, setGoldsmithValueOnOrder] = useState(null);
  // Wave 3.25 hot-fix — A 1-minute heartbeat used as ``now`` for the IST
  // aging pill so it auto-rolls past midnight without a page refresh.
  // We intentionally avoid a per-second tick to skip needless re-renders.
  const [nowTick, setNowTick] = useState(() => new Date());
  useEffect(() => {
    const tick = setInterval(() => setNowTick(new Date()), 60 * 1000);
    return () => clearInterval(tick);
  }, []);
  // Wave 4.x — Reflect the Order No in the browser tab title so operators who
  // open several order forms in separate tabs can tell them apart at a glance.
  useEffect(() => {
    const prev = document.title;
    if (order?.order_no) document.title = `${order.order_no} · Order Form`;
    return () => { document.title = prev; };
  }, [order?.order_no]);
  const canHold = isSuperAdmin || isCompanyAdmin;

  const load = async () => {
    const [o, s] = await Promise.all([
      api.get(`/orders/${id}`),
      api.get('/orders/stages'),
    ]);
    setOrder(o.data); setStagesMeta(s.data);
    setStageForm({ stage: o.data.stage, substage: o.data.substage || '', pending_reason: o.data.pending_reason || '', note: '' });
    // Wave 1.1 — fetch attributed balance for this order, if there is a goldsmith assigned
    if (o.data.goldsmith_id) {
      try {
        const { data: bal } = await api.get(`/goldsmith-issues/balance/by-goldsmith/${o.data.goldsmith_id}`);
        const ord = (bal.by_order || []).find((b) => b.order_id === id);
        setGoldsmithValueOnOrder(ord ? Number(ord.attributed_value || 0) : 0);
      } catch { setGoldsmithValueOnOrder(null); }
    } else {
      setGoldsmithValueOnOrder(null);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Load the Super-Admin ERP attachment cap once on mount.
  useEffect(() => {
    api.get('/system/attachment-caps')
      .then(({ data }) => setErpCapKb(Number(data?.erp_kb) || 500))
      .catch(() => { /* keep 500 default */ });
  }, []);

  const updateStage = async () => {
    try {
      await api.post(`/orders/${id}/stage`, {
        stage: stageForm.stage, substage: stageForm.substage || null,
        pending_reason: stageForm.pending_reason || null, note: stageForm.note,
      });
      toast.success('Stage updated'); setStageOpen(false); load();
    } catch (e) { toast.error(extractError(e, 'Stage update failed')); }
  };

  const submitHold = async (newStatus) => {
    try {
      const payload = { hold_status: newStatus };
      if (newStatus === 'on_hold') payload.reason = holdReason.trim();
      await api.post(`/orders/${id}/hold`, payload);
      toast.success(newStatus === 'on_hold' ? 'Order put on hold' : 'Order resumed');
      setHoldOpen(false); setHoldReason(''); load();
    } catch (e) { toast.error(extractError(e, 'Hold update failed')); }
  };

  if (!order || !stagesMeta) return <div className="text-slate-500 py-8">Loading…</div>;
  const allStages = stagesMeta.main_stages;
  const currentIdx = Math.max(0, allStages.indexOf(order.stage));
  const isOnHold = order.hold_status === 'on_hold';
  // Wave 3.45 — Repair-type orders use a different Receive flow.
  const isRepairOrder = order.order_type === 'repair' || !!order.customer_jewellery_item_id;

  const openRepairReceive = async () => {
    if (!order.customer_jewellery_item_id) {
      toast.error('This repair order is not linked to a repair item');
      return;
    }
    setRepairLoading(true);
    try {
      const { data } = await api.get(`/repairs/items/${order.customer_jewellery_item_id}`);
      setRepairReceiveItem(data);
      setRepairReceiveOpen(true);
    } catch (e) {
      toast.error(extractError(e, 'Failed to load repair item'));
    } finally {
      setRepairLoading(false);
    }
  };
  // Wave 3.99ac — Single entry point for the "Receive Jewellery" flow.
  // Repairs open the repair-receive dialog; standard jewellery orders go
  // to the receive wizard. This is now launched from the Delivery
  // checklist step (the standalone Actions item was removed for standard
  // orders per the shop's real workflow: Delivery == piece received back
  // from the goldsmith, which starts the receipt).
  const startReceiveFlow = () => {
    if (isRepairOrder) {
      openRepairReceive();
    } else {
      navigate(`/orders/${order.id}/receive-jewellery`);
    }
  };
  // Wave 1.1 (revised) — days since order creation, IST-based so the count
  // ticks at India midnight regardless of the user's browser timezone.
  // Wave 3.25 hot-fix — re-evaluate every minute so the pill auto-rolls
  // over when IST midnight passes while the user keeps the page open.
  const orderDays = order.created_at ? Math.max(0, daysSinceIST(order.created_at, nowTick) ?? 0) : null;
  const orderDaysLabel = order.created_at ? formatDaysSince(order.created_at, nowTick) : null;
  const isCompleted = order.stage === 'Completed';

  // Edit Order Header — the Change Goldsmith affordance is locked the
  // moment ANY raw material is issued. Mirror the backend guard exactly:
  // it blocks on a non-empty embedded `gold_issues` array OR any
  // goldsmith-issue line (surfaced as `has_material_issues`).
  const hasAnyMaterialIssued = !!(
    order.has_material_issues
    || (Array.isArray(order.gold_issues) && order.gold_issues.length > 0)
  );

  // Wave 3.99k — Sticky section-nav configuration. Each entry maps to
  // a stable ``id`` on the section wrapper below.
  // Wave 3.99m — Each chip now surfaces a small badge count derived
  // from the readily available data on ``order`` / the comments
  // context. Sections whose data loads asynchronously inside a
  // sub-component (Procurement / Ledger / Received Jewellery /
  // Activity Log / Checklist) are intentionally left without a badge
  // for now — the chip still renders as a jump anchor. Chips that
  // lead to sections which would render only a "no access" empty
  // state are hidden entirely when the user has zero material see
  // scope.
  const requirementsTotal = (order.requirements || []).length;
  const requirementsPending = (order.requirements || []).filter((r) => {
    const s = (r?.status || 'pending').toLowerCase();
    return !['complete', 'completed', 'done'].includes(s);
  }).length;
  const filesCount = (order.images || []).length;
  const historyCount = (order.stage_history || []).length;
  const commentsCount = (allComments || []).length;

  const SECTIONS = [
    { id: 'order-overview', label: 'Overview', tint: 'teal' },
    { id: 'order-files', label: 'Files', tint: 'sky', badge: filesCount || undefined },
    // Procurement / Requirements / Ledger are all material-driven — hide
    // the chip entirely when the operator has no material see scope so
    // they don't jump into an empty section.
    ...(canSeeAnyMaterial ? [{ id: 'order-procurement', label: 'Procurement', tint: 'amber' }] : []),
    { id: 'order-checklist', label: 'Checklist', tint: 'emerald' },
    ...(canSeeAnyMaterial
      ? [{
          id: 'order-requirements',
          label: 'Requirements',
          tint: 'violet',
          // Wave 3.99m — Requirements use ``pending/total`` semantics
          // per user request (all other sections use a plain total).
          badge: requirementsTotal > 0 ? `${requirementsPending}/${requirementsTotal}` : undefined,
        }]
      : []),
    ...(canSeeAnyMaterial ? [{ id: 'order-ledger', label: 'Raw Material Ledger', tint: 'indigo' }] : []),
    { id: 'order-received-jewellery', label: 'Received Jewellery', tint: 'rose' },
    { id: 'order-history', label: 'History', tint: 'stone', badge: historyCount || undefined },
    { id: 'order-comments', label: 'Comments', tint: 'purple', badge: commentsCount || undefined },
    ...(canViewLogs
      ? [{ id: 'order-activity', label: 'Activity Log', tint: 'slate' }]
      : []),
  ];

  return (
    <div className="space-y-5 relative">
      {isCompleted && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-[5]"
          data-testid="order-detail-completed-watermark"
          style={{
            backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(
              "<svg xmlns='http://www.w3.org/2000/svg' width='360' height='210'><text x='24' y='130' font-family='Montserrat, Arial, sans-serif' font-size='30' font-weight='700' letter-spacing='4' fill='#059669' fill-opacity='0.07' transform='rotate(-28 180 105)'>COMPLETED</text></svg>"
            )}")`,
            backgroundRepeat: 'repeat',
          }}
        />
      )}
      <section id="order-overview" className={sectionMat('teal')}>
      <Card className="p-5">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="font-mono text-xl bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)] px-3 py-1 rounded-lg" data-testid="order-detail-no">{order.order_no}</div>
              <ReferenceEditor
                order={order}
                companyId={companyId}
                canEdit={canEditOrderForm}
                onUpdated={load}
              />
              <OrderDesignSection
                order={order}
                companyId={companyId}
                canEdit={canEditOrderForm && !isSalesRole}
                onUpdated={load}
              />
              <StageBadge stage={order.stage} substage={order.substage} holdStatus={order.hold_status} />
              {/* Wave 3.83 — SLA tracking scope indicator (only shown when partial/off) */}
              {(() => {
                const s = order.sla_scope || {};
                const off = s.timeline === false && s.procurement === false;
                const partial = !off && (s.timeline === false || s.procurement === false);
                if (!off && !partial) return null;
                return (
                  <Badge
                    className={off
                      ? 'bg-rose-50 text-rose-700 border-rose-200 font-mono text-[11px]'
                      : 'bg-amber-50 text-amber-800 border-amber-200 font-mono text-[11px]'}
                    title={off
                      ? 'SLA tracking is fully OFF for this order (no delay alerts)'
                      : `SLA tracking partial · timeline: ${s.timeline === false ? 'OFF' : 'ON'}, procurement: ${s.procurement === false ? 'OFF' : 'ON'}`}
                    data-testid="order-detail-sla-scope-badge"
                  >
                    <BellOff size={11} className="mr-1" />
                    {off ? 'SLA Off' : 'SLA Partial'}
                  </Badge>
                );
              })()}
              <JewelleryPurityEditor order={order} onUpdated={load} canEditForm={canEditOrderForm} />
              <JewelleryTypeEditor order={order} onUpdated={load} canEditForm={canEditOrderForm} />
              <InHouseCadEditor order={order} onUpdated={load} />
              <ExternalCadWeightEditor order={order} onUpdated={load} />
              {order.ghat_confirmed && (
                <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 font-mono text-[11px]" data-testid="order-detail-ghat-badge">
                  <CheckCircle2 size={11} className="mr-1" />
                  Ghat: {formatGrams(order.ghat_weight_grams || 0)} g
                </Badge>
              )}
              {order.pending_reason && <Badge className="bg-rose-50 text-rose-700">Blocked: {order.pending_reason}</Badge>}
              {/* Wave 1.1 — Days since created + Value with Goldsmith */}
              {orderDays !== null && (
                <Badge variant="outline" className="font-mono text-[11px] bg-slate-50 text-slate-700 border-slate-300" data-testid="order-detail-days-badge">
                  <CalendarDays size={11} className="mr-1" />
                  {orderDaysLabel}
                </Badge>
              )}
              {goldsmithValueOnOrder !== null && (
                <Badge variant="outline" className="font-mono text-[11px] bg-amber-50 text-amber-800 border-amber-300" data-testid="order-detail-goldsmith-value-badge">
                  <Hammer size={11} className="mr-1" />
                  With Goldsmith: {formatINR(goldsmithValueOnOrder)}
                </Badge>
              )}
            </div>
            <div className="mt-2">
              <OrderTitleEditor order={order} onUpdated={load} canEditForm={canEditOrderForm} />
              <div className="text-xs text-slate-500 flex items-center gap-1 flex-wrap">
                <span>
                  Goldsmith: <span className="text-sm font-semibold text-slate-800" data-testid="order-detail-goldsmith">{order.goldsmith_name}</span>
                </span>
                {/* Edit Order Header — Change Goldsmith (in-place) pencil.
                    Visibility rules:
                    • Requires the Edit Order Header permission
                      (`orders.edit_header`, implied by `orders.edit` and
                      by CA / SA) via `canEditOrderForm`.
                    • Hidden the moment any raw material is issued so the
                      ledgers stay coherent (mirrors the backend guard).
                    • Hidden on Cancelled / Completed orders. */}
                {canEditOrderForm
                  && !hasAnyMaterialIssued
                  && order.stage !== 'Cancelled'
                  && order.stage !== 'Completed'
                  && (
                    <button
                      type="button"
                      onClick={() => setChangeGoldsmithOpen(true)}
                      className="ml-0.5 inline-flex items-center gap-0.5 rounded p-1 text-slate-400 hover:text-[var(--ink-navy)] hover:bg-slate-100 transition-colors"
                      title="Change Goldsmith — creates a new order form for the new goldsmith and removes this one"
                      data-testid="order-detail-change-goldsmith-button"
                    >
                      <Pencil size={12} />
                    </button>
                  )}
                {/* Wave 3.71 — Change Karigar affordance (heavier action —
                    cancels this order and creates a new one under a fresh
                    number). Visibility rules:
                    • Only CA / SA may perform this.
                    • Hidden the moment any raw material is issued
                      (`has_material_issues` becomes true) so ledgers stay
                      coherent.
                    • Hidden on Cancelled / Completed orders — nothing to
                      re-target. */}
                {(isSuperAdmin || isCompanyAdmin)
                  && !order.has_material_issues
                  && order.stage !== 'Cancelled'
                  && order.stage !== 'Completed'
                  && (
                    <button
                      type="button"
                      onClick={() => setChangeKarigarOpen(true)}
                      className="ml-0.5 inline-flex items-center gap-0.5 rounded p-1 text-slate-400 hover:text-[var(--ink-navy)] hover:bg-slate-100 transition-colors"
                      title="Change Karigar — cancels this order and creates a new one"
                      data-testid="order-detail-change-karigar-button"
                    >
                      <ArrowRightLeft size={12} />
                    </button>
                  )}
                {/* Wave 3.99i — Change Customer affordance. Shown to
                    Super Admin + Company Admin as long as the order is
                    not yet Completed. Also shown when no customer is
                    linked so an operator can add one. */}
                {/* Wave 3.99aa — Also allow anyone with orders.edit to
                    change the customer (previously SA/CA only). */}
                {canEditOrderForm && order.stage !== 'Completed' && (
                  order.customer_name ? (
                    <>
                      <span>·</span>
                      <span data-testid="order-detail-customer" className="inline-flex items-center gap-0.5">
                        Customer: <span className="text-sm font-semibold text-slate-800">{order.customer_name}</span>
                        <button
                          type="button"
                          onClick={() => setChangeCustomerOpen(true)}
                          className="ml-0.5 inline-flex items-center gap-0.5 rounded p-1 text-slate-400 hover:text-[var(--ink-navy)] hover:bg-slate-100 transition-colors"
                          title="Change customer"
                          data-testid="order-detail-change-customer-button"
                        >
                          <Pencil size={12} />
                        </button>
                      </span>
                    </>
                  ) : (
                    <>
                      <span>·</span>
                      <button
                        type="button"
                        onClick={() => setChangeCustomerOpen(true)}
                        className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-slate-500 hover:text-[var(--ink-navy)] hover:bg-slate-100 transition-colors text-[12px]"
                        title="Link a customer to this order"
                        data-testid="order-detail-add-customer-button"
                      >
                        <Pencil size={12} /> Add customer
                      </button>
                    </>
                  )
                )}
                {/* Fallback for users without edit rights: read-only customer chip */}
                {order.customer_name && !(canEditOrderForm && order.stage !== 'Completed') && (
                  <>
                    <span>·</span>
                    <span data-testid="order-detail-customer">
                      Customer: <span className="text-sm font-semibold text-slate-800">{order.customer_name}</span>
                    </span>
                  </>
                )}
              </div>
              {/* Wave 3.87 — Editable Description. Shown even when empty
                  so the operator can add one. */}
              <OrderDescriptionEditor order={order} onUpdated={load} />
              {/* Wave 3.71 — Shifted-to banner. Shown ONLY on the cancelled
                  source order so the operator knows exactly where the work
                  now lives. */}
              {order.stage === 'Cancelled' && order.karigar_shifted_to_order_no && (
                <div
                  className="mt-2 px-3 py-2 bg-slate-100 border border-slate-300 rounded-md text-xs text-slate-700 flex items-center gap-2 flex-wrap"
                  data-testid="order-shifted-banner"
                >
                  <ArrowRightLeft size={12} className="text-slate-500" />
                  <span>
                    This order was cancelled and shifted to{' '}
                    <span className="font-medium text-[var(--ink-navy)]">
                      {order.karigar_shifted_to_goldsmith_name || 'a new Karigar'}
                    </span>
                    {' · '}
                    New order:{' '}
                    <Link
                      to={`/orders/${order.karigar_shifted_to_order_id}`}
                      className="font-mono font-medium text-[var(--ink-navy)] underline decoration-dotted hover:decoration-solid"
                      data-testid="order-shifted-banner-link"
                    >
                      {order.karigar_shifted_to_order_no}
                    </Link>
                  </span>
                </div>
              )}
              {/* Wave 3.71 — Inverse banner on the NEW order so the operator
                  can jump back to the (cancelled) source for context. */}
              {order.karigar_shifted_from_order_no && (
                <div
                  className="mt-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-xs text-slate-600 flex items-center gap-2 flex-wrap"
                  data-testid="order-shifted-from-banner"
                >
                  <ArrowRightLeft size={12} className="text-slate-400" />
                  <span>
                    Karigar shifted from{' '}
                    <span className="font-medium">{order.karigar_previous_name || '—'}</span>
                    {' · '}
                    Original order:{' '}
                    <Link
                      to={`/orders/${order.karigar_shifted_from_order_id}`}
                      className="font-mono text-slate-700 underline decoration-dotted hover:decoration-solid"
                      data-testid="order-shifted-from-banner-link"
                    >
                      {order.karigar_shifted_from_order_no}
                    </Link>
                    {order.karigar_shift_reason && (
                      <>
                        {' · '}
                        <span className="italic">{order.karigar_shift_reason}</span>
                      </>
                    )}
                  </span>
                </div>
              )}
              {/* Wave 3.87 — Editable Special Comments callout. */}
              <OrderSpecialCommentsEditor order={order} onUpdated={load} canEditForm={canEditOrderForm} />
              {isOnHold && (
                <div className="mt-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-900" data-testid="order-hold-banner">
                  <span className="font-medium">On Hold</span>{order.hold_reason ? ` — ${order.hold_reason}` : ''}
                  {order.hold_by_email && <span className="text-amber-700"> · by {order.hold_by_email}</span>}
                  {order.hold_at && <span className="text-amber-700"> · {formatDateTime(order.hold_at)}</span>}
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            {isSalesRole && (
              <span
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-[12px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-200"
                data-testid="order-detail-sales-readonly-badge"
                title="Your role has read-only access. You can view and comment, but cannot edit or export."
              >
                <Eye size={13} /> View only
              </span>
            )}
            {/* Wave 3.21 — Consolidated Actions menu. Combines: Issue Material,
                Return Material, Receive Jewellery, Hold/Resume, Order Form PDF.
                Stays compact while keeping Update Stage as the primary CTA.
                Hidden entirely for view-only Sales roles (no mutations/exports). */}
            {!isSalesRole && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="h-9"
                  data-testid="order-actions-menu-trigger"
                >
                  <MoreHorizontal size={14} className="mr-1.5" />
                  Actions
                  <ChevronDown size={12} className="ml-1.5 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-56"
                data-testid="order-actions-menu-content"
              >
                <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-slate-500">
                  Order Actions
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {!isCompleted && canIssueAnyMaterial && (
                  <DropdownMenuItem
                    onSelect={(e) => { e.preventDefault(); setIssueOpen(true); }}
                    data-testid="order-actions-issue"
                    className="cursor-pointer"
                  >
                    <ArrowUpFromLine size={14} className="mr-2 text-[var(--ink-navy)]" />
                    Issue Material
                  </DropdownMenuItem>
                )}
                {!isCompleted && order.goldsmith_id && canIssueAnyMaterial && (
                  <DropdownMenuItem
                    onSelect={(e) => { e.preventDefault(); setReturnOpen(true); }}
                    data-testid="order-actions-return"
                    className="cursor-pointer text-amber-700 focus:text-amber-700"
                  >
                    <ArrowDownToLine size={14} className="mr-2" />
                    Return Material
                  </DropdownMenuItem>
                )}
                {!isCompleted && order.goldsmith_id && isRepairOrder && (
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      openRepairReceive();
                    }}
                    data-testid="order-actions-receive-jewellery"
                    className="cursor-pointer text-emerald-700 focus:text-emerald-700"
                    disabled={repairLoading}
                  >
                    <Diamond size={14} className="mr-2" />
                    Receive Repaired Piece
                  </DropdownMenuItem>
                )}
                {canHold && !isCompleted && (
                  <DropdownMenuItem
                    onSelect={(e) => { e.preventDefault(); setHoldReason(''); setHoldOpen(true); }}
                    data-testid="order-actions-hold-toggle"
                    className={`cursor-pointer ${isOnHold ? 'text-emerald-700 focus:text-emerald-700' : 'text-amber-700 focus:text-amber-700'}`}
                  >
                    {isOnHold ? (
                      <>
                        <PlayCircle size={14} className="mr-2" />
                        Resume Order
                      </>
                    ) : (
                      <>
                        <PauseCircle size={14} className="mr-2" />
                        Put on Hold
                      </>
                    )}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                {/* Wave 3.78 — Edit Production SLA + per-stage notify recipients. */}
                {!isCompleted && order.stage !== 'Cancelled' && (
                  <DropdownMenuItem
                    onSelect={(e) => { e.preventDefault(); setSlaEditOpen(true); }}
                    data-testid="order-actions-edit-sla"
                    className="cursor-pointer text-[var(--ink-navy)]"
                  >
                    <Timer size={14} className="mr-2" />
                    Edit Production SLA
                  </DropdownMenuItem>
                )}
                {/* Wave 3.79 — Print Timeline PDF (available for every order) */}
                <DropdownMenuItem
                  onSelect={async (e) => {
                    e.preventDefault();
                    try {
                      const res = await api.get(
                        `/production-timeline/orders/${order.id}/pdf`,
                        { responseType: 'blob' },
                      );
                      const url = URL.createObjectURL(res.data);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `timeline-${order.order_no || order.id}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      setTimeout(() => URL.revokeObjectURL(url), 1000);
                    } catch (err) {
                      toast.error('Failed to download timeline PDF');
                    }
                  }}
                  data-testid="order-actions-print-timeline"
                  className="cursor-pointer text-[var(--ink-navy)]"
                >
                  <Activity size={14} className="mr-2" />
                  Print Production Timeline
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={(e) => { e.preventDefault(); setOrderPdfDialog({ open: true, mode: 'download' }); }}
                  data-testid="order-actions-print"
                  className="cursor-pointer text-blue-700 focus:text-blue-700"
                >
                  <FileText size={14} className="mr-2" />
                  Order Form PDF
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={(e) => { e.preventDefault(); setDuplicateOpen(true); }}
                  data-testid="order-actions-duplicate"
                  className="cursor-pointer text-violet-700 focus:text-violet-700"
                >
                  <Copy size={14} className="mr-2" />
                  Duplicate Order Form
                </DropdownMenuItem>
                {/* Wave 3.89 — Stage-change moved into Actions menu.
                    Normal progression is driven by the Production Checklist
                    itself; this stays here for edge-case admin transitions
                    (e.g. force to Cancelled, or roll back a corner-case
                    stage) and is limited to CA/SA. */}
                {(isSuperAdmin || isCompanyAdmin) && !isCompleted && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={(e) => { e.preventDefault(); setStageOpen(true); }}
                      data-testid="order-actions-change-stage"
                      className="cursor-pointer text-slate-700 focus:text-slate-700"
                    >
                      <Activity size={14} className="mr-2" />
                      Change Stage (admin)
                    </DropdownMenuItem>
                    {order.stage !== 'Cancelled' && (
                      <DropdownMenuItem
                        onSelect={(e) => {
                          e.preventDefault();
                          setStageForm({
                            stage: 'Cancelled',
                            substage: '',
                            pending_reason: '',
                            note: 'Order cancelled',
                          });
                          setStageOpen(true);
                        }}
                        data-testid="order-actions-cancel"
                        className="cursor-pointer text-rose-700 focus:text-rose-700"
                      >
                        <XCircle size={14} className="mr-2" />
                        Cancel Order
                      </DropdownMenuItem>
                    )}
                  </>
                )}
                {/* Delete Order Form — CA / SA only, and only while no
                    material has been issued (mirrors Change-Karigar guard).
                    Destructive + irreversible, so it sits last with a
                    dedicated confirmation dialog. */}
                {(isSuperAdmin || isCompanyAdmin) && !order.has_material_issues && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={(e) => { e.preventDefault(); setDeleteOrderOpen(true); }}
                      data-testid="order-actions-delete"
                      className="cursor-pointer text-rose-700 focus:text-rose-700"
                    >
                      <Trash2 size={14} className="mr-2" />
                      Delete Order Form
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            )}

            {/* Hidden host for the Multi-Row Issue dialog — opened via the
                Actions menu above. */}
            {!isCompleted && (
              <MultiRowIssueDialog
                order={order}
                companyId={companyId}
                reload={load}
                open={issueOpen}
                onOpenChange={setIssueOpen}
                hideTrigger
              />
            )}

            {/* Wave 3.89 — Update Stage dialog is still available for the
                admin escape-hatch (opened from Actions menu), but the
                primary Update Stage button has been removed. */}
            <Dialog open={stageOpen} onOpenChange={setStageOpen}>
              <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Update Stage</DialogTitle><DialogDescription>Move this order to a new stage — history will be preserved.</DialogDescription></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Stage</Label>
                    <Select value={stageForm.stage} onValueChange={(v) => setStageForm({ ...stageForm, stage: v, substage: v === 'In Production' ? stageForm.substage : '' })}>
                      <SelectTrigger data-testid="stage-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {stagesMeta.main_stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {stageForm.stage === 'In Production' && (
                    <div><Label>Substage</Label>
                      <Select value={stageForm.substage || 'none'} onValueChange={(v) => setStageForm({ ...stageForm, substage: v === 'none' ? '' : v })}>
                        <SelectTrigger data-testid="substage-select"><SelectValue placeholder="Select substage" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {stagesMeta.production_substages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div><Label>Pending reason (if blocked)</Label><Input value={stageForm.pending_reason} onChange={(e) => setStageForm({ ...stageForm, pending_reason: e.target.value })} data-testid="stage-pending-reason-input" /></div>
                  <div><Label>Note</Label><Textarea value={stageForm.note} onChange={(e) => setStageForm({ ...stageForm, note: e.target.value })} /></div>
                </div>
                <DialogFooter><Button variant="outline" onClick={() => setStageOpen(false)}>Cancel</Button><Button onClick={updateStage} className="luxury-button" data-testid="stage-submit-button">Update</Button></DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={holdOpen} onOpenChange={setHoldOpen}>
              <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{isOnHold ? 'Resume Order' : 'Put Order on Hold'}</DialogTitle>
                  <DialogDescription>
                    {isOnHold
                      ? 'Resuming will reactivate this order. Stage updates will be allowed again.'
                      : 'On-hold orders are paused: stage updates and material issues are blocked until you resume. Only Company Admin or Super Admin can change this.'}
                  </DialogDescription>
                </DialogHeader>
                {!isOnHold && (
                  <div>
                    <Label>Reason <span className="text-rose-600">*</span></Label>
                    <Textarea
                      value={holdReason}
                      onChange={(e) => setHoldReason(e.target.value)}
                      placeholder="e.g. Customer hold, awaiting design change, etc."
                      rows={3}
                      data-testid="order-hold-reason-input"
                    />
                  </div>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setHoldOpen(false)}>Cancel</Button>
                  <Button
                    onClick={() => submitHold(isOnHold ? 'active' : 'on_hold')}
                    disabled={!isOnHold && !holdReason.trim()}
                    className={isOnHold ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-amber-600 hover:bg-amber-700 text-white'}
                    data-testid="order-hold-confirm-button"
                  >
                    {isOnHold ? 'Resume Order' : 'Put on Hold'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Wave 3.22 — Duplicate Order Form confirmation. Copies basics
                + requirements + images + special comments + purity + party
                + goldsmith + expected delivery date. Resets stage, history,
                fulfilment counters and ledger rows. Navigates to the new
                order on success. */}
            <Dialog open={duplicateOpen} onOpenChange={(v) => { if (!duplicating) setDuplicateOpen(v); }}>
              <DialogContent className="max-w-lg" data-testid="order-duplicate-dialog">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Copy size={16} className="text-violet-700" />
                    Duplicate Order Form
                  </DialogTitle>
                  <DialogDescription>
                    Create a new order form that mirrors this one. Useful for repeat orders
                    of the same item.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-2 text-xs">
                  <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2.5">
                    <div className="text-[11px] font-semibold uppercase text-emerald-800 tracking-wide mb-1">Will be copied</div>
                    <ul className="list-disc list-inside space-y-0.5 text-emerald-900">
                      <li>Customer / party + goldsmith</li>
                      <li>Expected delivery date</li>
                      <li>Gold purity ({order.jewellery_purity_name})</li>
                      <li>Requirement list ({(order.requirements || []).length} item{(order.requirements || []).length === 1 ? '' : 's'})</li>
                      <li>Special comments &amp; reference images</li>
                    </ul>
                  </div>
                  <div className="rounded-md border border-rose-200 bg-rose-50 p-2.5">
                    <div className="text-[11px] font-semibold uppercase text-rose-800 tracking-wide mb-1">Will NOT be copied</div>
                    <ul className="list-disc list-inside space-y-0.5 text-rose-900">
                      <li>Raw material issued / returned / received</li>
                      <li>Stage history (resets to <b>Issued</b>)</li>
                      <li>Ghat confirmation</li>
                    </ul>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDuplicateOpen(false)} disabled={duplicating}>
                    Cancel
                  </Button>
                  <Button
                    onClick={async () => {
                      if (duplicating) return;
                      setDuplicating(true);
                      try {
                        const res = await api.post(`/orders/${order.id}/duplicate`);
                        const newId = res.data?.id;
                        const newNo = res.data?.order_no;
                        toast.success(`Duplicated as ${newNo || 'new order'}`);
                        setDuplicateOpen(false);
                        if (newId) navigate(`/orders/${newId}`);
                      } catch (e) {
                        toast.error(extractError(e, 'Failed to duplicate order'));
                      } finally {
                        setDuplicating(false);
                      }
                    }}
                    className="bg-violet-600 hover:bg-violet-700 text-white"
                    disabled={duplicating}
                    data-testid="order-duplicate-confirm-button"
                  >
                    {duplicating ? 'Duplicating…' : (<><Copy size={14} className="mr-1.5" /> Duplicate</>)}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            {/* Wave 3.71 — Change Karigar (cancel-and-recreate) dialog. */}
            <ChangeKarigarDialog
              open={changeKarigarOpen}
              onOpenChange={setChangeKarigarOpen}
              order={order}
              companyId={companyId}
              onShifted={(newOrder) => {
                setChangeKarigarOpen(false);
                if (newOrder?.id) {
                  // Navigate to the fresh order so the operator sees the
                  // new artefact immediately.
                  navigate(`/orders/${newOrder.id}`);
                } else {
                  load();
                }
              }}
            />
            {/* Change Customer dialog. */}
            <ChangeCustomerDialog
              open={changeCustomerOpen}
              onOpenChange={setChangeCustomerOpen}
              order={order}
              companyId={companyId}
              onSaved={load}
            />
            {/* Edit Order Header — Change Goldsmith (cancel-and-recreate under a
                new order number for the new goldsmith; old order deleted). */}
            <ChangeGoldsmithDialog
              open={changeGoldsmithOpen}
              onOpenChange={setChangeGoldsmithOpen}
              order={order}
              companyId={companyId}
              onSaved={(newOrder) => {
                setChangeGoldsmithOpen(false);
                if (newOrder?.id) {
                  navigate(`/orders/${newOrder.id}`);
                } else {
                  load();
                }
              }}
            />
            {/* Delete Order Form — irreversible confirmation. Direct delete
                for CA / SA (no approval workflow); guarded server-side by the
                same "no material issued" rule. */}
            <AlertDialog open={deleteOrderOpen} onOpenChange={(v) => { if (!deletingOrder) setDeleteOrderOpen(v); }}>
              <AlertDialogContent data-testid="delete-order-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2 text-rose-700">
                    <Trash2 size={16} />
                    Delete this order form?
                  </AlertDialogTitle>
                  <AlertDialogDescription asChild>
                    <div className="space-y-2 text-sm text-slate-600">
                      <div>
                        You are about to permanently delete order{' '}
                        <span className="font-mono font-medium text-[var(--ink-navy)]">{order.order_no}</span>
                        {order.customer_name ? <> for <span className="font-medium">{order.customer_name}</span></> : null}.
                      </div>
                      <div className="rounded-md border border-rose-200 bg-rose-50 p-2.5 text-[13px] text-rose-800">
                        This action is <strong>not reversible</strong>. The order form, its comments and
                        stage history will be removed. It is allowed only because no material has been
                        issued on this order.
                      </div>
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={deletingOrder} data-testid="delete-order-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    disabled={deletingOrder}
                    onClick={async (e) => {
                      e.preventDefault();
                      setDeletingOrder(true);
                      try {
                        await api.delete(`/orders/${order.id}`);
                        toast.success(`Order ${order.order_no} deleted.`);
                        setDeleteOrderOpen(false);
                        navigate('/orders');
                      } catch (err) {
                        toast.error(extractError(err, 'Failed to delete order'));
                        setDeletingOrder(false);
                      }
                    }}
                    className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600"
                    data-testid="delete-order-confirm"
                  >
                    {deletingOrder ? (
                      <><Loader2 size={14} className="mr-1.5 animate-spin" /> Deleting…</>
                    ) : (
                      <><Trash2 size={14} className="mr-1.5" /> Delete Order</>
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            {/* Wave 3.78/3.82/3.83 — Edit Production SLA + per-stage notify + procurement config + scope. */}
            <EditOrderSlaDialog
              open={slaEditOpen}
              onOpenChange={setSlaEditOpen}
              order={order}
              companyId={companyId}
              canToggleTracking={!!(isSuperAdmin || isCompanyAdmin)}
              onSaved={load}
            />
          </div>
        </div>
        <div className="mt-5 flex flex-wrap gap-2 items-center">
          {allStages.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`px-3 py-1.5 rounded-md text-xs ${i <= currentIdx ? 'bg-[var(--ink-navy)] text-white' : 'bg-slate-100 text-slate-500'}`}>{s}</div>
              {i < allStages.length - 1 && <ChevronRight size={12} className="text-slate-400" />}
            </React.Fragment>
          ))}
        </div>
      </Card>
      </section>

      {/* Wave 3.99k — Sticky section-nav chip bar. Placed immediately
          after the Overview so it activates as soon as the user
          scrolls past the header. */}
      <StickySectionNav sections={SECTIONS} topOffset={56} />

      <section id="order-files" className={sectionMat('sky')}>
        <ImagesSection order={order} reload={load} erpCapKb={erpCapKb} />
      </section>

      <section id="order-procurement" className={sectionMat('amber')}>
        {/* Wave 3.82 — Material Procurement panel (parallel raw-material tracks).
            Placed immediately above the on-page Production Timeline (Milestones)
            so operators see procurement status alongside fabrication milestones. */}
        <MaterialProcurementSection order={order} companyId={companyId} reload={load} />
      </section>

      <section id="order-checklist" className={sectionMat('emerald')}>
        {/* Wave 3.85 — Manual Production Checklist. Wave 3.86 absorbed the
            old Milestones section into this component so weights, notes,
            timelines, and comment-callouts for CAD/CAM/Chamka/Ghat now live
            here (colour-coded by SLA). Setting Complete is also handled
            inline. */}
        <ProductionChecklistSection order={order} reload={load} onReceiveJewellery={startReceiveFlow} />
      </section>

      <section id="order-requirements" className={sectionMat('violet')}>
        <RequirementsSection order={order} companyId={companyId} reload={load} />
      </section>

      <section id="order-ledger" className={sectionMat('indigo')}>
        <RawMaterialLedgerSection
          order={order}
          reload={load}
          onOpenRmlPdf={isSalesRole ? undefined : () => setRmlPdfDialog({ open: true, mode: 'download' })}
        />
      </section>

      <section id="order-received-jewellery" className={sectionMat('rose')}>
        {/* Wave 3.22 — Received Jewellery against this order. One card per
            jewellery doc with a thumbnail, key totals, and a quick-link
            to the full Bill of Materials. */}
        <ReceivedJewellerySection orderId={order.id} />
      </section>

      <section id="order-history" className={sectionMat('stone')}>
        <Card className="p-5">
          <div className="font-medium text-[var(--ink-navy)] mb-3">Stage History</div>
          <div className="divide-y divide-[var(--divider)] text-sm">
            {(order.stage_history || []).map((h) => (
              <div key={h.id} className="py-2 flex items-start gap-3">
                <Badge variant="secondary" className="text-[10px]">{h.stage}{h.substage ? ` · ${h.substage}` : ''}</Badge>
                <div className="flex-1">
                  {h.pending_reason && <div className="text-xs text-rose-700">Blocked: {h.pending_reason}</div>}
                  {h.note && <div className="text-xs text-slate-600">{h.note}</div>}
                  <div className="text-[11px] text-slate-400">by {h.changed_by_email}</div>
                </div>
                <div className="text-[11px] text-slate-500 whitespace-nowrap">{formatDateTime(h.timestamp)}</div>
              </div>
            ))}
          </div>
        </Card>
      </section>

      <section id="order-comments" className={sectionMat('purple')}>
        <OrderCommentsSection order={order} />
      </section>

      {canViewLogs && (
        <section id="order-activity" className={sectionMat('slate')}>
          <OrderActivityLogSection orderId={order.id} />
        </section>
      )}

      {returnOpen && (
        <ReturnDialog
          open={returnOpen}
          onOpenChange={setReturnOpen}
          companyId={companyId}
          goldsmithId={order.goldsmith_id}
          goldsmithName={order.goldsmith_name}
          filter={{ orderId: order.id }}
          onSuccess={() => { setReturnOpen(false); load(); }}
        />
      )}

      {/* Phase 11 — Order Form PDF download dialog with branding opt-in */}
      <PdfDownloadDialog
        open={orderPdfDialog.open}
        onOpenChange={(o) => setOrderPdfDialog({ ...orderPdfDialog, open: o })}
        title={`Order Form PDF — ${order.order_no}`}
        description="Choose whether to print the company logo and name on this order form."
        companyId={order.company_id}
        endpoint={`/orders/${order.id}/pdf`}
        filename={`OrderForm_${(order.order_no || order.id).replace(/\//g, '-')}.pdf`}
        mode={orderPdfDialog.mode}
      />

      {/* Phase 19 — Raw Material Ledger PDF download */}
      <PdfDownloadDialog
        open={rmlPdfDialog.open}
        onOpenChange={(o) => setRmlPdfDialog({ ...rmlPdfDialog, open: o })}
        title={`Raw Material Ledger PDF — ${order.order_no}`}
        description="Choose whether to print the company logo and name on this ledger."
        companyId={order.company_id}
        endpoint={`/orders/${order.id}/raw-material-ledger/pdf`}
        filename={`RawMaterialLedger_${(order.order_no || order.id).replace(/\//g, '-')}.pdf`}
        mode={rmlPdfDialog.mode}
      />

      {/* Wave 3.45 — Repair-type orders use the Strict Reconciliation
          Worksheet (RepairReceiveDialog) instead of the standard
          /receive-jewellery wizard. Inline dialog launched from the
          Actions menu via openRepairReceive(). */}
      {repairReceiveOpen && repairReceiveItem && (
        <RepairReceiveDialog
          open={repairReceiveOpen}
          onOpenChange={(v) => {
            setRepairReceiveOpen(v);
            if (!v) setRepairReceiveItem(null);
          }}
          item={repairReceiveItem}
          companyId={order.company_id}
          onCompleted={() => {
            setRepairReceiveOpen(false);
            setRepairReceiveItem(null);
            load();
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// ============================================================================
// Phase 9 hotfix — Per-order Activity Log
// Surfaces every action performed against this order in chronological (newest
// first) order: creation, edits, stage changes, raw-material issues / returns,
// comments, ghat confirmation, hold/resume, requirements, image uploads, etc.
// Each row carries: action label, summary, user (name/email), IST timestamp.
// ============================================================================
function OrderActivityLogSection({ orderId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/orders/${orderId}/activity-log`)
      .then(({ data }) => { if (!cancelled) setEvents(data?.events || []); })
      .catch(() => { if (!cancelled) setEvents([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [orderId]);

  const colorFor = (action) => {
    switch (action) {
      case 'issue': return 'bg-amber-50 text-amber-700';
      case 'return': return 'bg-emerald-50 text-emerald-700';
      case 'stage_change': return 'bg-blue-50 text-blue-700';
      case 'comment': return 'bg-violet-50 text-violet-700';
      case 'ghat_confirm': return 'bg-orange-50 text-orange-700';
      case 'hold': return 'bg-rose-50 text-rose-700';
      case 'resume': return 'bg-emerald-50 text-emerald-700';
      case 'create': return 'bg-slate-100 text-slate-700';
      case 'update': return 'bg-slate-100 text-slate-600';
      case 'delete': return 'bg-rose-100 text-rose-700';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  return (
    <Card className="p-5" data-testid="order-activity-log">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center justify-between w-full text-left"
        data-testid="order-activity-log-toggle"
      >
        <div className="flex items-center gap-2">
          <Activity size={16} className="text-slate-600" />
          <div>
            <div className="font-medium text-[var(--ink-navy)]">Activity Log</div>
            <div className="text-xs text-slate-500">All actions performed on this order — newest first.</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-[10px]" data-testid="order-activity-log-count">{events.length}</Badge>
          {expanded ? <ChevronUp size={16} className="text-slate-500" /> : <ChevronDown size={16} className="text-slate-500" />}
        </div>
      </button>
      {expanded && (
        <div className="mt-3">
          {loading && (
            <div className="text-xs text-slate-500" data-testid="order-activity-log-loading">Loading…</div>
          )}
          {!loading && events.length === 0 && (
            <div className="text-sm text-slate-500 py-4 text-center border border-dashed border-[var(--divider)] rounded-md" data-testid="order-activity-log-empty">
              No activity recorded yet.
            </div>
          )}
          {!loading && events.length > 0 && (
            <div className="divide-y divide-[var(--divider)] text-sm" data-testid="order-activity-log-list">
              {events.map((e) => (
                <div key={e.id} className="py-2.5 flex items-start gap-3" data-testid={`order-activity-log-row-${e.id}`}>
                  <Badge className={`text-[10px] shrink-0 ${colorFor(e.action)}`} data-testid={`order-activity-log-action-${e.id}`}>
                    {e.label}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    {e.summary && (
                      <div className="text-xs text-slate-700 break-words" data-testid={`order-activity-log-summary-${e.id}`}>
                        {e.summary}
                      </div>
                    )}
                    {!e.summary && e.entity_label && (
                      <div className="text-xs text-slate-700 font-mono">{e.entity_label}</div>
                    )}
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      by <span className="font-medium text-slate-700" data-testid={`order-activity-log-user-${e.id}`}>{e.user_name || e.user_email || '—'}</span>
                    </div>
                  </div>
                  <div className="text-[11px] text-slate-500 whitespace-nowrap shrink-0" data-testid={`order-activity-log-ts-${e.id}`}>
                    {formatDateTime(e.timestamp)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}


// Phase 8.6 — Order Comments Section + Deep-link auto-scroll
// Wave 3.26 — Topic + subtopic + sub-subtopic taxonomy for contextual badges.
// ============================================================================

// Helper to derive issued packets for a given material slug from the order.
function _packetOptionsForMaterial(order, materialSlug) {
  if (!order) return [];
  const issueFieldMap = {
    diamond: 'diamond_issues',
    color_stone: 'color_stone_issues',
    pearl: 'pearl_issues',
  };
  const field = issueFieldMap[materialSlug];
  if (!field) return []; // gold has no packets
  const issues = order[field] || [];
  const seen = new Map();
  for (const e of issues) {
    if (!e.packet_id) continue;
    if (!seen.has(e.packet_id)) {
      seen.set(e.packet_id, { id: e.packet_id, packet_no: e.packet_no || '—' });
    }
  }
  return Array.from(seen.values());
}

const COMMENT_TOPIC_OPTIONS = [
  { value: 'general', label: 'General' },
  { value: 'ghat', label: 'Ghat' },
  { value: 'requirement', label: 'Requirement' },
  { value: 'raw_material', label: 'Raw Material' },
  { value: 'setting', label: 'Setting' },
  { value: 'qc', label: 'QC' },
];

const COMMENT_MATERIAL_OPTIONS = [
  { value: 'gold', label: 'Gold' },
  { value: 'diamond', label: 'Diamonds' },
  { value: 'color_stone', label: 'Colour Stones' },
  { value: 'pearl', label: 'Pearls' },
];

// Setting comments pick the raw material being set (no Gold — only stones /
// pearls). QC comments pick a test head. Both are REQUIRED sub-selections and
// map onto ``subtopic_id`` for the matching topic.
const COMMENT_SETTING_MATERIAL_OPTIONS = [
  { value: 'diamond', label: 'Diamonds' },
  { value: 'color_stone', label: 'Colour Stones' },
  { value: 'pearl', label: 'Pearls' },
];

const COMMENT_QC_HEAD_OPTIONS = [
  { value: 'gold_testing', label: 'Gold Testing' },
  { value: 'cvd_testing', label: 'CVD Testing' },
];

// Wave 3.65 — Ghat sub-milestones dropdown for the comment composer.  The
// backend accepts these values as ``subtopic_id`` when ``topic='ghat'``.
// Wave 3.73 — Added ``chamka`` between CAM and Ghat.
const COMMENT_GHAT_MILESTONE_OPTIONS = [
  { value: 'overall', label: 'Overall Ghat' },
  { value: 'cad', label: 'CAD' },
  { value: 'cam', label: 'CAM' },
  { value: 'chamka', label: 'Chamka' },
  { value: 'ghat', label: 'Ghat' },
];

function OrderCommentsSection({ order }) {
  const { user: me } = useAuth();
  const location = useLocation();
  const { comments, reload, composerPreset } = useOrderComments();
  const [body, setBody] = useState('');
  const [requiresSA, setRequiresSA] = useState(false);
  const [priority, setPriority] = useState(false);
  const [busy, setBusy] = useState(false);
  const [highlightId, setHighlightId] = useState(null);
  const scrolledRef = useRef(null);
  // Wave 3.68 — root of the composer card so row-level "Add Comment"
  // shortcuts can scroll it into view. The textareaRef below is used
  // for auto-focus after preset arrival.
  const composerRootRef = React.useRef(null);

  // Wave 3.26 — taxonomy form state
  const [topic, setTopic] = useState('general');
  const [subtopicId, setSubtopicId] = useState('');
  const [subSubtopicId, setSubSubtopicId] = useState('');
  // Wave 3.65 — @mention picker state + candidate list.
  const [mentionCandidates, setMentionCandidates] = useState([]);
  const [mentionedIds, setMentionedIds] = useState([]);
  const [mentionPickerOpen, setMentionPickerOpen] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  // Wave 3.66 — Inline @autocomplete state driven by the textarea.
  // ``mentionInlineStart`` is the character index of the leading ``@`` in
  // ``body``; ``mentionInlineQuery`` is the text between ``@`` and the
  // caret. When ``mentionInlineOpen`` is true the composer renders a
  // small floating list below the textarea and intercepts arrow-key
  // navigation until the user picks or dismisses.
  const [mentionInlineOpen, setMentionInlineOpen] = useState(false);
  const [mentionInlineQuery, setMentionInlineQuery] = useState('');
  const [mentionInlineActiveIdx, setMentionInlineActiveIdx] = useState(0);
  const [mentionInlineStart, setMentionInlineStart] = useState(-1);
  const textareaRef = React.useRef(null);

  React.useEffect(() => {
    let cancelled = false;
    api.get(`/orders/${order.id}/mention-candidates`)
      .then((r) => { if (!cancelled) setMentionCandidates(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setMentionCandidates([]); });
    return () => { cancelled = true; };
  }, [order.id]);

  const mentionedUsers = mentionedIds
    .map((id) => mentionCandidates.find((u) => u.id === id))
    .filter(Boolean);
  const mentionFilter = mentionSearch.trim().toLowerCase();
  const filteredMentionCandidates = mentionFilter
    ? mentionCandidates.filter((u) => {
        const hay = `${u.name || ''} ${u.email || ''} ${u.role || ''}`.toLowerCase();
        return hay.includes(mentionFilter);
      })
    : mentionCandidates;
  const toggleMention = (id) => {
    setMentionedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // Wave 3.66 — inline autocomplete helpers ---------------------------------
  // Filter candidates by the inline query, capping at 6 rows so the picker
  // stays compact.  Kept independent of the popover-based ``mentionSearch``
  // so the two entry points don't clobber each other's UI state.
  const inlineFilteredCandidates = React.useMemo(() => {
    const q = (mentionInlineQuery || '').trim().toLowerCase();
    const base = q
      ? mentionCandidates.filter((u) => {
          const hay = `${u.name || ''} ${u.email || ''} ${u.role || ''}`.toLowerCase();
          return hay.includes(q);
        })
      : mentionCandidates;
    return base.slice(0, 6);
  }, [mentionInlineQuery, mentionCandidates]);

  // Keep the highlight in-bounds whenever the filter narrows the list.
  React.useEffect(() => {
    if (mentionInlineActiveIdx >= inlineFilteredCandidates.length) {
      setMentionInlineActiveIdx(0);
    }
  }, [inlineFilteredCandidates.length, mentionInlineActiveIdx]);

  // Look at the text before the caret and decide whether we're inside an
  // ``@word`` fragment (start-of-input OR previous char is whitespace).
  // Returns ``{ start, query }`` on match, ``null`` otherwise.
  const detectMentionTrigger = (value, caret) => {
    const before = value.slice(0, caret);
    // Regex: last ``@`` preceded by start or whitespace, followed by
    // non-whitespace-non-``@`` characters up to the caret.
    const m = /(^|\s)@([^\s@]*)$/.exec(before);
    if (!m) return null;
    const query = m[2] || '';
    const start = caret - query.length - 1; // position of the ``@``
    return { start, query };
  };

  const onBodyChange = (e) => {
    setBody(e.target.value);
    const el = e.target;
    const trig = detectMentionTrigger(el.value, el.selectionStart ?? el.value.length);
    if (trig) {
      setMentionInlineStart(trig.start);
      setMentionInlineQuery(trig.query);
      setMentionInlineOpen(true);
    } else if (mentionInlineOpen) {
      setMentionInlineOpen(false);
      setMentionInlineStart(-1);
      setMentionInlineQuery('');
    }
  };

  // Insert the tag in the textarea and add the user to the chip list.
  const pickInlineMention = (u) => {
    if (!u) return;
    const display = (u.name && u.name.trim()) || (u.email || '').split('@')[0] || 'user';
    const el = textareaRef.current;
    const caret = el ? (el.selectionStart ?? body.length) : body.length;
    const start = mentionInlineStart >= 0 ? mentionInlineStart : caret;
    const beforeAt = body.slice(0, start);
    const afterCaret = body.slice(caret);
    const insertion = `@${display} `;
    const newBody = `${beforeAt}${insertion}${afterCaret}`;
    setBody(newBody);
    setMentionedIds((prev) => (prev.includes(u.id) ? prev : [...prev, u.id]));
    setMentionInlineOpen(false);
    setMentionInlineStart(-1);
    setMentionInlineQuery('');
    // Restore focus + caret position after React commits the new value.
    const newCaret = beforeAt.length + insertion.length;
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        try { textareaRef.current.setSelectionRange(newCaret, newCaret); } catch (_) { /* ignore */ }
      }
    }, 0);
  };

  // Resolve subtopic options based on selected topic.
  const requirementOptions = useMemo(() => {
    const reqs = order?.requirements || [];
    return reqs.map((r, idx) => {
      const mt = (r.material_type || '').toLowerCase();
      // Compose a human-friendly quantity string per material type so two
      // requirements that share the same classification can still be told
      // apart at a glance (Wave 3.26 user feedback).
      //
      // Field map (matches `_resolve_comment_taxonomy` upstream / DB shape):
      //   gold       → required_grams (+ remaining_grams)
      //   non-gold   → required_carats, required_pieces (+ remaining_*)
      let qty = '';
      const isGold = mt === 'gold';
      if (isGold) {
        const reqG = Number(r.required_grams || 0);
        const remG = Number(r.remaining_grams || 0);
        if (reqG > 0) {
          qty = `${reqG.toFixed(3)} g`;
          if (remG > 0 && Math.abs(remG - reqG) > 0.001) {
            qty += ` (${remG.toFixed(3)} g pending)`;
          }
        }
      } else {
        const reqC = Number(r.required_carats || 0);
        const reqP = Number(r.required_pieces || 0);
        const remC = Number(r.remaining_carats || 0);
        const remP = Number(r.remaining_pieces || 0);
        const parts = [];
        if (reqC > 0) parts.push(`${reqC.toFixed(2)} ct`);
        if (reqP > 0) parts.push(`${reqP} pcs`);
        qty = parts.join(' · ');
        // Show pending split if partially fulfilled.
        const partiallyFulfilled = (
          (reqC > 0 && Math.abs(remC - reqC) > 0.001) ||
          (reqP > 0 && remP !== reqP)
        );
        if (partiallyFulfilled) {
          const remParts = [];
          if (remC > 0) remParts.push(`${remC.toFixed(2)} ct`);
          if (remP > 0) remParts.push(`${remP} pcs`);
          if (remParts.length > 0) qty += ` (${remParts.join(' · ')} pending)`;
        }
      }
      const desc = (r.description || '').trim();
      // Trim long descriptions so the option doesn't blow out the dropdown.
      const descShort = desc.length > 60 ? `${desc.slice(0, 60)}…` : desc;
      const status = (r.status || '').toLowerCase();
      // Surface a short creation timestamp (DD-MMM) so two otherwise
      // identical requirements can still be told apart by when they
      // were raised.
      let createdAtShort = '';
      const ts = r.created_at;
      if (ts) {
        try {
          const dt = new Date(ts);
          if (!Number.isNaN(dt.getTime())) {
            createdAtShort = dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
          }
        } catch (_) { /* ignore */ }
      }
      // Compose a sensible label: prefer classification_label, else the
      // description (often the only distinguishing text on color
      // stone/pearl rows). Fall back to "Requirement".
      const labelBase = r.classification_label || r.description || 'Requirement';
      return {
        id: r.id,
        label: labelBase,
        material: mt,
        quantity: qty,
        description: descShort,
        status,
        seq: idx + 1,
        createdAtShort,
      };
    });
  }, [order?.requirements]);

  const subtopicOptions = topic === 'requirement'
    ? requirementOptions
    : topic === 'raw_material'
      ? COMMENT_MATERIAL_OPTIONS
      : topic === 'ghat'
        ? COMMENT_GHAT_MILESTONE_OPTIONS
        : topic === 'setting'
          ? COMMENT_SETTING_MATERIAL_OPTIONS
          : topic === 'qc'
            ? COMMENT_QC_HEAD_OPTIONS
            : [];

  // Sub-subtopic only for raw_material with a chosen material slug.
  const subSubtopicOptions = topic === 'raw_material' && subtopicId
    ? _packetOptionsForMaterial(order, subtopicId)
    : [];

  // Reset dependent fields when topic/subtopic changes.
  useEffect(() => {
    // Wave 3.65 — pre-select the "Overall" bucket when the user picks
    // Ghat so the second dropdown never renders empty (matches the
    // 'requirement'/'raw_material' UX where the user immediately gets a
    // meaningful selection).
    setSubtopicId(topic === 'ghat' ? 'overall' : '');
    setSubSubtopicId('');
  }, [topic]);
  useEffect(() => {
    setSubSubtopicId('');
  }, [subtopicId]);

  // Deep-link to a specific comment via #comment-<id> OR ?commentId=<id>
  // (Wave 3.65 — bell notifications use the query-param form so this handler
  // also consumes location.search).
  useEffect(() => {
    if (comments.length === 0) return;
    let id = null;
    const hash = (location.hash || '').replace(/^#/, '');
    if (hash.startsWith('comment-')) {
      id = hash.slice('comment-'.length);
    } else {
      const sp = new URLSearchParams(location.search || '');
      const q = sp.get('commentId');
      if (q) id = q;
    }
    if (!id || scrolledRef.current === id) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`comment-${id}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightId(id);
        scrolledRef.current = id;
        setTimeout(() => setHighlightId(null), 2500);
      }
    }, 150);
    return () => clearTimeout(t);
  }, [comments, location.hash, location.search]);

  // Resolve label for the subtopic / sub-subtopic from the option lists.
  const resolveSubtopicLabel = () => {
    if (topic === 'requirement') {
      const r = requirementOptions.find((x) => x.id === subtopicId);
      if (!r) return null;
      // Append sequence # + quantity/date when there are duplicates so the
      // persisted label is unambiguous in comment tiles and badge titles.
      const dupes = requirementOptions.filter((x) => x.label === r.label);
      if (dupes.length > 1) {
        const parts = [`#${r.seq}`, r.label];
        const extra = [];
        if (r.quantity) extra.push(r.quantity);
        if (r.createdAtShort) extra.push(`raised ${r.createdAtShort}`);
        if (extra.length > 0) parts.push(`(${extra.join(', ')})`);
        return parts.join(' ');
      }
      return r.label;
    }
    if (topic === 'raw_material') {
      const m = COMMENT_MATERIAL_OPTIONS.find((x) => x.value === subtopicId);
      return m?.label || null;
    }
    if (topic === 'ghat') {
      const m = COMMENT_GHAT_MILESTONE_OPTIONS.find((x) => x.value === subtopicId);
      return m?.label || null;
    }
    if (topic === 'setting') {
      const m = COMMENT_SETTING_MATERIAL_OPTIONS.find((x) => x.value === subtopicId);
      return m?.label || null;
    }
    if (topic === 'qc') {
      const m = COMMENT_QC_HEAD_OPTIONS.find((x) => x.value === subtopicId);
      return m?.label || null;
    }
    return null;
  };
  const resolveSubSubtopicLabel = () => {
    if (topic !== 'raw_material' || !subSubtopicId) return null;
    const p = subSubtopicOptions.find((x) => x.id === subSubtopicId);
    return p?.packet_no || null;
  };

  const submit = async () => {
    const text = (body || '').trim();
    if (!text) return;
    // Validation per topic
    if (topic === 'requirement' && !subtopicId) {
      toast.error('Pick a requirement for this comment'); return;
    }
    if (topic === 'raw_material' && !subtopicId) {
      toast.error('Pick a material category for this comment'); return;
    }
    if (topic === 'setting' && !subtopicId) {
      toast.error('Pick a raw material (Diamonds / Colour Stones / Pearls) for this setting comment'); return;
    }
    if (topic === 'qc' && !subtopicId) {
      toast.error('Pick Gold Testing or CVD Testing for this QC comment'); return;
    }
    setBusy(true);
    try {
      const payload = {
        body: text,
        requires_super_admin_attention: requiresSA,
        priority,
        topic,
        // Wave 3.65 — always send mention list (empty array is safe).
        mentioned_user_ids: mentionedIds,
      };
      if (topic === 'ghat') {
        payload.subtopic_type = 'milestone';
        payload.subtopic_id = subtopicId || 'overall';
        payload.subtopic_label = resolveSubtopicLabel();
      } else if (topic === 'requirement') {
        payload.subtopic_type = 'requirement';
        payload.subtopic_id = subtopicId;
        payload.subtopic_label = resolveSubtopicLabel();
      } else if (topic === 'raw_material') {
        payload.subtopic_type = 'material';
        payload.subtopic_id = subtopicId;
        payload.subtopic_label = resolveSubtopicLabel();
        if (subSubtopicId) {
          payload.sub_subtopic_type = 'packet';
          payload.sub_subtopic_id = subSubtopicId;
          payload.sub_subtopic_label = resolveSubSubtopicLabel();
        }
      } else if (topic === 'setting') {
        payload.subtopic_type = 'setting_material';
        payload.subtopic_id = subtopicId;
        payload.subtopic_label = resolveSubtopicLabel();
      } else if (topic === 'qc') {
        payload.subtopic_type = 'qc_head';
        payload.subtopic_id = subtopicId;
        payload.subtopic_label = resolveSubtopicLabel();
      }
      const { data } = await api.post(`/orders/${order.id}/comments`, payload);
      setBody(''); setRequiresSA(false); setPriority(false);
      setTopic('general'); setSubtopicId(''); setSubSubtopicId('');
      setMentionedIds([]);
      toast.success(`Comment ${data.comment_no} posted${data.requires_super_admin_attention ? ' (Super Admin notified)' : ''}`);
      await reload();
      // Auto-scroll to the new comment
      setTimeout(() => {
        const el = document.getElementById(`comment-${data.id}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setHighlightId(data.id);
          setTimeout(() => setHighlightId(null), 2000);
        }
      }, 200);
    } catch (e) { toast.error(extractError(e, 'Failed to post comment')); }
    finally { setBusy(false); }
  };

  const Enter = (e) => {
    // Wave 3.66 — When inline mention autocomplete is open, arrow keys
    // navigate the picker and Enter/Tab commit the highlighted user.
    // Space also dismisses so the user can just type past the ``@`` if
    // they didn't actually mean to tag anyone.
    if (mentionInlineOpen && inlineFilteredCandidates.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionInlineActiveIdx((i) => (i + 1) % inlineFilteredCandidates.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionInlineActiveIdx((i) => (i - 1 + inlineFilteredCandidates.length) % inlineFilteredCandidates.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        // Don't swallow Ctrl/Cmd+Enter — that still submits.
        if (!(e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          pickInlineMention(inlineFilteredCandidates[mentionInlineActiveIdx] || inlineFilteredCandidates[0]);
          return;
        }
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionInlineOpen(false);
        return;
      }
    }
    // Cmd/Ctrl+Enter submits
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
  };

  // Helper to render a coloured topic pill on each comment tile.
  const topicPill = (c) => {
    const t = (c.topic || 'general').toLowerCase();
    const cls = {
      ghat: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      requirement: 'bg-amber-50 text-amber-800 border-amber-200',
      raw_material: 'bg-sky-50 text-sky-700 border-sky-200',
      setting: 'bg-violet-50 text-violet-700 border-violet-200',
      qc: 'bg-rose-50 text-rose-700 border-rose-200',
      general: 'bg-slate-50 text-slate-600 border-slate-200',
    }[t] || 'bg-slate-50 text-slate-600 border-slate-200';
    const label = {
      ghat: 'Ghat',
      requirement: 'Requirement',
      raw_material: 'Raw Material',
      setting: 'Setting',
      qc: 'QC',
      general: 'General',
    }[t] || 'General';
    const trailing = [];
    if (c.subtopic_label) trailing.push(c.subtopic_label);
    if (c.sub_subtopic_label) trailing.push(c.sub_subtopic_label);
    return (
      <Badge variant="outline" className={`text-[10px] ${cls}`}>
        {label}
        {trailing.length > 0 && (
          <span className="ml-1 font-mono">› {trailing.join(' › ')}</span>
        )}
      </Badge>
    );
  };

  // Wave 3.68 — Row-level "Add Comment" shortcuts publish a preset to
  // the shared OrderCommentsContext. Apply the incoming topic/subtopic
  // scope to our local composer state, scroll the composer into view,
  // and focus the textarea so the operator can start typing immediately.
  React.useEffect(() => {
    if (!composerPreset || !composerPreset.token) return;
    if (composerPreset.topic) setTopic(composerPreset.topic);
    // subtopicId is applied via a queued micro-task because setting the
    // topic first triggers the topic-reset effect that clears subtopicId.
    // We schedule the actual pick after that reset commits.
    const timer = setTimeout(() => {
      if (composerPreset.subtopicId) setSubtopicId(composerPreset.subtopicId);
      if (composerPreset.subSubtopicId) setSubSubtopicId(composerPreset.subSubtopicId);
      // Scroll composer into view + focus textarea — unless this was a silent
      // pre-scope (e.g. opening a checklist callout), in which case we leave
      // the viewport where it is.
      if (composerPreset.scroll !== false) {
        if (composerRootRef.current) {
          composerRootRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        setTimeout(() => {
          if (textareaRef.current) {
            try { textareaRef.current.focus(); } catch (_) { /* ignore */ }
          }
        }, 300);
      }
    }, 30);
    return () => clearTimeout(timer);
  }, [composerPreset?.token]);

  return (
    <Card className="p-5" data-testid="order-comments" ref={composerRootRef}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <MessageSquare size={14} className="text-[var(--ink-navy)]" />
          <div className="font-medium text-[var(--ink-navy)]">Comments</div>
          <Badge variant="secondary" className="text-[10px]">{comments.length}</Badge>
        </div>
        <div className="text-[11px] text-slate-500">
          Tip: Press <kbd className="border border-[var(--divider)] rounded px-1 py-0.5 text-[10px]">⌘</kbd>+
          <kbd className="border border-[var(--divider)] rounded px-1 py-0.5 text-[10px]">Enter</kbd> to send.
        </div>
      </div>

      {/* Wave 3.85 — Composer at TOP, newest comments first via
          flex-col-reverse on the wrapper. The composer's `border-t`
          became a `border-b` because it's now above the list. */}
      <div className="flex flex-col-reverse gap-4">

      {/* Existing comments — reversed so newest appears first. */}
      <div className="space-y-2">
        {comments.length === 0 && (
          <div className="text-sm text-slate-500 py-6 text-center border border-dashed rounded-md">No comments yet — be the first to post.</div>
        )}
        {[...comments].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')).map((c) => {
          const isMine = c.created_by === me.id;
          const isCA = c.created_by_role === 'company_admin';
          const isSA = c.created_by_role === 'super_admin';
          // Wave 3.79 — System-generated comments (production delay alerts, etc.)
          const isSystem = c.is_system || c.created_by === 'system' || c.created_by_role === 'system';
          const isSevere = isSystem && c.sub_subtopic_label === '2x_sla';
          const initials = isSystem
            ? 'SYS'
            : (c.created_by_name || c.created_by_email || '?').slice(0, 2).toUpperCase();
          const highlighted = highlightId === c.id;
          const readBy = (c.read_by || []).filter((r) => r.user_id !== c.created_by);
          const saAcks = c.sa_acknowledged_by || [];
          return (
            <div
              key={c.id}
              id={`comment-${c.id}`}
              className={`flex gap-3 p-3 rounded-md border transition-colors ${
                highlighted
                  ? 'border-amber-300 bg-amber-50/60'
                  : isSevere
                    ? 'border-red-300 bg-red-50/60'
                    : isSystem
                      ? 'border-amber-200 bg-amber-50/40'
                      : 'border-[var(--divider)] bg-white hover:bg-slate-50'
              }`}
              data-testid={`order-comment-${c.id}`}
            >
              <div className={`h-8 w-8 rounded-full grid place-items-center text-[11px] font-medium ${
                isSevere ? 'bg-red-100 text-red-700'
                  : isSystem ? 'bg-amber-100 text-amber-800'
                  : isSA ? 'bg-violet-100 text-violet-700'
                  : isCA ? 'bg-blue-100 text-blue-700'
                  : 'bg-slate-100 text-slate-700'
              }`}>
                {isSystem ? <AlertCircle size={13} /> : initials}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  <span className="font-medium text-[var(--ink-navy)]">
                    {isSystem ? 'Production Alert · Neutral Tag Domain' : (c.created_by_name || c.created_by_email)}
                  </span>
                  {isSevere && <Badge className="bg-red-100 text-red-700 border-red-300 text-[10px]"><AlertTriangle size={9} className="mr-0.5" />CRITICAL · 2× SLA</Badge>}
                  {isSystem && !isSevere && <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-[10px]"><Timer size={9} className="mr-0.5" />SLA Breach</Badge>}
                  {isSA && !isSystem && <Badge className="bg-violet-50 text-violet-700 text-[10px]"><Shield size={9} className="mr-0.5" />Super Admin</Badge>}
                  {isCA && !isSystem && <Badge className="bg-blue-50 text-blue-700 text-[10px]">Company Admin</Badge>}
                  {isMine && !isSystem && <Badge variant="secondary" className="text-[10px]">You</Badge>}
                  <span className="font-mono text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{c.comment_no}</span>
                  <span className="text-slate-400 text-[10px]">{formatDateTime(c.created_at)}</span>
                  {!isSystem && topicPill(c)}
                  {c.priority && (
                    <Badge className="bg-rose-50 text-rose-700 border border-rose-200 text-[10px]" data-testid={`order-comment-priority-badge-${c.id}`}><Flag size={9} className="mr-0.5" />Priority</Badge>
                  )}
                  {c.requires_super_admin_attention && (
                    saAcks.length > 0
                      ? <Badge className="bg-emerald-50 text-emerald-700 text-[10px]"><CheckCircle2 size={9} className="mr-0.5" />SA acknowledged</Badge>
                      : <Badge className="bg-amber-50 text-amber-700 text-[10px]"><Shield size={9} className="mr-0.5" />Awaiting Super Admin</Badge>
                  )}
                </div>
                <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap break-words" data-testid={`order-comment-body-${c.id}`}>{c.body}</div>
                {/* Wave 3.65 — Mentioned users chip row */}
                {(c.mentioned_users || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1 items-center text-[11px]">
                    <span className="text-slate-400">Mentioned:</span>
                    {(c.mentioned_users || []).map((mu) => (
                      <Badge
                        key={mu.id}
                        className="bg-amber-50 text-amber-800 border border-amber-200 text-[10px] font-medium"
                        data-testid={`order-comment-mentioned-${c.id}-${mu.id}`}
                        title={mu.email}
                      >
                        <AtSign size={9} className="mr-0.5" />
                        {mu.name || mu.email.split('@')[0]}
                      </Badge>
                    ))}
                  </div>
                )}
                {/* Phase 9.10 — Read receipts */}
                {(readBy.length > 0 || saAcks.length > 0) && (
                  <div className="mt-2 pt-2 border-t border-dashed border-[var(--divider)] flex flex-wrap gap-3 text-[10px] text-slate-500" data-testid={`order-comment-readers-${c.id}`}>
                    {saAcks.map((r, i) => (
                      <span key={`sa-${i}`} className="inline-flex items-center gap-1 text-emerald-700">
                        <Shield size={9} />
                        Acknowledged by Super Admin <span className="font-medium">{r.user_name || r.user_email}</span> at {formatDateTime(r.acknowledged_at)}
                      </span>
                    ))}
                    {readBy.map((r, i) => (
                      <span key={`r-${i}`} className="inline-flex items-center gap-1">
                        Read by <span className="font-medium text-slate-700">{r.user_name || r.user_email}</span>
                        {r.role && <span className="opacity-70">({r.role.replace('_', ' ')})</span>}
                        at {formatDateTime(r.read_at)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Composer */}
      <div className="border-b border-[var(--divider)] pb-3 space-y-2">
        {/* Wave 3.26 — Topic / Subtopic selectors */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Topic</span>
            <Select value={topic} onValueChange={setTopic}>
              <SelectTrigger className="h-8 w-[140px]" data-testid="order-comment-topic-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COMMENT_TOPIC_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`order-comment-topic-${o.value}`}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {topic === 'ghat' && (
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-slate-500">›</span>
              <Select value={subtopicId || 'overall'} onValueChange={setSubtopicId}>
                <SelectTrigger className="h-8 w-[160px]" data-testid="order-comment-ghat-milestone-select">
                  <SelectValue placeholder="Milestone" />
                </SelectTrigger>
                <SelectContent>
                  {COMMENT_GHAT_MILESTONE_OPTIONS.map((o) => (
                    <SelectItem
                      key={o.value}
                      value={o.value}
                      data-testid={`order-comment-ghat-milestone-${o.value}`}
                    >
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {topic === 'requirement' && (
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-slate-500">›</span>
              <div className="min-w-[280px] max-w-[360px] flex-1">
                <SearchableSelect
                  items={requirementOptions}
                  value={subtopicId}
                  onValueChange={(v) => setSubtopicId(v || '')}
                  itemToLabel={(r) => (
                    <span className="flex items-center gap-1.5">
                      <span className="font-mono text-[10px] text-slate-400">#{r.seq}</span>
                      <span className="truncate">{r.label}</span>
                      {r.quantity ? <span className="font-mono text-[10px] text-slate-500 hidden sm:inline">({r.quantity})</span> : null}
                    </span>
                  )}
                  itemToSubLabel={(r) => {
                    const bits = [];
                    if (r.material) bits.push(r.material.replace('_', ' '));
                    if (r.status) bits.push(r.status);
                    if (r.quantity) bits.push(r.quantity);
                    if (r.description) bits.push(r.description);
                    return bits.join(' · ') || null;
                  }}
                  searchKeys={['label', 'description', 'material', 'quantity']}
                  placeholder={requirementOptions.length === 0 ? 'No requirements yet' : 'Pick a requirement'}
                  searchPlaceholder="Search requirement…"
                  disabled={requirementOptions.length === 0}
                  testId="order-comment-requirement-select"
                  triggerClassName="h-8"
                  contentClassName="max-w-[480px]"
                />
              </div>
            </div>
          )}
          {topic === 'raw_material' && (
            <>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-slate-500">›</span>
                <Select value={subtopicId} onValueChange={setSubtopicId}>
                  <SelectTrigger className="h-8 w-[160px]" data-testid="order-comment-material-select">
                    <SelectValue placeholder="Material" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMMENT_MATERIAL_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value} data-testid={`order-comment-material-${o.value}`}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {subtopicId && subSubtopicOptions.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-slate-500">›</span>
                  <div className="min-w-[160px]">
                    <SearchableSelect
                      items={[{ id: '__all__', packet_no: '— All packets —' }, ...subSubtopicOptions]}
                      value={subSubtopicId || '__all__'}
                      onValueChange={(v) => setSubSubtopicId(v === '__all__' ? '' : (v || ''))}
                      itemToLabel={(p) => p.packet_no}
                      searchKeys={['packet_no']}
                      placeholder="Packet (optional)"
                      searchPlaceholder="Search packet no…"
                      testId="order-comment-packet-select"
                      triggerClassName="h-8"
                    />
                  </div>
                </div>
              )}
            </>
          )}
          {topic === 'setting' && (
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-slate-500">›</span>
              <Select value={subtopicId} onValueChange={setSubtopicId}>
                <SelectTrigger className="h-8 w-[170px]" data-testid="order-comment-setting-material-select">
                  <SelectValue placeholder="Raw material" />
                </SelectTrigger>
                <SelectContent>
                  {COMMENT_SETTING_MATERIAL_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value} data-testid={`order-comment-setting-material-${o.value}`}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {topic === 'qc' && (
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-slate-500">›</span>
              <Select value={subtopicId} onValueChange={setSubtopicId}>
                <SelectTrigger className="h-8 w-[170px]" data-testid="order-comment-qc-head-select">
                  <SelectValue placeholder="QC head" />
                </SelectTrigger>
                <SelectContent>
                  {COMMENT_QC_HEAD_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value} data-testid={`order-comment-qc-head-${o.value}`}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        {/* Wave 3.65 — @mention picker + selected chips */}
        <div className="flex flex-wrap items-center gap-2">
          <Popover open={mentionPickerOpen} onOpenChange={setMentionPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8"
                disabled={mentionCandidates.length === 0}
                data-testid="order-comment-mention-open"
                title={mentionCandidates.length === 0 ? 'No taggable teammates in this company' : 'Tag a teammate — they will get a bell notification'}
              >
                <AtSign size={14} className="mr-1.5" />
                Tag Users
                {mentionedIds.length > 0 && (
                  <Badge className="ml-1.5 bg-[var(--ink-navy)] text-white text-[10px] font-mono">{mentionedIds.length}</Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[320px] p-0" align="start" data-testid="order-comment-mention-popover">
              <div className="p-2 border-b border-[var(--divider)]">
                <Input
                  autoFocus
                  value={mentionSearch}
                  onChange={(e) => setMentionSearch(e.target.value)}
                  placeholder="Search by name, email, or role…"
                  className="h-8 text-sm"
                  data-testid="order-comment-mention-search"
                />
              </div>
              <div className="max-h-[280px] overflow-y-auto">
                {filteredMentionCandidates.length === 0 ? (
                  <div className="text-xs text-slate-500 italic p-4 text-center">
                    {mentionSearch ? 'No teammates match your search.' : 'No teammates to tag.'}
                  </div>
                ) : (
                  filteredMentionCandidates.map((u) => {
                    const active = mentionedIds.includes(u.id);
                    return (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => toggleMention(u.id)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-left border-b border-[var(--divider)] last:border-b-0 hover:bg-slate-50 transition-colors ${active ? 'bg-amber-50/50' : ''}`}
                        data-testid={`order-comment-mention-item-${u.id}`}
                      >
                        <Checkbox
                          checked={active}
                          onCheckedChange={() => toggleMention(u.id)}
                          className="pointer-events-none"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-[var(--ink-navy)] truncate">
                            {u.name || u.email}
                          </div>
                          <div className="text-[11px] text-slate-500 truncate">
                            <span className="font-mono">{u.email}</span>
                            {u.role && (
                              <Badge className="ml-1.5 bg-slate-50 text-slate-600 border border-slate-200 text-[9px] uppercase">
                                {u.role.replace('_', ' ')}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
              <div className="p-2 border-t border-[var(--divider)] flex justify-between">
                <button
                  type="button"
                  onClick={() => { setMentionedIds([]); setMentionSearch(''); }}
                  className="text-[11px] text-slate-500 hover:text-slate-700"
                  data-testid="order-comment-mention-clear"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={() => setMentionPickerOpen(false)}
                  className="text-[11px] text-[var(--ink-navy)] font-medium hover:underline"
                >
                  Done
                </button>
              </div>
            </PopoverContent>
          </Popover>
          {mentionedUsers.map((u) => (
            <Badge
              key={u.id}
              variant="outline"
              className="bg-amber-50 text-amber-800 border-amber-200 inline-flex items-center gap-1 pr-1 text-[11px]"
              data-testid={`order-comment-mention-chip-${u.id}`}
            >
              <AtSign size={10} />
              <span>{u.name || u.email.split('@')[0]}</span>
              <button
                type="button"
                onClick={() => toggleMention(u.id)}
                className="ml-1 rounded-full hover:bg-amber-100 p-0.5"
                aria-label={`Remove mention for ${u.email}`}
              >
                <XIcon size={10} />
              </button>
            </Badge>
          ))}
        </div>
        <div className="relative">
          <Textarea
            ref={textareaRef}
            value={body}
            onChange={onBodyChange}
            onKeyDown={Enter}
            onBlur={() => {
              // Delay close so a click on a list item can register first.
              setTimeout(() => setMentionInlineOpen(false), 150);
            }}
            onSelect={(e) => {
              // Handles caret moves via mouse / arrow keys while typing —
              // keeps the trigger accurate even when the user backspaces
              // or reaches the ``@`` from the right side.
              const el = e.target;
              const trig = detectMentionTrigger(el.value, el.selectionStart ?? el.value.length);
              if (trig) {
                setMentionInlineStart(trig.start);
                setMentionInlineQuery(trig.query);
                setMentionInlineOpen(true);
              } else if (mentionInlineOpen) {
                setMentionInlineOpen(false);
              }
            }}
            placeholder="Add a comment… type @ to tag a teammate."
            rows={2}
            data-testid="order-comment-input"
          />
          {mentionInlineOpen && (
            <div
              className="absolute left-0 right-0 mt-1 z-30 rounded-md border border-[var(--divider)] bg-white shadow-lg overflow-hidden"
              data-testid="order-comment-mention-inline-popover"
              // ``onMouseDown`` (not onClick) so the picker fires before
              // the textarea's onBlur → setTimeout close race.
              onMouseDown={(e) => e.preventDefault()}
            >
              <div className="px-3 py-1.5 bg-[var(--paper-warm)] text-[10px] uppercase tracking-wide text-slate-500 flex items-center justify-between">
                <span>Tag a teammate</span>
                <span className="font-mono">
                  {inlineFilteredCandidates.length}
                  {mentionInlineQuery ? ` match${inlineFilteredCandidates.length === 1 ? '' : 'es'} for “${mentionInlineQuery}”` : ' teammate' + (inlineFilteredCandidates.length === 1 ? '' : 's')}
                </span>
              </div>
              {inlineFilteredCandidates.length === 0 ? (
                <div className="text-xs text-slate-500 italic p-3 text-center" data-testid="order-comment-mention-inline-empty">
                  No teammate matches “{mentionInlineQuery}”. Press Esc to keep typing.
                </div>
              ) : (
                <ul className="max-h-[220px] overflow-y-auto">
                  {inlineFilteredCandidates.map((u, i) => {
                    const active = i === mentionInlineActiveIdx;
                    const already = mentionedIds.includes(u.id);
                    return (
                      <li key={u.id}>
                        <button
                          type="button"
                          onMouseDown={(e) => { e.preventDefault(); pickInlineMention(u); }}
                          onMouseEnter={() => setMentionInlineActiveIdx(i)}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-left border-b border-[var(--divider)] last:border-b-0 transition-colors ${active ? 'bg-amber-50' : 'hover:bg-slate-50'}`}
                          data-testid={`order-comment-mention-inline-item-${u.id}`}
                        >
                          <AtSign size={12} className={active ? 'text-amber-600' : 'text-slate-400'} />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-[var(--ink-navy)] truncate">
                              {u.name || u.email}
                              {already && (
                                <span className="ml-2 text-[10px] uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1 py-0.5">Tagged</span>
                              )}
                            </div>
                            <div className="text-[11px] text-slate-500 truncate flex items-center gap-1.5">
                              <span className="font-mono">{u.email}</span>
                              {u.role && (
                                <Badge className="bg-slate-50 text-slate-600 border border-slate-200 text-[9px] uppercase">
                                  {u.role.replace('_', ' ')}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              <div className="px-3 py-1.5 bg-[var(--paper-warm)] text-[10px] text-slate-500 flex items-center justify-between border-t border-[var(--divider)]">
                <span>↑↓ navigate · ⏎/Tab select · Esc dismiss</span>
                <span className="font-mono text-slate-400">@ trigger</span>
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-4">
            <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
              <Checkbox
                checked={requiresSA}
                onCheckedChange={(v) => setRequiresSA(!!v)}
                data-testid="order-comment-requires-sa"
              />
              <Shield size={11} className="text-amber-600" />
              <span>Requires Super Admin Attention</span>
            </label>
            <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
              <Checkbox
                checked={priority}
                onCheckedChange={(v) => setPriority(!!v)}
                data-testid="order-comment-priority"
              />
              <Flag size={11} className={priority ? 'text-rose-600' : 'text-slate-400'} />
              <span>Priority (urgent)</span>
            </label>
          </div>
          <Button
            onClick={submit}
            disabled={busy || !body.trim()}
            className="luxury-button h-9"
            data-testid="order-comment-submit"
          >
            <Send size={14} className="mr-1.5" /> {busy ? 'Posting…' : 'Post Comment'}
          </Button>
        </div>
      </div>
      </div>{/* Wave 3.85 — flex-col-reverse wrapper close */}
    </Card>
  );
}

// ============================================================================
// Phase 8.3 — Ghat Confirmation Section
// Any user can confirm (ticking requires Ghat weight in grams).
// Only Company Admin / Super Admin can unconfirm.
// Purely informational — does not block stage progression.
// ============================================================================
// Phase 9 hotfix — Jewellery Purity Editor
// Allows the user to (a) set the order's jewellery purity if it was missing
// (e.g. legacy orders pre-Phase 8) or (b) edit an existing purity. The edit
// triggers a re-fetch of the order which automatically recomputes the
// "→ Jewellery Purity" and "→ Balance @ Jewellery Purity" columns in the
// Gold ledger via PATCH /api/orders/{id}/jewellery-purity.
// ============================================================================
function JewelleryPurityEditor({ order, onUpdated, canEditForm = true }) {
  const [open, setOpen] = useState(false);
  const [purities, setPurities] = useState([]);
  const [selectedId, setSelectedId] = useState(order.jewellery_purity_id || '');
  const [busy, setBusy] = useState(false);
  // Phase 12 \u2014 inline purity create
  const [purityDlgOpen, setPurityDlgOpen] = useState(false);
  // Edit Order Header — only users with `orders.edit_header` (implied by
  // `orders.edit` / admin roles) may change the gold purity, and never on
  // terminal orders. Callers pass the gate via `canEditForm`.
  const stage = (order.stage || '').trim();
  const isTerminal = stage === 'Completed' || stage === 'Cancelled';
  const canEdit = canEditForm && !isTerminal;

  const loadPurities = async () => {
    if (!order.company_id) return;
    try {
      const { data } = await api.get('/gold/purities', { params: { company_id: order.company_id } });
      setPurities(data || []);
    } catch {
      setPurities([]);
    }
  };

  useEffect(() => {
    if (!open || !order.company_id) return;
    loadPurities();
    // eslint-disable-next-line
  }, [open, order.company_id]);

  useEffect(() => { setSelectedId(order.jewellery_purity_id || ''); }, [order.jewellery_purity_id]);

  const submit = async () => {
    if (!selectedId) { toast.error('Please pick a jewellery purity'); return; }
    setBusy(true);
    try {
      await api.patch(`/orders/${order.id}/jewellery-purity`, {
        jewellery_purity_id: selectedId,
      });
      toast.success('Jewellery purity updated. Gold ledger recomputed.');
      setOpen(false);
      if (onUpdated) onUpdated();
    } catch (e) {
      toast.error(extractError(e, 'Failed to update purity'));
    } finally { setBusy(false); }
  };

  const hasPurity = !!order.jewellery_purity_name;

  return (
    <>
      {hasPurity ? (
        canEdit ? (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-1 bg-[var(--gold-bg)] text-[var(--gold-icon)] border border-[var(--gold-border)] font-mono text-[11px] px-2 py-0.5 rounded-md hover:brightness-95 transition-[filter] duration-150"
            data-testid="order-detail-purity"
            title={`Jewellery gold purity: ${order.jewellery_purity_name} (${(order.jewellery_purity_percent || 0).toFixed(2)}%) — click to edit`}
          >
            <Coins size={11} className="mr-1" />
            {order.jewellery_purity_name} · {(order.jewellery_purity_percent || 0).toFixed(2)}%
            <Pencil size={9} className="ml-1 opacity-60" />
          </button>
        ) : (
          // Read-only display for users without the Edit Order Header
          // permission — the value is informative but not editable.
          <span
            className="inline-flex items-center gap-1 bg-[var(--gold-bg)] text-[var(--gold-icon)] border border-[var(--gold-border)] font-mono text-[11px] px-2 py-0.5 rounded-md opacity-80"
            data-testid="order-detail-purity"
            title={`Jewellery gold purity: ${order.jewellery_purity_name} (${(order.jewellery_purity_percent || 0).toFixed(2)}%)`}
          >
            <Coins size={11} className="mr-1" />
            {order.jewellery_purity_name} · {(order.jewellery_purity_percent || 0).toFixed(2)}%
          </span>
        )
      ) : canEdit ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1 bg-amber-50 text-amber-800 border border-amber-200 font-mono text-[11px] px-2 py-0.5 rounded-md hover:bg-amber-100 transition-colors duration-150"
          data-testid="order-detail-purity-prompt"
          title="No jewellery purity set for this order — click to set it"
        >
          <AlertCircle size={11} className="mr-1" />
          Set Jewellery Purity
        </button>
      ) : null}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="jewellery-purity-dialog">
          <DialogHeader>
            <DialogTitle>
              {hasPurity ? 'Edit Jewellery Purity' : 'Set Jewellery Purity'}
            </DialogTitle>
            <DialogDescription>
              {hasPurity
                ? 'Changing the jewellery purity will recompute the Gold ledger conversion columns (→ Jewellery Purity, → Balance @ Jewellery Purity). Underlying gold issues stay unchanged.'
                : 'Pick the gold purity at which this jewellery will be manufactured. This is used to convert issued 999 gold into jewellery-purity grams across the ledger.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label className="text-xs text-slate-600">Jewellery Purity</Label>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <SearchableSelect
                  items={purities}
                  value={selectedId}
                  onValueChange={(v) => setSelectedId(v || '')}
                  itemToLabel={(p) => `${p.name} (${Number(p.percent || 0).toFixed(2)}%)`}
                  searchKeys={['name']}
                  placeholder={purities.length === 0 ? 'No purities — click + to create' : 'Select a purity…'}
                  searchPlaceholder="Search purity…"
                  disabled={purities.length === 0}
                  testId="jewellery-purity-select"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0"
                onClick={() => setPurityDlgOpen(true)}
                data-testid="jewellery-purity-create-button"
                title="Create new purity"
              >
                <Plus size={14} />
              </Button>
            </div>
            {hasPurity && (
              <div className="text-[11px] text-slate-500">
                Current: <span className="font-medium text-slate-700">{order.jewellery_purity_name}</span> ({(order.jewellery_purity_percent || 0).toFixed(2)}%)
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy} data-testid="jewellery-purity-cancel">
              Cancel
            </Button>
            <Button onClick={submit} disabled={busy || !selectedId || selectedId === '__none'} data-testid="jewellery-purity-save">
              {busy ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 12 \u2014 inline purity create dialog */}
      <PurityFormDialog
        open={purityDlgOpen}
        onOpenChange={setPurityDlgOpen}
        companyId={order.company_id}
        onCreated={async (p) => {
          await loadPurities();
          if (p?.id) setSelectedId(p.id);
        }}
      />
    </>
  );
}


// ============================================================================
// Wave 3.87 — Order Title editor
// ---------------------------------------------------------------------------
// Anyone in the order's company (or Super Admin) can rename the order's
// title as long as the order is not Completed / Cancelled. Renders as a
// bold title that reveals a pencil on hover; clicking swaps it for an
// input + Save/Cancel controls. Empty / >200 char titles are rejected.
// ============================================================================
function OrderTitleEditor({ order, onUpdated, canEditForm = true }) {
  const stage = (order.stage || '').trim();
  // Wave 3.99aa — Additionally require the caller-provided
  // `canEditForm` gate (defaulting to true for legacy callers) so the
  // pencil affordance only appears for users with the Edit Order
  // Forms permission (or admins).
  const canEdit = canEditForm && stage !== 'Completed' && stage !== 'Cancelled';
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(order.title || '');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setValue(order.title || ''); }, [order.title]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const cancel = () => {
    setEditing(false);
    setValue(order.title || '');
  };

  const save = async () => {
    const trimmed = (value || '').trim();
    if (!trimmed) {
      toast.error('Title cannot be empty');
      return;
    }
    if (trimmed.length > 200) {
      toast.error('Title must be at most 200 characters');
      return;
    }
    if (trimmed === (order.title || '').trim()) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/orders/${order.id}/title`, { title: trimmed });
      toast.success('Title updated');
      setEditing(false);
      onUpdated?.();
    } catch (e) {
      toast.error(extractError(e, 'Failed to update title'));
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); save(); }
    if (e.key === 'Escape') { e.preventDefault(); cancel(); }
  };

  if (!editing) {
    return (
      <div className="group inline-flex items-center gap-1.5 max-w-full">
        <div
          className="font-medium text-[var(--ink-navy)] truncate"
          data-testid="order-detail-title"
        >
          {order.title}
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="shrink-0 text-slate-400 hover:text-[var(--ink-navy)] transition-colors opacity-60 hover:opacity-100"
            title="Edit title"
            data-testid="order-detail-title-edit-button"
          >
            <Pencil size={12} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 max-w-full">
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={onKeyDown}
        maxLength={200}
        placeholder="Order title"
        disabled={busy}
        className="h-8 text-sm font-medium max-w-md"
        data-testid="order-detail-title-input"
      />
      <Button
        size="sm"
        onClick={save}
        disabled={busy}
        className="luxury-button h-8"
        data-testid="order-detail-title-save-button"
      >
        {busy ? <Loader2 size={12} className="mr-1 animate-spin" /> : null}
        Save
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={cancel}
        disabled={busy}
        className="h-8"
        data-testid="order-detail-title-cancel-button"
      >
        Cancel
      </Button>
    </div>
  );
}


// ============================================================================
// Wave 3.87 — Order Description editor
// ---------------------------------------------------------------------------
// Long-form free-text description. Read-only view is a soft slate line
// beneath the title/goldsmith row. Hover reveals a pencil that swaps to
// a Textarea + Save/Cancel controls. Empty description renders as a
// muted "+ Add description" shortcut so operators can create one without
// leaving the page. Blocked once the order is Completed / Cancelled.
// ============================================================================
function OrderDescriptionEditor({ order, onUpdated }) {
  const stage = (order.stage || '').trim();
  const canEdit = stage !== 'Completed' && stage !== 'Cancelled';
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(order.description || '');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setValue(order.description || ''); }, [order.description]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const cancel = () => {
    setEditing(false);
    setValue(order.description || '');
  };

  const save = async () => {
    const trimmed = (value || '').trim();
    if (trimmed.length > 2000) {
      toast.error('Description must be at most 2000 characters');
      return;
    }
    if (trimmed === (order.description || '').trim()) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/orders/${order.id}/description`, { description: trimmed });
      toast.success('Description updated');
      setEditing(false);
      onUpdated?.();
    } catch (e) {
      toast.error(extractError(e, 'Failed to update description'));
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    // Nothing to display + no edit permission → don't render.
    if (!order.description && !canEdit) return null;
    return (
      <div className="group mt-1">
        {order.description ? (
          <div className="flex items-start gap-1.5">
            <div
              className="text-sm text-slate-600 whitespace-pre-wrap flex-1 min-w-0"
              data-testid="order-detail-description"
            >
              {order.description}
            </div>
            {canEdit && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="shrink-0 text-slate-400 hover:text-[var(--ink-navy)] transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
                title="Edit description"
                data-testid="order-detail-description-edit-button"
              >
                <Pencil size={12} />
              </button>
            )}
          </div>
        ) : canEdit ? (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-xs text-slate-400 hover:text-[var(--ink-navy)] italic inline-flex items-center gap-1 transition-colors"
            data-testid="order-detail-description-add-button"
          >
            <Plus size={11} /> Add description
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-2">
      <Textarea
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        maxLength={2000}
        rows={3}
        placeholder="Describe the piece, karigar hand-off notes, revisions…"
        disabled={busy}
        className="text-sm"
        data-testid="order-detail-description-input"
      />
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={save}
          disabled={busy}
          className="luxury-button h-8"
          data-testid="order-detail-description-save-button"
        >
          {busy ? <Loader2 size={12} className="mr-1 animate-spin" /> : null}
          Save
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={cancel}
          disabled={busy}
          className="h-8"
          data-testid="order-detail-description-cancel-button"
        >
          Cancel
        </Button>
        <span className="text-[10px] text-slate-500">{value.length}/2000</span>
      </div>
    </div>
  );
}


// ============================================================================
// Wave 3.87 — Order Special Comments editor
// ---------------------------------------------------------------------------
// Renders the bold-red callout when a value exists, plus a hover pencil to
// edit. Empty state exposes a subtle "+ Add special comments" affordance
// so late instructions are never trapped in a separate page. Blocked once
// the order is Completed / Cancelled.
// ============================================================================
function OrderSpecialCommentsEditor({ order, onUpdated, canEditForm = true }) {
  const stage = (order.stage || '').trim();
  // Edit Order Header — only users with the `orders.edit_header`
  // permission (implied by `orders.edit` / admin roles), supplied via
  // `canEditForm`, may edit the special comments, and never on terminal
  // orders. The existing value is still shown read-only to everyone.
  const canEdit = canEditForm && stage !== 'Completed' && stage !== 'Cancelled';
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(order.special_comments || '');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setValue(order.special_comments || ''); }, [order.special_comments]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const cancel = () => {
    setEditing(false);
    setValue(order.special_comments || '');
  };

  const save = async () => {
    const trimmed = (value || '').trim();
    if (trimmed.length > 2000) {
      toast.error('Special Comments must be at most 2000 characters');
      return;
    }
    if (trimmed === (order.special_comments || '').trim()) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/orders/${order.id}/special-comments`, { special_comments: trimmed });
      toast.success('Special Comments updated');
      setEditing(false);
      onUpdated?.();
    } catch (e) {
      toast.error(extractError(e, 'Failed to update special comments'));
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    if (order.special_comments) {
      return (
        <div
          className="mt-2 px-3 py-2 bg-rose-50 border-2 border-rose-300 rounded-md text-sm text-rose-800 font-semibold group"
          data-testid="order-special-comments-banner"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <span className="font-bold uppercase tracking-wide text-xs text-rose-700 block mb-0.5">Special Comments</span>
              <span className="text-rose-900 whitespace-pre-wrap">{order.special_comments}</span>
            </div>
            {canEdit && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="shrink-0 text-rose-500 hover:text-rose-700 transition-colors opacity-60 hover:opacity-100"
                title="Edit special comments"
                data-testid="order-detail-special-comments-edit-button"
              >
                <Pencil size={13} />
              </button>
            )}
          </div>
        </div>
      );
    }
    if (canEdit) {
      return (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="mt-2 text-xs text-slate-400 hover:text-rose-700 italic inline-flex items-center gap-1 transition-colors"
          data-testid="order-detail-special-comments-add-button"
        >
          <Plus size={11} /> Add special comments
        </button>
      );
    }
    return null;
  }

  return (
    <div className="mt-2 px-3 py-2 bg-rose-50 border-2 border-rose-300 rounded-md space-y-2">
      <span className="font-bold uppercase tracking-wide text-xs text-rose-700 block">Special Comments</span>
      <Textarea
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        maxLength={2000}
        rows={3}
        placeholder="Anything the karigar MUST know — this prints in bold red on the Job Card."
        disabled={busy}
        className="text-sm bg-white"
        data-testid="order-detail-special-comments-input"
      />
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={save}
          disabled={busy}
          className="luxury-button h-8"
          data-testid="order-detail-special-comments-save-button"
        >
          {busy ? <Loader2 size={12} className="mr-1 animate-spin" /> : null}
          Save
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={cancel}
          disabled={busy}
          className="h-8"
          data-testid="order-detail-special-comments-cancel-button"
        >
          Cancel
        </Button>
        <span className="text-[10px] text-slate-500 ml-auto">{value.length}/2000</span>
      </div>
    </div>
  );
}


// ============================================================================
// Wave 3.86 — Jewellery Type editor
// ---------------------------------------------------------------------------
// Order Manager / CA / SA can change the jewellery type on any order that
// is not Completed / Cancelled. The dialog offers three follow-up modes
// for SLA:
//   • Keep current SLA    — no change beyond the type name.
//   • Apply new template  — reset SLA & procurement defaults from the
//                           resolved SLA template for the new type.
//   • Custom SLA config   — open the full EditOrderSlaDialog so the
//                           operator can tune each stage/track manually.
// ============================================================================
function JewelleryTypeEditor({ order, onUpdated, canEditForm = true }) {
  const { user, isSuperAdmin, isCompanyAdmin, hasPermission } = useAuth();
  const [open, setOpen] = useState(false);
  const [types, setTypes] = useState([]);
  const [newType, setNewType] = useState((order.jewellery_type || '').toString());
  const [busy, setBusy] = useState(false);
  const [slaDlgOpen, setSlaDlgOpen] = useState(false);

  // Wave 3.99aa — Anyone with the `orders.edit` permission (the same
  // permission that governs the Edit Order Forms toggle) could already
  // change the jewellery type. Edit Order Header — the dedicated
  // `orders.edit_header` permission also grants this. The composite gate
  // is supplied by the caller via `canEditForm`; we keep the legacy
  // order_manager fast-path so those users don't lose the affordance.
  const canEdit = !!(
    canEditForm
    || isSuperAdmin || isCompanyAdmin || user?.role === 'order_manager'
    || hasPermission('orders.edit') || hasPermission('orders.edit_header')
  );
  const stage = (order.stage || '').trim();
  const isTerminal = stage === 'Completed' || stage === 'Cancelled';

  const loadTypes = async () => {
    if (!order.company_id) return;
    try {
      const { data } = await api.get('/jewellery-types', {
        params: { company_id: order.company_id },
      });
      setTypes(Array.isArray(data) ? data : []);
    } catch { setTypes([]); }
  };

  useEffect(() => {
    if (!open || !order.company_id) return;
    setNewType(order.jewellery_type || '');
    loadTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, order.company_id]);

  const submit = async (mode) => {
    const targetType = (newType || '').trim() || null;
    if (targetType === (order.jewellery_type || null) && mode !== 'custom') {
      setOpen(false);
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/orders/${order.id}/jewellery-type`, {
        jewellery_type: targetType || '',
        sla_mode: mode,
      });
      const modeMsg = {
        keep: 'kept SLA unchanged',
        apply_template: 'reset SLA from new template',
        custom: 'ready to configure custom SLA',
      }[mode] || '';
      toast.success(`Jewellery type updated · ${modeMsg}`);
      if (mode === 'custom') {
        setOpen(false);
        setSlaDlgOpen(true);
      } else {
        setOpen(false);
      }
      onUpdated?.();
    } catch (e) {
      toast.error(extractError(e, 'Failed to update jewellery type'));
    } finally {
      setBusy(false);
    }
  };

  if (!order.jewellery_type && !canEdit) return null;

  return (
    <>
      {order.jewellery_type ? (
        <button
          type="button"
          onClick={() => canEdit && !isTerminal && setOpen(true)}
          disabled={!canEdit || isTerminal}
          className={`inline-flex items-center gap-1 bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)] border border-[var(--jewellery-icon)]/30 font-mono text-[11px] px-2 py-0.5 rounded-md ${
            canEdit && !isTerminal ? 'hover:brightness-95 transition-[filter] duration-150' : 'opacity-70 cursor-default'
          }`}
          data-testid="order-detail-jewellery-type"
          title={canEdit && !isTerminal
            ? `Type of jewellery: ${order.jewellery_type} — click to change`
            : `Type of jewellery: ${order.jewellery_type}`}
        >
          {order.jewellery_type}
          {canEdit && !isTerminal && <Pencil size={9} className="ml-1 opacity-60" />}
        </button>
      ) : canEdit && !isTerminal ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1 bg-slate-50 text-slate-600 border border-slate-200 font-mono text-[11px] px-2 py-0.5 rounded-md hover:bg-slate-100 transition-colors"
          data-testid="order-detail-jewellery-type-set"
          title="Add jewellery type"
        >
          + Type of Jewellery
        </button>
      ) : null}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="change-jewellery-type-dialog">
          <DialogHeader>
            <DialogTitle>Change Jewellery Type</DialogTitle>
            <DialogDescription>
              Pick the new type of jewellery for this order. You can keep the current SLA,
              reset it to the template for the new type, or open the full SLA editor to fine-tune.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <Label className="text-xs text-slate-600">Type of Jewellery</Label>
              <SearchableSelect
                items={[
                  { id: '__none', name: '— No type —' },
                  ...types.map((t) => ({ id: t.name, name: t.name })),
                ]}
                value={newType || '__none'}
                onValueChange={(v) => setNewType(!v || v === '__none' ? '' : v)}
                itemToLabel={(t) => t.name}
                searchKeys={['name']}
                placeholder={types.length ? 'Select type…' : 'Loading types…'}
                searchPlaceholder="Search type…"
                testId="change-jewellery-type-select"
              />
              <div className="text-[10px] text-slate-500 mt-1">
                Types are managed on the New Order Form.
              </div>
            </div>
          </div>
          <DialogFooter className="flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={busy}
              data-testid="change-jewellery-type-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => submit('keep')}
              disabled={busy}
              data-testid="change-jewellery-type-keep-sla"
            >
              Keep Current SLA
            </Button>
            <Button
              variant="outline"
              onClick={() => submit('apply_template')}
              disabled={busy}
              data-testid="change-jewellery-type-apply-template"
            >
              Apply New Template
            </Button>
            <Button
              onClick={() => submit('custom')}
              disabled={busy}
              className="luxury-button"
              data-testid="change-jewellery-type-custom-sla"
            >
              Custom SLA Config
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom SLA follow-up: reuse the EditOrderSlaDialog in existing-order mode. */}
      <EditOrderSlaDialog
        open={slaDlgOpen}
        onOpenChange={setSlaDlgOpen}
        order={order}
        companyId={order.company_id}
        canToggleTracking={!!(isSuperAdmin || isCompanyAdmin)}
        onSaved={onUpdated}
      />
    </>
  );
}


// ============================================================================
// Wave 4.x — In-House CAD badge / toggle (header)
// ----------------------------------------------------------------------------
// Shows whether the order's CAD is developed in-house or externally, and lets
// the ORDER CREATOR, a COMPANY ADMIN, or a SUPER ADMIN flip it. Locked once
// the design chain has started (any of CAD/CAM/Chamka/Ghat ticked) and on
// terminal orders — matching the backend guard.
// ============================================================================
function InHouseCadEditor({ order, onUpdated }) {
  const { user, isSuperAdmin, isCompanyAdmin } = useAuth();
  const [busy, setBusy] = useState(false);
  const inHouse = order.in_house_cad !== false;
  const isCreator = !!(order.created_by && user?.id && order.created_by === user.id);
  const canEdit = !!(isSuperAdmin || isCompanyAdmin || isCreator);
  const stage = (order.stage || '').trim();
  const isTerminal = stage === 'Completed' || stage === 'Cancelled';
  // Lock once the design chain / Ghat has started (mirrors backend).
  const started = !!(
    order.cad_done || order.cam_done || order.chamka_done
    || order.ghat_done || order.ghat_confirmed
  );
  const locked = isTerminal || started;
  const editable = canEdit && !locked;

  const toggle = async () => {
    if (!editable || busy) return;
    setBusy(true);
    try {
      await api.patch(`/orders/${order.id}/in-house-cad`, { in_house_cad: !inHouse });
      toast.success(!inHouse ? 'CAD set to In-House' : 'CAD set to External');
      onUpdated?.();
    } catch (e) {
      toast.error(extractError(e, 'Failed to update In-House CAD'));
    } finally {
      setBusy(false);
    }
  };

  const title = editable
    ? `CAD is ${inHouse ? 'developed in-house' : 'external'} — click to switch to ${inHouse ? 'External' : 'In-House'}`
    : locked
      ? `CAD is ${inHouse ? 'in-house' : 'external'} — locked once CAD/CAM/Chamka/Ghat has started`
      : `CAD is ${inHouse ? 'in-house' : 'external'}`;

  const cls = inHouse
    ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
    : 'bg-slate-100 text-slate-600 border-slate-300';

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={!editable || busy}
      className={`inline-flex items-center gap-1 border font-mono text-[11px] px-2 py-0.5 rounded-md ${cls} ${
        editable ? 'hover:brightness-95 transition-[filter] duration-150' : 'opacity-80 cursor-default'
      }`}
      data-testid="order-detail-in-house-cad"
      title={title}
    >
      <Cpu size={11} />
      {inHouse ? 'In-House CAD' : 'External CAD'}
      {editable && (busy
        ? <Loader2 size={9} className="ml-0.5 animate-spin" />
        : <Pencil size={9} className="ml-0.5 opacity-60" />)}
    </button>
  );
}


// ============================================================================
// External CAD Weight editor (header)
// ----------------------------------------------------------------------------
// Renders ONLY for External-CAD orders (order.in_house_cad === false).
//
//   • While no CAD weight is set (order.external_cad_weight_pending === true)
//     the order sits at "CAD Active" and the backend greys out CAD/CAM/Chamka
//     in the production checklist. We surface a prominent "Set CAD Weight"
//     control so the user can enter the weight.
//   • Saving posts to POST /orders/{id}/external-cad-weight which auto-raises
//     the Gold requirement to the CAD weight and flips status to
//     "Waiting for Gold". After saving we show a read-only pill that can still
//     be edited / re-saved.
//
// Sales roles and terminal (Completed/Cancelled) orders get a read-only view,
// mirroring the backend guard.
// ============================================================================
function ExternalCadWeightEditor({ order, onUpdated }) {
  const { isSalesRole } = useAuth();
  const [open, setOpen] = useState(false);
  const [weight, setWeight] = useState('');
  const [busy, setBusy] = useState(false);

  const current = order.cad_weight_grams;

  React.useEffect(() => {
    if (open) setWeight(current != null ? String(current) : '');
  }, [open, current]);

  // Only External-CAD orders use this control — in-house captures CAD weight
  // on the CAD milestone instead. (Hooks above run unconditionally.)
  if (order.in_house_cad !== false) return null;

  const pending = order.external_cad_weight_pending === true;
  const stage = (order.stage || '').trim();
  const isTerminal = stage === 'Completed' || stage === 'Cancelled';
  const canEdit = !isSalesRole && !isTerminal;

  const save = async () => {
    const val = Number(weight);
    if (weight === '' || Number.isNaN(val) || val <= 0) {
      toast.error('CAD weight must be a number greater than zero');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/orders/${order.id}/external-cad-weight`,
        { cad_weight_grams: val },
      );
      const delta = data?.requirement_created?.delta_grams;
      if (delta) {
        toast.success(`CAD weight saved · Gold requirement raised by ${formatGrams(delta)} g`);
      } else {
        toast.success('CAD weight saved');
      }
      setOpen(false);
      onUpdated?.();
    } catch (e) {
      toast.error(extractError(e, 'Failed to save CAD weight'));
    } finally {
      setBusy(false);
    }
  };

  // Read-only view for sales roles / terminal orders.
  if (!canEdit) {
    if (pending) {
      return (
        <Badge
          className="bg-amber-50 text-amber-800 border border-amber-200 font-mono text-[11px]"
          data-testid="order-detail-external-cad-weight-pending"
          title="External CAD weight not set yet — order sits at CAD Active"
        >
          <Scale size={11} className="mr-1" />
          CAD Weight pending
        </Badge>
      );
    }
    return (
      <Badge
        className="bg-slate-100 text-slate-700 border border-slate-300 font-mono text-[11px]"
        data-testid="order-detail-external-cad-weight"
      >
        <Scale size={11} className="mr-1" />
        CAD Weight: {formatGrams(current || 0)} g
      </Badge>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1 border font-mono text-[11px] px-2 py-0.5 rounded-md transition-[filter] duration-150 hover:brightness-95 ${
            pending
              ? 'bg-amber-50 text-amber-800 border-amber-300'
              : 'bg-slate-100 text-slate-700 border-slate-300'
          }`}
          data-testid="order-detail-external-cad-weight-trigger"
          title={pending
            ? 'Set the External CAD weight to raise the gold requirement'
            : 'Edit External CAD weight'}
        >
          <Scale size={11} />
          {pending ? 'Set CAD Weight' : `CAD Weight: ${formatGrams(current || 0)} g`}
          <Pencil size={9} className="ml-0.5 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-72 p-3"
        data-testid="order-detail-external-cad-weight-popover"
      >
        <div className="space-y-2">
          <div className="text-xs font-semibold text-[var(--ink-navy)]">External CAD Weight</div>
          <p className="text-[11px] leading-snug text-slate-500">
            {pending
              ? 'Saving auto-raises the Gold requirement to this weight and moves the order to “Waiting for Gold”.'
              : 'Re-saving updates the CAD weight for this External-CAD order.'}
          </p>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min="0"
              step="0.001"
              inputMode="decimal"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              placeholder="grams"
              className="h-8 font-mono text-sm"
              data-testid="order-detail-external-cad-weight-input"
              onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
              autoFocus
            />
            <span className="text-xs text-slate-500">g</span>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={() => setOpen(false)}
              disabled={busy}
              data-testid="order-detail-external-cad-weight-cancel"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="h-7 text-xs"
              onClick={save}
              disabled={busy}
              data-testid="order-detail-external-cad-weight-save"
            >
              {busy ? <Loader2 size={12} className="mr-1 animate-spin" /> : null}
              Save
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}


// ============================================================================
// Wave 3.65 — Order Milestones Section
//
// Renders three independent CAD / CAM / Ghat milestones, each with:
//   • a checkbox to mark the milestone as done
//   • a weight input (3-decimal grams) that appears on tick
//   • an audit stamp (timestamp + user) and expandable timeline of every
//     tick / weight-update / untick pulled from `order.milestones_log`
//   • a comment callout badge scoped to that milestone (Ghat → CAD/CAM/Ghat)
//
// Governance mirrors the old GhatConfirmation card:
//   • Any user can tick
//   • Only Company Admin / Super Admin can un-tick
//
// Backing endpoints (Wave 3.65):
//   POST /orders/{id}/milestones/{kind}/tick     – payload: {weight_grams, note}
//   POST /orders/{id}/milestones/{kind}/untick   – CA/SA only
// ============================================================================
const MILESTONE_DEFS = [
  { kind: 'cad', label: 'CAD Weight', subtopic: 'cad', substage: 'CAD Confirmed' },
  { kind: 'cam', label: 'CAM Weight', subtopic: 'cam', substage: 'CAM Confirmed' },
  // Wave 3.73 — Chamka Issued sits between CAM and Ghat in the strict
  // milestone rank. Ticking after CAM advances the substage; ticking
  // before CAM tips substage forward too (backend enforces).
  { kind: 'chamka', label: 'Chamka Weight', subtopic: 'chamka', substage: 'Chamka Issued' },
  { kind: 'ghat', label: 'Ghat Weight', subtopic: 'ghat', substage: 'Ghat Confirmed' },
];

function MilestoneRow({ order, def, reload }) {
  const { isSuperAdmin, isCompanyAdmin } = useAuth();
  const done = !!order[`${def.kind}_done`];
  const weightRaw = order[`${def.kind}_weight_grams`];
  const doneAt = order[`${def.kind}_done_at`];
  const doneBy = order[`${def.kind}_done_by_email`];
  const [showInput, setShowInput] = useState(false);
  const [weight, setWeight] = useState(weightRaw != null ? String(weightRaw) : '');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const isCompleted = order.stage === 'Completed';

  // Sync inputs with the latest server-side value whenever the row rerenders.
  React.useEffect(() => {
    setWeight(weightRaw != null ? String(weightRaw) : '');
  }, [weightRaw]);

  const log = (order.milestones_log || []).filter((e) => e.kind === def.kind);

  const onCheckedChange = (checked) => {
    if (isCompleted) {
      toast.error('Order is Completed — milestones cannot be changed.');
      return;
    }
    if (checked) {
      // Reveal weight input; the actual tick happens on Save.
      setShowInput(true);
    } else {
      // Un-tick — CA/SA only.
      if (!(isSuperAdmin || isCompanyAdmin)) {
        toast.error(`Only Company Admin / Super Admin can un-tick ${def.label}.`);
        return;
      }
      untick();
    }
  };

  const submit = async () => {
    const g = Number(weight || 0);
    if (g <= 0) { toast.error(`Enter a valid ${def.label} in grams`); return; }
    setBusy(true);
    try {
      const res = await api.post(`/orders/${order.id}/milestones/${def.kind}/tick`, {
        weight_grams: g,
        note: note || '',
      });
      toast.success(`${def.label} recorded`);
      setShowInput(false);
      setNote('');
      // Wave 3.97 — On CAD ticks / edits, backend attaches a
      // ``_gold_shortfall`` envelope so we can surface a one-tap
      // top-up / reduce action if the gold requirement is now out of
      // step with the CAD weight.
      if (def.kind === 'cad') {
        try {
          showGoldShortfallToast(res?.data?._gold_shortfall, order.id, reload);
        } catch { /* best-effort */ }
      }
      reload();
    } catch (e) { toast.error(extractError(e, `Failed to save ${def.label}`)); }
    finally { setBusy(false); }
  };

  const untick = async () => {
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/milestones/${def.kind}/untick`);
      toast.success(`${def.label} un-ticked`);
      reload();
    } catch (e) { toast.error(extractError(e, `Failed to un-tick ${def.label}`)); }
    finally { setBusy(false); }
  };

  const isEditing = !done && showInput;

  return (
    <div
      className={`p-3 rounded-md border transition-colors ${done ? 'border-emerald-200 bg-emerald-50/40' : 'border-[var(--divider)] bg-white'}`}
      data-testid={`milestone-row-${def.kind}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <Checkbox
            checked={done}
            onCheckedChange={onCheckedChange}
            disabled={busy || isCompleted}
            className="mt-1"
            data-testid={`milestone-checkbox-${def.kind}`}
          />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-sm font-medium text-[var(--ink-navy)]">{def.label}</div>
              <CommentCalloutBadge
                filterKey={`ghat:${def.subtopic}`}
                spec={{ topic: 'ghat', subtopic_id: def.subtopic }}
                label={`View comments on ${def.label}`}
                testId={`milestone-comment-callout-${def.kind}`}
                tone="amber"
                size="xs"
              />
              {done && (
                <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-[10px] uppercase tracking-wide">
                  Done
                </Badge>
              )}
            </div>
            {done ? (
              <div className="text-xs text-slate-600 mt-1">
                <span className="font-mono text-[var(--ink-navy)]" data-testid={`milestone-weight-${def.kind}`}>
                  {formatGrams(weightRaw || 0)} g
                </span>
                <span className="text-slate-400"> · by </span>
                <span className="font-medium text-slate-700">{doneBy || '—'}</span>
                {doneAt && <span className="text-slate-400"> · {formatDateTime(doneAt)}</span>}
              </div>
            ) : (
              <div className="text-[11px] text-slate-500 mt-1">
                Tick when {def.label.toLowerCase()} is recorded. Auto-updates order substage to “{def.substage}” while in production.
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {log.length > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((s) => !s)}
              className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-[var(--ink-navy)] px-2 py-1 rounded-md hover:bg-slate-50"
              data-testid={`milestone-timeline-toggle-${def.kind}`}
              title={expanded ? 'Hide timeline' : 'Show timeline'}
            >
              {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              Timeline
              <Badge className="ml-1 bg-slate-100 text-slate-700 border-slate-200 text-[10px] font-mono">{log.length}</Badge>
            </button>
          )}
          {done && (isSuperAdmin || isCompanyAdmin) && (
            <Button
              variant="outline"
              size="sm"
              onClick={untick}
              disabled={busy || isCompleted}
              className="border-rose-200 text-rose-700 hover:bg-rose-50 h-8"
              data-testid={`milestone-untick-${def.kind}`}
              title={isCompleted ? 'Order is Completed — cannot un-tick' : undefined}
            >
              Un-tick
            </Button>
          )}
        </div>
      </div>
      {isEditing && (
        <div className="mt-3 pt-3 border-t border-dashed border-[var(--divider)]">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-1">
              <Label className="text-[11px]">{def.label} (grams) <span className="text-rose-600">*</span></Label>
              <Input
                type="number"
                step="0.001"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder="e.g. 12.345"
                data-testid={`milestone-weight-input-${def.kind}`}
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px]">Note (optional)</Label>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Short remark (e.g. matches CAD approval Rev-2)"
                data-testid={`milestone-note-input-${def.kind}`}
              />
            </div>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => { setShowInput(false); setWeight(weightRaw != null ? String(weightRaw) : ''); setNote(''); }}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={submit}
              className="luxury-button"
              disabled={busy || !(Number(weight || 0) > 0)}
              data-testid={`milestone-save-${def.kind}`}
            >
              {busy ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      )}
      {expanded && log.length > 0 && (
        <div className="mt-3 pt-3 border-t border-dashed border-[var(--divider)]">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2 font-semibold">Timeline</div>
          <div className="relative pl-4">
            <div className="absolute left-1.5 top-1 bottom-1 w-px bg-slate-200" aria-hidden="true" />
            <ul className="space-y-2">
              {[...log].reverse().map((e, i) => (
                <li key={i} className="relative">
                  <div className={`absolute -left-3 top-1.5 h-2 w-2 rounded-full ring-2 ring-white ${e.action === 'unticked' ? 'bg-rose-500' : 'bg-emerald-500'}`} aria-hidden="true" />
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-[12px]">
                    <span className="font-mono text-slate-700">{formatDateTime(e.at)}</span>
                    <Badge className={`text-[9px] uppercase ${e.action === 'unticked' ? 'bg-rose-50 text-rose-700 border border-rose-200' : e.action === 'weight_updated' ? 'bg-amber-50 text-amber-800 border border-amber-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>
                      {e.action.replace('_', ' ')}
                    </Badge>
                    {e.weight_grams != null && (
                      <span className="font-mono tabular-nums text-[var(--ink-navy)]">{formatGrams(e.weight_grams)} g</span>
                    )}
                    <span className="text-slate-500">by <span className="text-slate-700">{e.user_email}</span></span>
                    {e.note && <span className="text-slate-500 italic">— {e.note}</span>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      {/* Wave 3.67 — Per-row inline comment list. Opens ONLY when the CAD /
          CAM / Ghat callout badge (filterKey `ghat:<kind>`) is clicked,
          so each row shows exactly its own scoped comments. */}
      <InlineCommentList
        filterKey={`ghat:${def.subtopic}`}
        spec={{ topic: 'ghat', subtopic_id: def.subtopic }}
        emptyHint={`No comments on ${def.label} yet. Use the composer with Topic = Ghat › ${def.label.replace(' Weight', '')} to add one.`}
        testIdPrefix={`milestone-${def.kind}-comment`}
      />
    </div>
  );
}


// ============================================================================
// Wave 3.81 — Manual Setting-Complete hand-off row
// ---------------------------------------------------------------------------
// Renders inside MilestonesSection right after Ghat. Only actionable when
// every requirement is fulfilled (matches backend guardrail). Shows a
// friendly "waiting on X/Y requirements" chip when the gate is closed.
// ============================================================================
function SettingHandoffRow({ order, reload }) {
  const { isSuperAdmin, isCompanyAdmin, user } = useAuth();
  const [note, setNote] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [showInput, setShowInput] = React.useState(false);

  const done = !!order.setting_done_at;
  const doneAt = order.setting_done_at;
  const doneBy = order.setting_done_by_email;
  const noteSaved = order.setting_note;
  const isCompleted = order.stage === 'Completed';

  // Requirements fulfillment gate — mirrors backend logic.
  const reqs = order.requirements || [];
  const total = reqs.length;
  const completed = reqs.filter((r) => ['complete', 'completed', 'done'].includes((r.status || '').toLowerCase())).length;
  const fullyFulfilled = total === 0 || completed === total;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/setting/complete`, { note: note || '' });
      toast.success('Setting hand-off recorded — Quality Check can begin.');
      setShowInput(false);
      setNote('');
      reload();
    } catch (e) {
      toast.error(extractError(e, 'Failed to mark Setting complete'));
    } finally { setBusy(false); }
  };

  const untick = async () => {
    const canUntick = isSuperAdmin || isCompanyAdmin || order.created_by_email === user?.email;
    if (!canUntick) {
      toast.error('Only the order creator or an admin can un-tick Setting.');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/setting/uncomplete`);
      toast.success('Setting un-ticked');
      reload();
    } catch (e) {
      toast.error(extractError(e, 'Failed to un-tick Setting'));
    } finally { setBusy(false); }
  };

  return (
    <div
      className={`rounded-md border p-3 ${done ? 'border-emerald-200 bg-emerald-50/40' : fullyFulfilled ? 'border-[var(--gold-accent)]/40 bg-[var(--paper-warm)]/60' : 'border-stone-200 bg-stone-50/60'}`}
      data-testid="setting-handoff-row"
    >
      <div className="flex items-start gap-3">
        <div className="pt-0.5">
          <Checkbox
            checked={done}
            disabled={isCompleted || busy || (!done && !fullyFulfilled)}
            onCheckedChange={(v) => {
              if (isCompleted) { toast.error('Order is Completed — cannot change Setting.'); return; }
              if (v) {
                if (!fullyFulfilled) {
                  toast.error(`Cannot tick Setting yet — ${completed}/${total} requirements fulfilled.`);
                  return;
                }
                setShowInput(true);
              } else {
                untick();
              }
            }}
            data-testid="setting-handoff-checkbox"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-2">
            <span className="text-sm font-medium text-[var(--ink-navy)]">Setting Complete</span>
            {done ? (
              <Badge className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200">
                <CheckCircle2 size={10} className="mr-0.5" /> Handed to QC
              </Badge>
            ) : fullyFulfilled ? (
              <Badge className="text-[10px] bg-[var(--paper-warm)] text-[var(--gold-accent)] border border-[var(--gold-accent)]/40">
                Ready — click to tick
              </Badge>
            ) : (
              <Badge className="text-[10px] bg-stone-100 text-stone-700 border border-stone-200">
                <PauseCircle size={10} className="mr-0.5" />
                Waiting · {completed}/{total} requirements fulfilled
              </Badge>
            )}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {done
              ? <>Ticked <span className="font-mono">{formatDateTime(doneAt)}</span>{doneBy ? <> by <span className="text-slate-700">{doneBy}</span></> : null}{noteSaved ? <> · <span className="italic">"{noteSaved}"</span></> : null}</>
              : fullyFulfilled
                ? 'All raw material requirements are fulfilled. Tick to hand the piece over to Quality Check.'
                : 'Setting is blocked until every raw material requirement is fulfilled. This ensures QC time reflects actual manufacturing.'}
          </div>
        </div>
      </div>
      {showInput && !done && (
        <div className="mt-3 pt-3 border-t border-dashed border-[var(--divider)]">
          <Label className="text-[11px]">Hand-off note (optional)</Label>
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Handed to QC bench 2"
            data-testid="setting-handoff-note-input"
          />
          <div className="mt-2 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => { setShowInput(false); setNote(''); }}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={submit}
              className="luxury-button"
              disabled={busy}
              data-testid="setting-handoff-save"
            >
              {busy ? 'Saving…' : 'Mark Setting Complete'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


function MilestonesSection({ order, reload }) {
  return (
    <Card className="p-5" data-testid="milestones-section">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="text-sm font-medium text-[var(--ink-navy)] flex items-center gap-2">
            Milestones
            <CommentCalloutBadge
              filterKey="ghat"
              spec={{ topic: 'ghat' }}
              label="View all Ghat / milestone comments"
              testId="ghat-comments-callout"
            />
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Tick each milestone as it happens — CAD, CAM, Chamka, then Ghat. Substage auto-advances during In Production.
            Once every raw-material requirement is fulfilled, tick <b>Setting Complete</b> to hand the piece over to Quality Check.
          </div>
        </div>
      </div>
      <div className="space-y-2" data-testid="milestones-list">
        {MILESTONE_DEFS.map((def) => (
          <MilestoneRow key={def.kind} order={order} def={def} reload={reload} />
        ))}
        {/* Wave 3.81 — Explicit Setting-complete hand-off */}
        <SettingHandoffRow order={order} reload={reload} />
      </div>
      <InlineCommentList
        filterKey="ghat"
        spec={{ topic: 'ghat' }}
        emptyHint="No comments on milestones yet. Use the composer below with Topic = Ghat to add one."
        testIdPrefix="ghat-comment"
      />
    </Card>
  );
}

// Kept as a thin backward-compatibility alias. Existing render sites still
// reference `<GhatConfirmationSection …/>`; they now render the new
// MilestonesSection so no additional call-site edits are required.
function GhatConfirmationSection(props) {
  return <MilestonesSection {...props} />;
}

// ============================================================================
// Phase 7 — Raw Material Ledger (per Order)
// Computes Issued / Returned / Consumed / Balance from embedded diamond_issues + gold_issues.
//
// Wave 3.25 — `source` values now distinguish the three negative-entry
// flavours so the order form no longer confuses "consumed in jewellery"
// with "returned to inventory":
//   - source == 'return'                           → Returned bucket
//   - source == 'jewellery_overflow_to_packet'     → Returned bucket (overflow to source packet)
//   - source == 'jewellery_overflow_to_unattributed' → Returned bucket (overflow to goldsmith pool)
//   - source == 'jewellery_consumed'               → Consumed bucket (now embedded in jewellery)
// "Balance with Goldsmith" = Issued − Returned − Consumed (= 0 once Receive completes).
// ============================================================================

// Classify a ledger entry into one of three buckets. Used everywhere
// the Order's Raw Material Ledger needs to surface Issued / Returned /
// Consumed separately.
const CONSUMED_SOURCES = new Set(['jewellery_consumed']);
// Wave 3.39 — 'transfer_out' is a negative entry on the source order's RML
// and belongs in the Returned bucket. 'transfer_in' is a positive entry on
// the destination order and is classified as Issued (default).
// Wave 4.x — 'jewellery_ghisai_loss' is a write-off (stone weight ground
// away during recutting, never received back). It reduces the goldsmith's
// balance so it lives in the Returned bucket for reconciliation, but it is
// labelled distinctly in the row as a write-off (not a real return).
const RETURN_SOURCES = new Set(['return', 'jewellery_overflow_to_packet', 'jewellery_overflow_to_unattributed', 'transfer_out', 'jewellery_ghisai_loss']);
function classifyRmlEntry(r) {
  if (!r) return 'issued';
  const c = Number(r.carats || 0);
  const p = Number(r.pieces || 0);
  const g = Number(r.grams_999 ?? r.grams ?? 0);
  if (CONSUMED_SOURCES.has(r.source)) return 'consumed';
  if (RETURN_SOURCES.has(r.source)) return 'returned';
  if (c < 0 || p < 0 || g < 0) return 'returned';  // legacy fallback
  return 'issued';
}

function RawMaterialLedgerSection({ order, reload, onOpenRmlPdf }) {
  // Wave 3.41 — read the master Value Visibility permission.
  // Wave 3.99l — read the Material Access Matrix so we can strip
  // out-of-scope material blocks (Gold / Diamonds / CS / Pearls)
  // entirely from the Raw Material Ledger.
  const { canViewValues, hasMaterialAccess, hasPermission } = useAuth();
  const canSeeGold = hasMaterialAccess('gold', 'see');
  const canSeeDiamond = hasMaterialAccess('diamond', 'see');
  const canSeeColorStone = hasMaterialAccess('color_stone', 'see');
  const canSeePearl = hasMaterialAccess('pearl', 'see');
  // Wave 3.99z — Mountings sub-ledger. Own permission family
  // ``materials.mountings.see``. Data lives in a separate collection so
  // it needs its own fetch. We depend on ``order`` reference itself so
  // that every parent reload (which sets a new order object via
  // ``setOrder``) also refreshes the mounting issues — mounting
  // issue/void doesn't bump the order document's updated_at.
  const canSeeMounting = hasPermission('materials.mountings.see');
  const [mountingIssues, setMountingIssues] = useState({ issues: [], total_issued_grams: 0 });
  useEffect(() => {
    if (!order?.id || !canSeeMounting) return;
    let cancelled = false;
    api.get(`/orders/${order.id}/mountings`)
      .then(({ data }) => { if (!cancelled) setMountingIssues(data || { issues: [], total_issued_grams: 0 }); })
      .catch(() => { if (!cancelled) setMountingIssues({ issues: [], total_issued_grams: 0 }); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, canSeeMounting]);
  const diamondRows = useMemo(() => (order.diamond_issues || []).slice().sort(
    (a, b) => (a.timestamp || '').localeCompare(b.timestamp || '')
  ), [order.diamond_issues]);
  const colorStoneRows = useMemo(() => (order.color_stone_issues || []).slice().sort(
    (a, b) => (a.timestamp || '').localeCompare(b.timestamp || '')
  ), [order.color_stone_issues]);
  const pearlRows = useMemo(() => (order.pearl_issues || []).slice().sort(
    (a, b) => (a.timestamp || '').localeCompare(b.timestamp || '')
  ), [order.pearl_issues]);
  const goldRows = useMemo(() => (order.gold_issues || []).slice().sort(
    (a, b) => (a.timestamp || '').localeCompare(b.timestamp || '')
  ), [order.gold_issues]);

  // Phase 8.2 — Pull order-level purity snapshot as fallback when a row has no per-line snapshot.
  const orderPurityName = order.jewellery_purity_name || '';
  const orderPurityPercent = Number(order.jewellery_purity_percent || 0);

  // Wave 3.25 — Linked received jewellery, used to render an explanatory
  // banner above the Raw Material Ledger ("Consumed entries correspond
  // to materials embedded in Jewellery #JW-xxx"). Fetched here so the
  // banner stays close to the ledger without prop drilling.
  const [linkedJewellery, setLinkedJewellery] = useState([]);
  useEffect(() => {
    if (!order?.id) return;
    let cancelled = false;
    api.get('/jewellery', {
      params: { order_id: order.id, company_id: order.company_id, limit: 50 },
    })
      .then(({ data }) => { if (!cancelled) setLinkedJewellery(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setLinkedJewellery([]); });
    return () => { cancelled = true; };
  }, [order?.id, order?.company_id]);

  // Phase 9.4 — Gold value mode toggle + current 999 rate (loaded only when mode = current_rate)
  const initialMode = order.gold_value_mode === 'current_rate' ? 'current_rate' : 'ledger_cost';
  const [mode, setMode] = useState(initialMode);
  const [currentRate999, setCurrentRate999] = useState(null);
  const [savingMode, setSavingMode] = useState(false);

  // Refresh local mode when order doc changes upstream
  useEffect(() => {
    Promise.resolve().then(() => setMode(initialMode));
  }, [initialMode]);

  // Fetch the latest 999 rate the first time we switch to current_rate mode
  useEffect(() => {
    if (mode !== 'current_rate') return;
    let cancelled = false;
    api.get('/gold/rates', { params: { company_id: order.company_id, limit: 1 } })
      .then(({ data }) => {
        if (cancelled) return;
        const latest = Array.isArray(data) && data.length > 0 ? data[0] : null;
        setCurrentRate999(latest ? Number(latest.rate_per_gram_999 || 0) : 0);
      })
      .catch(() => { if (!cancelled) setCurrentRate999(0); });
    return () => { cancelled = true; };
  }, [mode, order.company_id]);

  const changeMode = async (next) => {
    if (next === mode) return;
    setMode(next);
    // Persist on the order
    setSavingMode(true);
    try {
      await api.put(`/orders/${order.id}`, { gold_value_mode: next });
      if (reload) await reload();
    } catch (e) {
      toast.error(extractError(e, 'Failed to save mode'));
      setMode(initialMode);
    } finally { setSavingMode(false); }
  };

  // Wave 3.21 — Global expand/collapse for packet groups across Diamonds,
  // Colour Stones, and Pearls. The packet groups locally manage their open
  // state, but they reset to `allExpanded` whenever `forceVersion` changes
  // — letting the user override the global toggle on individual rows
  // without losing the bulk-control affordance.
  const [allExpanded, setAllExpanded] = useState(false);
  const [forceVersion, setForceVersion] = useState(0);

  // Wave 3.40 — Show/Hide Value column toggle. When OFF, the Rate-derived
  // "Value" column is hidden across every ledger table (Gold, Diamonds,
  // Colour Stones, Pearls), and the Gold mode toggle (Ledger Cost /
  // Current Rate, which only affects valuation) is also hidden. Default
  // ON so the existing behaviour is preserved unless the user opts out.
  // Wave 3.41 — When the user lacks ``values.view``, this toggle is
  // FORCED to false and the button itself is hidden (see toolbar below).
  const [showValuesPref, setShowValuesPref] = useState(true);
  const showValues = canViewValues && showValuesPref;

  // Wave 3.39 — Material Transfer dialog. Lets the user move raw material
  // from this order to another Order Form belonging to the SAME goldsmith.
  const [transferOpen, setTransferOpen] = useState(false);

  // Compute totals — Wave 3.25 splits negative entries into Consumed
  // (embedded in jewellery via source='jewellery_consumed') vs Returned
  // (back to vault or to goldsmith's pool). Helper `classifyRmlEntry`
  // codifies this taxonomy and is shared across every table below.
  const dTotal = useMemo(() => diamondRows.reduce((acc, r) => {
    const c = Math.abs(Number(r.carats || 0));
    const p = Math.abs(Number(r.pieces || 0));
    const v = Math.abs(Number(r.value || 0));
    const kind = classifyRmlEntry(r);
    if (kind === 'issued')        { acc.issuedC += c; acc.issuedP += p; acc.issuedV += v; }
    else if (kind === 'consumed') { acc.consumedC += c; acc.consumedP += p; acc.consumedV += v; }
    else                          { acc.returnedC += c; acc.returnedP += p; acc.returnedV += v; }
    return acc;
  }, { issuedC: 0, issuedP: 0, issuedV: 0, consumedC: 0, consumedP: 0, consumedV: 0, returnedC: 0, returnedP: 0, returnedV: 0 }), [diamondRows]);
  const csTotal = useMemo(() => colorStoneRows.reduce((acc, r) => {
    const c = Math.abs(Number(r.carats || 0));
    const p = Math.abs(Number(r.pieces || 0));
    const v = Math.abs(Number(r.value || 0));
    const kind = classifyRmlEntry(r);
    if (kind === 'issued')        { acc.issuedC += c; acc.issuedP += p; acc.issuedV += v; }
    else if (kind === 'consumed') { acc.consumedC += c; acc.consumedP += p; acc.consumedV += v; }
    else                          { acc.returnedC += c; acc.returnedP += p; acc.returnedV += v; }
    return acc;
  }, { issuedC: 0, issuedP: 0, issuedV: 0, consumedC: 0, consumedP: 0, consumedV: 0, returnedC: 0, returnedP: 0, returnedV: 0 }), [colorStoneRows]);
  const prTotal = useMemo(() => pearlRows.reduce((acc, r) => {
    const c = Math.abs(Number(r.carats || 0));
    const p = Math.abs(Number(r.pieces || 0));
    const v = Math.abs(Number(r.value || 0));
    const kind = classifyRmlEntry(r);
    if (kind === 'issued')        { acc.issuedC += c; acc.issuedP += p; acc.issuedV += v; }
    else if (kind === 'consumed') { acc.consumedC += c; acc.consumedP += p; acc.consumedV += v; }
    else                          { acc.returnedC += c; acc.returnedP += p; acc.returnedV += v; }
    return acc;
  }, { issuedC: 0, issuedP: 0, issuedV: 0, consumedC: 0, consumedP: 0, consumedV: 0, returnedC: 0, returnedP: 0, returnedV: 0 }), [pearlRows]);
  const gTotal = useMemo(() => goldRows.reduce((acc, r) => {
    const g = Math.abs(Number(r.grams_999 ?? r.grams ?? 0));
    const v = Math.abs(Number(r.value || 0));
    const kind = classifyRmlEntry(r);
    if (kind === 'issued')        { acc.issuedG += g; acc.issuedV += v; }
    else if (kind === 'consumed') { acc.consumedG += g; acc.consumedV += v; }
    else                          { acc.returnedG += g; acc.returnedV += v; }
    return acc;
  }, { issuedG: 0, issuedV: 0, consumedG: 0, consumedV: 0, returnedG: 0, returnedV: 0 }), [goldRows]);

  return (
    <Card className="p-5" data-testid="order-material-ledger">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="font-medium text-[var(--ink-navy)]">Raw Material Ledger</div>
          <div className="text-xs text-slate-500">
            Every issue and return for this order, with clickable memo links and running balance.
            {orderPurityName && (
              <> Gold is shown as <span className="font-mono text-slate-700">999</span> grams alongside the converted weight at this order's purity (<span className="font-mono text-slate-700">{orderPurityName} · {orderPurityPercent.toFixed(2)}%</span>).</>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Wave 3.39 — Transfer Material to another Order Form
              belonging to the same Goldsmith. Records a return on the
              source order and a fresh issue on the destination, linked
              by a transfer ID for audit trail. */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setTransferOpen(true)}
            data-testid="rml-transfer-material-button"
            className="shrink-0"
            disabled={!order?.goldsmith_id}
            title={order?.goldsmith_id ? 'Transfer material to another order of the same goldsmith.' : 'Assign a goldsmith to this order first.'}
          >
            <ArrowRightLeft size={14} className="mr-1.5" />
            Transfer Material
          </Button>
          {/* Wave 3.40 — Show / Hide Value column. Defaults to ON.
              Affects every ledger table (Gold, Diamonds, Colour Stones,
              Pearls). When OFF the Gold Ledger Cost / Current Rate
              mode toggle is also hidden because it only affects valuation.
              Wave 3.41 — Entirely hidden for users without ``values.view``
              (they can't see values anyway). */}
          {canViewValues && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowValuesPref((v) => !v)}
              data-testid="rml-show-values-toggle"
              className="shrink-0"
              title={showValuesPref ? 'Hide the Value column (and the Gold rate toggle).' : 'Show the Value column (and the Gold rate toggle).'}
            >
              {showValuesPref ? (
                <>
                  <EyeOff size={14} className="mr-1.5" />
                  Hide Value
                </>
              ) : (
                <>
                  <Eye size={14} className="mr-1.5" />
                  Show Value
                </>
              )}
            </Button>
          )}
          {/* Wave 3.21 — Global Expand All / Collapse All toggle for the
              packet-grouped ledgers (Diamonds, Colour Stones, Pearls). The
              Gold ledger is chronological (no packet grouping) so this
              toggle does not apply to it. Default state: collapsed. */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setAllExpanded((v) => !v);
              setForceVersion((v) => v + 1);
            }}
            data-testid="rml-expand-collapse-toggle"
            className="shrink-0"
            title={allExpanded ? 'Collapse every packet group' : 'Expand every packet group to see individual entries'}
          >
            {allExpanded ? (
              <>
                <ChevronUp size={14} className="mr-1.5" />
                Collapse All
              </>
            ) : (
              <>
                <ChevronDown size={14} className="mr-1.5" />
                Expand All
              </>
            )}
          </Button>
          {onOpenRmlPdf && (
            <Button
              variant="outline"
              size="sm"
              onClick={onOpenRmlPdf}
              data-testid="rml-pdf-button"
              className="shrink-0"
            >
              <FileText size={14} className="mr-1.5" /> PDF
            </Button>
          )}
        </div>
      </div>

      {/* Wave 3.25 — Linked Jewellery banner. Explains that "Consumed"
          rows reflect raw material now embedded in the finished jewellery
          piece(s) below. Keeps users from misreading "Consumed" as
          another flavour of "Returned to vault". */}
      {linkedJewellery.length > 0 && (
        <div
          className="mb-4 flex items-start gap-2 rounded-md border border-[var(--jewellery-icon)]/30 bg-[var(--jewellery-bg)]/40 px-3 py-2"
          data-testid="rml-consumed-banner"
        >
          <Diamond size={14} className="text-[var(--jewellery-icon)] mt-0.5 shrink-0" />
          <div className="text-[11px] leading-snug text-[var(--ink-navy)]">
            <span className="font-medium">Consumed</span> entries below correspond to raw material now embedded in
            {' '}
            {linkedJewellery.slice(0, 3).map((j, i) => (
              <React.Fragment key={j.id}>
                <Link
                  to={`/jewellery/${j.id}`}
                  className="font-mono text-[11px] text-[var(--jewellery-icon)] hover:underline"
                  data-testid={`rml-banner-jewellery-link-${j.id}`}
                >
                  #{j.jewellery_no}
                </Link>
                {i < Math.min(linkedJewellery.length, 3) - 1 ? ', ' : ''}
              </React.Fragment>
            ))}
            {linkedJewellery.length > 3 ? ` (+${linkedJewellery.length - 3} more)` : ''}
            . They have left the goldsmith's bench permanently, so the goldsmith balance for this order is
            <span className="font-medium"> Issued − Consumed − Returned</span> (should reach 0 once received).
          </div>
        </div>
      )}

      {/* Phase 9.8 — Always render in fixed order: Gold → Mountings → Diamonds → Color Stones → Pearls */}
      {/* Wave 3.99l — When user has zero material 'see' rights, show an
          explicit access notice so the section isn't rendered as an
          empty card. */}
      {!canSeeGold && !canSeeDiamond && !canSeeColorStone && !canSeePearl && !canSeeMounting && (
        <div
          className="text-sm text-slate-500 py-6 text-center border border-dashed rounded-md"
          data-testid="rml-empty-access"
        >
          You don&apos;t have access to view any raw material ledgers on this order.
        </div>
      )}

      {/* Gold Ledger (first) */}
      {canSeeGold && (
      <div className="mb-5" data-testid="rml-gold-block">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Coins size={14} className="text-[var(--gold-icon)]" />
            <div className="text-sm font-medium text-[var(--ink-navy)]">Gold</div>
            <CommentCalloutBadge
              filterKey="material:gold"
              spec={{ topic: 'raw_material', subtopic_id: 'gold' }}
              label="View comments on Gold"
              testId="material-comments-callout-gold"
              tone="amber"
            />
            {goldRows.length > 0 && (
              <Badge variant="secondary" className="text-[10px]" data-testid="gold-totals-badge">
                Issued {formatGrams(gTotal.issuedG)} g
                {gTotal.consumedG > 0 ? ` · Consumed ${formatGrams(gTotal.consumedG)} g` : ''}
                {gTotal.returnedG > 0 ? ` · Returned ${formatGrams(gTotal.returnedG)} g` : ''}
                {' · Balance '}{formatGrams(gTotal.issuedG - gTotal.consumedG - gTotal.returnedG)} g (999)
              </Badge>
            )}
          </div>
          {/* Phase 9.4 — Mode toggle */}
          {showValues && (
            <div className="inline-flex items-center rounded-md border border-[var(--divider)] overflow-hidden text-xs" data-testid="gold-value-mode-toggle">
              <button
                type="button"
                onClick={() => changeMode('ledger_cost')}
                disabled={savingMode}
                className={`px-3 py-1.5 ${mode === 'ledger_cost' ? 'bg-[var(--ink-navy)] text-white' : 'bg-white text-slate-700 hover:bg-slate-50'} disabled:opacity-60`}
                data-testid="gold-value-mode-ledger"
                title="Value gold at the cost captured when each line was issued (running-average at the time)"
              >Ledger Cost</button>
              <button
                type="button"
                onClick={() => changeMode('current_rate')}
                disabled={savingMode}
                className={`px-3 py-1.5 ${mode === 'current_rate' ? 'bg-[var(--ink-navy)] text-white' : 'bg-white text-slate-700 hover:bg-slate-50'} disabled:opacity-60`}
                data-testid="gold-value-mode-current"
                title="Re-value gold at the latest 999 rate from Gold Rates master"
              >Current Rate</button>
            </div>
          )}
        </div>
        {showValues && mode === 'current_rate' && (
          <div className="text-[11px] text-slate-500 mb-2 px-1">
            Re-valuing at latest 999 rate: <span className="font-mono text-[var(--ink-navy)]">{currentRate999 == null ? 'loading…' : formatINR(currentRate999) + ' / g'}</span>
            {orderPurityPercent > 0 && currentRate999 != null && (
              <> · effective {orderPurityName || 'jewellery'} rate ≈ <span className="font-mono text-[var(--ink-navy)]">{formatINR(currentRate999 * (orderPurityPercent / 100))} / g</span></>
            )}
          </div>
        )}
        {goldRows.length === 0 ? (
          <div className="text-sm text-slate-500 py-4 text-center border border-dashed rounded-md">No gold issued to this order yet.</div>
        ) : (
          <GoldLedgerTable rows={goldRows} orderPurityName={orderPurityName} orderPurityPercent={orderPurityPercent} mode={mode} currentRate999={currentRate999} showValues={showValues} />
        )}
        <InlineCommentList
          filterKey="material:gold"
          spec={{ topic: 'raw_material', subtopic_id: 'gold' }}
          emptyHint="No comments on Gold yet."
          testIdPrefix="material-comment-gold"
        />
      </div>
      )}

      {/* Wave 3.99z — Mountings (Semi-Finished Gold Pieces). Included in
          jewellery net weight but EXCLUDED from karigar labour
          calculation. Grams-only, no re-valuation modes needed. */}
      {canSeeMounting && (
      <div className="mb-5" data-testid="rml-mounting-block">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <Component size={14} className="text-[var(--gold-accent,#B08D57)]" />
          <div className="text-sm font-medium text-[var(--ink-navy)]">Mountings (Semi-Finished Gold Pieces)</div>
          {(mountingIssues.total_issued_grams || 0) > 0 && (
            <Badge variant="secondary" className="text-[10px] bg-amber-50 text-amber-800 border-amber-200" data-testid="mounting-totals-badge">
              Issued {Number(mountingIssues.total_issued_grams || 0).toFixed(3)} g
            </Badge>
          )}
        </div>
        <div
          className="mb-2 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 text-[11.5px] text-amber-900"
          data-testid="mounting-labour-callout"
        >
          <span className="font-medium">Included in Net Weight</span>
          {' · '}
          <span className="font-medium">Excluded from Karigar labour</span>
          {' — '}
          Karigar isn&apos;t paid labour for these pre-made pieces; only their weight adds to the final jewellery net.
        </div>
        {(mountingIssues.issues || []).length === 0 ? (
          <div className="text-sm text-slate-500 py-3 text-center border border-dashed rounded-md" data-testid="mounting-empty">
            No mountings issued to this order yet.
          </div>
        ) : (
          <MountingLedgerTable rows={mountingIssues.issues || []} />
        )}
      </div>
      )}

      {/* Diamonds Ledger */}
      {canSeeDiamond && (
      <div className="mb-5" data-testid="rml-diamond-block">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <Gem size={14} className="text-[var(--diamond-icon)]" />
          <div className="text-sm font-medium text-[var(--ink-navy)]">Diamonds</div>
          <CommentCalloutBadge
            filterKey="material:diamond"
            spec={{ topic: 'raw_material', subtopic_id: 'diamond' }}
            label="View comments on Diamonds"
            testId="material-comments-callout-diamond"
            tone="navy"
          />
          {diamondRows.length > 0 && (
            <Badge variant="secondary" className="text-[10px]" data-testid="diamond-totals-badge">
              Issued {formatCarats(dTotal.issuedC)} ct · {dTotal.issuedP} pcs
              {dTotal.consumedC > 0 ? ` · Consumed ${formatCarats(dTotal.consumedC)} ct / ${dTotal.consumedP} pcs` : ''}
              {dTotal.returnedC > 0 ? ` · Returned ${formatCarats(dTotal.returnedC)} ct / ${dTotal.returnedP} pcs` : ''}
              {' · Balance '}{formatCarats(dTotal.issuedC - dTotal.consumedC - dTotal.returnedC)} ct · {dTotal.issuedP - dTotal.consumedP - dTotal.returnedP} pcs
            </Badge>
          )}
        </div>
        {diamondRows.length === 0 ? (
          <div className="text-sm text-slate-500 py-4 text-center border border-dashed rounded-md">No diamonds issued to this order yet.</div>
        ) : (
          <DiamondLedgerTable
            rows={diamondRows}
            forceOpen={allExpanded}
            forceVersion={forceVersion}
            showValues={showValues}
          />
        )}
        <InlineCommentList
          filterKey="material:diamond"
          spec={{ topic: 'raw_material', subtopic_id: 'diamond' }}
          emptyHint="No comments on Diamonds yet."
          testIdPrefix="material-comment-diamond"
        />
      </div>
      )}

      {/* Color Stones Ledger — Phase 16 */}
      {canSeeColorStone && (
      <div className="mb-5" data-testid="rml-color-stone-block">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <Sparkles size={14} className="text-[var(--colorstone-icon, #c2185b)]" />
          <div className="text-sm font-medium text-[var(--ink-navy)]">Colour Stones</div>
          <CommentCalloutBadge
            filterKey="material:color_stone"
            spec={{ topic: 'raw_material', subtopic_id: 'color_stone' }}
            label="View comments on Colour Stones"
            testId="material-comments-callout-color_stone"
            tone="rose"
          />
          {colorStoneRows.length > 0 && (
            <Badge variant="secondary" className="text-[10px]" data-testid="color-stone-totals-badge">
              Issued {formatCarats(csTotal.issuedC)} ct · {csTotal.issuedP} pcs
              {csTotal.consumedC > 0 ? ` · Consumed ${formatCarats(csTotal.consumedC)} ct / ${csTotal.consumedP} pcs` : ''}
              {csTotal.returnedC > 0 ? ` · Returned ${formatCarats(csTotal.returnedC)} ct / ${csTotal.returnedP} pcs` : ''}
              {' · Balance '}{formatCarats(csTotal.issuedC - csTotal.consumedC - csTotal.returnedC)} ct · {csTotal.issuedP - csTotal.consumedP - csTotal.returnedP} pcs
            </Badge>
          )}
        </div>
        {colorStoneRows.length === 0 ? (
          <div className="text-sm text-slate-500 py-3 text-center border border-dashed rounded-md">No colour stones issued to this order yet.</div>
        ) : (
          <DiamondLedgerTable
            rows={colorStoneRows}
            packetRoutePrefix="/color-stones/packets"
            packetPillCls="bg-[var(--colorstone-bg,#FCE7F3)] text-[var(--colorstone-icon,#C84A6F)]"
            testIdPrefix="cs"
            forceOpen={allExpanded}
            forceVersion={forceVersion}
            showValues={showValues}
          />
        )}
        <InlineCommentList
          filterKey="material:color_stone"
          spec={{ topic: 'raw_material', subtopic_id: 'color_stone' }}
          emptyHint="No comments on Colour Stones yet."
          testIdPrefix="material-comment-color_stone"
        />
      </div>
      )}

      {/* Pearls Ledger — Phase 16 */}
      {canSeePearl && (
      <div data-testid="rml-pearl-block">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <CircleIcon size={14} className="text-[var(--pearl-icon, #6B7280)]" />
          <div className="text-sm font-medium text-[var(--ink-navy)]">Pearls</div>
          <CommentCalloutBadge
            filterKey="material:pearl"
            spec={{ topic: 'raw_material', subtopic_id: 'pearl' }}
            label="View comments on Pearls"
            testId="material-comments-callout-pearl"
            tone="emerald"
          />
          {pearlRows.length > 0 && (
            <Badge variant="secondary" className="text-[10px]" data-testid="pearl-totals-badge">
              Issued {formatCarats(prTotal.issuedC)} ct · {prTotal.issuedP} pcs
              {prTotal.consumedC > 0 ? ` · Consumed ${formatCarats(prTotal.consumedC)} ct / ${prTotal.consumedP} pcs` : ''}
              {prTotal.returnedC > 0 ? ` · Returned ${formatCarats(prTotal.returnedC)} ct / ${prTotal.returnedP} pcs` : ''}
              {' · Balance '}{formatCarats(prTotal.issuedC - prTotal.consumedC - prTotal.returnedC)} ct · {prTotal.issuedP - prTotal.consumedP - prTotal.returnedP} pcs
            </Badge>
          )}
        </div>
        {pearlRows.length === 0 ? (
          <div className="text-sm text-slate-500 py-3 text-center border border-dashed rounded-md">No pearls issued to this order yet.</div>
        ) : (
          <DiamondLedgerTable
            rows={pearlRows}
            packetRoutePrefix="/pearls/packets"
            packetPillCls="bg-slate-100 text-slate-700"
            testIdPrefix="pearl"
            forceOpen={allExpanded}
            forceVersion={forceVersion}
            showValues={showValues}
          />
        )}
        <InlineCommentList
          filterKey="material:pearl"
          spec={{ topic: 'raw_material', subtopic_id: 'pearl' }}
          emptyHint="No comments on Pearls yet."
          testIdPrefix="material-comment-pearl"
        />
      </div>
      )}
      {/* Wave 3.39 — Material Transfer dialog */}
      <TransferMaterialDialog
        open={transferOpen}
        onOpenChange={setTransferOpen}
        sourceOrder={order}
        onTransferred={() => {
          if (reload) reload();
        }}
      />
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Phase 9 hotfix — DiamondLedgerTable now groups entries BY PACKET.
//
// • Each packet renders a parent row showing per-packet totals (Issued Cts/Pcs,
//   Returned Cts/Pcs, Balance Cts/Pcs, Value). Collapsed by default for a
//   clean overview.
// • Clicking the chevron expands a per-packet detail table: each individual
//   issue/return entry with date, memo (or return memo), qty, and a running
//   balance computed PER-PACKET (so the balance column reflects this packet's
//   on-hand inventory, not a mixed total across all packets).
// ────────────────────────────────────────────────────────────────────────────
function DiamondLedgerTable({ rows, packetRoutePrefix = '/diamonds/packets', packetPillCls = 'bg-[var(--diamond-bg)] text-[var(--diamond-icon)]', testIdPrefix = 'diamond', forceOpen = false, forceVersion = 0, showValues = true }) {
  // Build packet groups, preserving chronological order of entries within each.
  const groups = useMemo(() => {
    const byPacket = new Map();
    rows.forEach((r) => {
      const key = r.packet_id || r.packet_no || '__unattributed__';
      if (!byPacket.has(key)) {
        byPacket.set(key, {
          packet_id: r.packet_id || null,
          packet_no: r.packet_no || '—',
          entries: [],
        });
      }
      byPacket.get(key).entries.push(r);
    });
    const list = Array.from(byPacket.values()).map((g) => {
      // sort entries by timestamp ascending so running balance is intuitive
      g.entries.sort((a, b) => (a.timestamp || '').localeCompare(b.timestamp || ''));
      let issuedC = 0, issuedP = 0, issuedV = 0;
      let consumedC = 0, consumedP = 0, consumedV = 0;
      let returnedC = 0, returnedP = 0, returnedV = 0;
      g.entries.forEach((r) => {
        const c = Math.abs(Number(r.carats || 0));
        const p = Math.abs(Number(r.pieces || 0));
        const v = Math.abs(Number(r.value || 0));
        const kind = classifyRmlEntry(r);
        if (kind === 'issued')        { issuedC += c; issuedP += p; issuedV += v; }
        else if (kind === 'consumed') { consumedC += c; consumedP += p; consumedV += v; }
        else                          { returnedC += c; returnedP += p; returnedV += v; }
      });
      g.totals = {
        issuedC, issuedP, issuedV,
        consumedC, consumedP, consumedV,
        returnedC, returnedP, returnedV,
        balanceC: issuedC - consumedC - returnedC,
        balanceP: issuedP - consumedP - returnedP,
        balanceV: issuedV - consumedV - returnedV,
      };
      return g;
    });
    // Wave 3.99c — Enforce the canonical Diamond sub-category hierarchy
    // (Polki → Diamonds → Rose Cuts → Brown Diamonds → Chakri → Other)
    // at the PACKET-GROUP level. Every entry inside a packet shares one
    // classification, so we rank each group by its first entry's
    // ``classification_label``. Groups with no classification fall to
    // the "Other" bucket last. Within a rank we preserve first-seen
    // chronological order for stable rendering.
    if (testIdPrefix === 'diamond') {
      const decorated = list.map((g, idx) => {
        const first = g.entries[0] || {};
        const [rank] = diamondHierarchyRank(first.classification_label || '');
        return { g, idx, rank };
      });
      decorated.sort((a, b) => a.rank - b.rank || a.idx - b.idx);
      return decorated.map((d) => d.g);
    }
    return list;
  }, [rows, testIdPrefix]);

  // Wave 3.22 — When the global "Expand All" toggle is active we render a
  // strictly *flat* chronological table: every individual ledger entry is
  // shown on its own row with a Packet column. The packet summary rows and
  // per-packet chevrons are hidden completely so the view is uncluttered.
  // Per-packet running balances are still surfaced on each row so the
  // numbers remain meaningful when the same packet has multiple entries.
  if (forceOpen) {
    return (
      <DiamondLedgerFlatTable
        groups={groups}
        packetRoutePrefix={packetRoutePrefix}
        packetPillCls={packetPillCls}
        testIdPrefix={testIdPrefix}
        showValues={showValues}
      />
    );
  }

  return (
    <div className="overflow-x-auto" data-testid={`order-${testIdPrefix}-ledger-grouped`}>
      <table className="w-full text-sm" data-testid={`order-${testIdPrefix}-ledger-table`}>
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="text-left px-2 py-1.5 w-8"></th>
            <th className="text-left px-2 py-1.5">Packet</th>
            <th className="text-right px-2 py-1.5">Issued Cts</th>
            <th className="text-right px-2 py-1.5">Issued Pcs</th>
            <th className="text-right px-2 py-1.5 text-amber-700">Consumed Cts</th>
            <th className="text-right px-2 py-1.5 text-amber-700">Consumed Pcs</th>
            <th className="text-right px-2 py-1.5 text-rose-700">Returned Cts</th>
            <th className="text-right px-2 py-1.5 text-rose-700">Returned Pcs</th>
            <th className="text-right px-2 py-1.5">Balance Cts</th>
            <th className="text-right px-2 py-1.5">Balance Pcs</th>
            {showValues && <th className="text-right px-2 py-1.5">Value</th>}
          </tr>
        </thead>
        <tbody>
          {groups.map((g, gi) => (
            <DiamondPacketGroup
              key={g.packet_id || g.packet_no || gi}
              group={g}
              gi={gi}
              packetRoutePrefix={packetRoutePrefix}
              packetPillCls={packetPillCls}
              testIdPrefix={testIdPrefix}
              forceOpen={forceOpen}
              forceVersion={forceVersion}
              showValues={showValues}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Wave 3.22 — Flat ("Expand All") variant of the packet-grouped ledger.
// All entries across all packets are laid out chronologically. Each row
// carries its packet pill so the user can still trace the source. Running
// balance is tracked per packet (re-derived as we walk the entries in
// timestamp order) so the numbers match the grouped view's totals.
// ----------------------------------------------------------------------------
function DiamondLedgerFlatTable({ groups, packetRoutePrefix, packetPillCls, testIdPrefix, showValues = true }) {
  const flatEntries = useMemo(() => {
    // Wave 3.25 hot-fix — Group rows by packet first so all activity for
    // the same packet sits together; within a packet the entries stay in
    // chronological order. Previously a global timestamp sort caused
    // entries from different packets to interleave, making it hard to
    // read the per-packet flow even in the "flat" view.
    //
    // Wave 3.26 — Tag each row with `isFirstOfPacket` / `isLastOfPacket`
    // so the renderer can draw a thin box border around consecutive rows
    // that share the same packet number (improves scannability in the
    // "Expand All" view).
    const items = [];
    groups.forEach((g) => {
      // Sort entries within the packet chronologically (the parent already
      // sorted; defensive re-sort to keep this component robust).
      const sorted = [...g.entries].sort((a, b) => (a.timestamp || '').localeCompare(b.timestamp || ''));
      sorted.forEach((e, eIdx) => {
        items.push({
          entry: e,
          packet: g,
          isFirstOfPacket: eIdx === 0,
          isLastOfPacket: eIdx === sorted.length - 1,
        });
      });
    });
    // Per-packet running balance — re-derived as we walk the grouped list.
    const balanceByPacket = new Map();
    return items.map((it) => {
      const pk = it.packet.packet_id || it.packet.packet_no || '__unattributed__';
      const prior = balanceByPacket.get(pk) || { bC: 0, bP: 0 };
      const next = {
        bC: prior.bC + Number(it.entry.carats || 0),
        bP: prior.bP + Number(it.entry.pieces || 0),
      };
      balanceByPacket.set(pk, next);
      return { ...it, balC: next.bC, balP: next.bP };
    });
  }, [groups]);

  if (flatEntries.length === 0) {
    return (
      <div className="text-sm text-slate-500 py-4 text-center border border-dashed rounded-md">
        No entries yet.
      </div>
    );
  }

  // Wave 3.26 — Resolve the packet "box" border styling for each row.
  // We render each row inside a single <tbody> but apply borders per-cell
  // so the box visually wraps consecutive same-packet rows. Top/bottom
  // borders only on the first/last row of a packet; left/right borders on
  // every row's outermost cells. Slightly darker `slate-400` keeps the
  // outline crisp without being heavy.
  const boxBorderColor = 'border-slate-400';
  const cellEdgeCls = (isFirst, isLast, position) => {
    // position ∈ 'first' | 'last' | 'middle'
    const parts = [];
    if (isFirst) parts.push(`border-t ${boxBorderColor}`);
    if (isLast) parts.push(`border-b ${boxBorderColor}`);
    if (position === 'first') {
      parts.push(`border-l ${boxBorderColor}`);
      if (isFirst) parts.push('rounded-tl');
      if (isLast) parts.push('rounded-bl');
    }
    if (position === 'last') {
      parts.push(`border-r ${boxBorderColor}`);
      if (isFirst) parts.push('rounded-tr');
      if (isLast) parts.push('rounded-br');
    }
    return parts.join(' ');
  };

  return (
    <div className="overflow-x-auto" data-testid={`order-${testIdPrefix}-ledger-flat`}>
      <table className="w-full text-sm border-separate border-spacing-0" data-testid={`order-${testIdPrefix}-ledger-flat-table`}>
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="text-left px-2 py-1.5">Date</th>
            <th className="text-left px-2 py-1.5">Memo</th>
            <th className="text-left px-2 py-1.5">Packet</th>
            <th className="text-right px-2 py-1.5">Issued Cts</th>
            <th className="text-right px-2 py-1.5">Issued Pcs</th>
            <th className="text-right px-2 py-1.5 text-amber-700">Consumed Cts</th>
            <th className="text-right px-2 py-1.5 text-amber-700">Consumed Pcs</th>
            <th className="text-right px-2 py-1.5 text-rose-700">Returned Cts</th>
            <th className="text-right px-2 py-1.5 text-rose-700">Returned Pcs</th>
            <th className="text-right px-2 py-1.5">Balance Cts</th>
            <th className="text-right px-2 py-1.5">Balance Pcs</th>
            {showValues && <th className="text-right px-2 py-1.5">Value</th>}
          </tr>
        </thead>
        <tbody>
          {flatEntries.map(({ entry, packet, balC, balP, isFirstOfPacket, isLastOfPacket }, idx) => {
            const c = Number(entry.carats || 0);
            const p = Number(entry.pieces || 0);
            const v = Number(entry.value || 0);
            const kind = classifyRmlEntry(entry);
            const isConsumed = kind === 'consumed';
            const isReturned = kind === 'returned';
            const isIssued = kind === 'issued';
            const rowBgCls = isConsumed ? 'bg-amber-50/40' : isReturned ? 'bg-rose-50/30' : 'hover:bg-slate-50/40';
            // Add a small top spacing gap between consecutive packet boxes.
            const rowSpacingCls = isFirstOfPacket && idx > 0 ? 'border-t-[6px] border-t-transparent' : '';
            const tdBase = 'px-2 py-1.5';
            return (
              <tr
                key={entry.entry_id || `${packet.packet_id || packet.packet_no}-${idx}`}
                className={`${rowBgCls} ${rowSpacingCls}`}
                data-testid={`${testIdPrefix}-flat-row-${idx}`}
                data-packet-no={packet.packet_no || ''}
                data-packet-first={isFirstOfPacket ? '1' : '0'}
                data-packet-last={isLastOfPacket ? '1' : '0'}
              >
                <td className={`${tdBase} text-[11px] text-slate-500 whitespace-nowrap ${cellEdgeCls(isFirstOfPacket, isLastOfPacket, 'first')}`}>
                  {formatDateTime(entry.timestamp)}
                </td>
                <td className={`${tdBase} ${cellEdgeCls(isFirstOfPacket, isLastOfPacket, 'middle')}`}>
                  {isConsumed ? (
                    <span className="text-[11px] text-amber-700 italic">Embedded in jewellery</span>
                  ) : entry.source === 'transfer_out' ? (
                    <span className="text-[11px] text-indigo-700 italic" data-testid={`${testIdPrefix}-flat-row-${idx}-transfer-out`}>↗ Transferred to {entry.transfer_to_order_no || '—'}</span>
                  ) : entry.source === 'transfer_in' ? (
                    <span className="text-[11px] text-emerald-700 italic" data-testid={`${testIdPrefix}-flat-row-${idx}-transfer-in`}>↙ Transferred from {entry.transfer_from_order_no || '—'}</span>
                  ) : isReturned && entry.source === 'jewellery_overflow_to_unattributed' ? (
                    <span className="text-[11px] text-rose-700 italic">↺ Returned to goldsmith pool</span>
                  ) : entry.source === 'jewellery_ghisai_loss' ? (
                    <span className="text-[11px] text-rose-700 italic" data-testid={`${testIdPrefix}-flat-row-${idx}-ghisai-loss`}>✂ Ghisai Loss (write-off)</span>
                  ) : (entry.issue_memo_no || entry.goldsmith_issue_no) ? (
                    <MemoLink
                      id={entry.goldsmith_issue_id}
                      memoNo={entry.issue_memo_no || entry.goldsmith_issue_no}
                      testId={`${testIdPrefix}-flat-row-${idx}-memo`}
                    />
                  ) : (
                    <span className="text-[11px] text-slate-400">—</span>
                  )}
                  {entry.return_memo_no && (
                    <ReturnMemoLink
                      id={entry.return_id}
                      memoNo={entry.return_memo_no}
                      className="ml-1"
                      testId={`${testIdPrefix}-flat-row-${idx}-return-memo`}
                    />
                  )}
                  {(entry.shape || entry.size || entry.sieves) ? (
                    <div
                      className="mt-0.5 flex flex-wrap items-center gap-1"
                      data-testid={`${testIdPrefix}-flat-row-${idx}-particulars`}
                    >
                      {entry.shape ? (
                        <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700">Shape: {entry.shape}</span>
                      ) : null}
                      {entry.size ? (
                        <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700">Size: {entry.size}</span>
                      ) : null}
                      {entry.sieves ? (
                        <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700">Sieves: {entry.sieves}</span>
                      ) : null}
                    </div>
                  ) : null}
                  {entry.note ? (
                    <div
                      className="mt-0.5 flex items-start gap-1 text-[11px] text-slate-500 italic max-w-[260px] whitespace-normal break-words"
                      title={entry.note}
                      data-testid={`${testIdPrefix}-flat-row-${idx}-note`}
                    >
                      <StickyNote size={11} className="mt-[1px] shrink-0 text-slate-400" />
                      <span>{entry.note}</span>
                    </div>
                  ) : null}
                </td>
                <td className={`${tdBase} ${cellEdgeCls(isFirstOfPacket, isLastOfPacket, 'middle')}`}>
                  {packet.packet_id ? (
                    <Link
                      to={`${packetRoutePrefix}/${packet.packet_id}`}
                      className={`font-mono text-[11px] ${packetPillCls} px-2 py-0.5 rounded hover:underline`}
                      data-testid={`${testIdPrefix}-flat-packet-link-${idx}`}
                    >
                      {packet.packet_no}
                    </Link>
                  ) : (
                    <span className="font-mono text-[11px]">{packet.packet_no}</span>
                  )}
                </td>
                <td className={`${tdBase} text-right font-mono tabular-nums ${cellEdgeCls(isFirstOfPacket, isLastOfPacket, 'middle')}`}>
                  {isIssued && Math.abs(c) > 0 ? formatCarats(Math.abs(c)) : ''}
                </td>
                <td className={`${tdBase} text-right font-mono tabular-nums ${cellEdgeCls(isFirstOfPacket, isLastOfPacket, 'middle')}`}>
                  {isIssued && Math.abs(p) > 0 ? Math.abs(p) : ''}
                </td>
                <td className={`${tdBase} text-right font-mono tabular-nums text-amber-700 ${cellEdgeCls(isFirstOfPacket, isLastOfPacket, 'middle')}`}>
                  {isConsumed && Math.abs(c) > 0 ? formatCarats(Math.abs(c)) : ''}
                </td>
                <td className={`${tdBase} text-right font-mono tabular-nums text-amber-700 ${cellEdgeCls(isFirstOfPacket, isLastOfPacket, 'middle')}`}>
                  {isConsumed && Math.abs(p) > 0 ? Math.abs(p) : ''}
                </td>
                <td className={`${tdBase} text-right font-mono tabular-nums text-rose-700 ${cellEdgeCls(isFirstOfPacket, isLastOfPacket, 'middle')}`}>
                  {isReturned && Math.abs(c) > 0 ? formatCarats(Math.abs(c)) : ''}
                </td>
                <td className={`${tdBase} text-right font-mono tabular-nums text-rose-700 ${cellEdgeCls(isFirstOfPacket, isLastOfPacket, 'middle')}`}>
                  {isReturned && Math.abs(p) > 0 ? Math.abs(p) : ''}
                </td>
                <td className={`${tdBase} text-right font-mono tabular-nums font-semibold text-[var(--ink-navy)] ${cellEdgeCls(isFirstOfPacket, isLastOfPacket, 'middle')}`}>
                  {formatCarats(balC)}
                </td>
                <td className={`${tdBase} text-right font-mono tabular-nums font-semibold text-[var(--ink-navy)] ${cellEdgeCls(isFirstOfPacket, isLastOfPacket, showValues ? 'middle' : 'last')}`}>
                  {balP}
                </td>
                {showValues && (
                  <td className={`${tdBase} text-right font-mono tabular-nums text-slate-700 ${cellEdgeCls(isFirstOfPacket, isLastOfPacket, 'last')}`}>
                    {formatINR(v)}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function DiamondPacketGroup({ group, gi, packetRoutePrefix = '/diamonds/packets', packetPillCls = 'bg-[var(--diamond-bg)] text-[var(--diamond-icon)]', testIdPrefix = 'diamond', forceOpen = false, forceVersion = 0, showValues = true }) {
  const [open, setOpen] = useState(forceOpen); // initial state mirrors global toggle
  // Wave 3.21 — when the parent flips the global Expand/Collapse toggle it
  // bumps `forceVersion`; every packet group syncs its local state to that
  // new value. Per-row chevron clicks afterwards still work independently
  // until the next global toggle.
  useEffect(() => {
    setOpen(forceOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceVersion]);
  const { totals } = group;
  // Wave 3.26 — Map the visual testIdPrefix back to the canonical material
  // slug used by the comment taxonomy (diamond | color_stone | pearl).
  const materialSlug = testIdPrefix === 'cs' ? 'color_stone' : testIdPrefix === 'pearl' ? 'pearl' : 'diamond';
  return (
    <>
      <tr
        className={`border-t border-[var(--divider)] hover:bg-slate-50/60 cursor-pointer ${open ? 'bg-slate-50/40' : ''}`}
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testIdPrefix}-packet-row-${gi}`}
      >
        <td className="px-2 py-1.5 text-slate-500">
          <button
            type="button"
            className="inline-flex items-center justify-center h-5 w-5 rounded hover:bg-slate-200 transition-colors duration-150"
            data-testid={`${testIdPrefix}-packet-toggle-${gi}`}
            aria-label={open ? 'Collapse packet details' : 'Expand packet details'}
            onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
          >
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        </td>
        <td className="px-2 py-1.5">
          {group.packet_id ? (
            <Link
              to={`${packetRoutePrefix}/${group.packet_id}`}
              onClick={(e) => e.stopPropagation()}
              className={`font-mono text-[12px] ${packetPillCls} px-2 py-0.5 rounded hover:underline`}
              data-testid={`${testIdPrefix}-packet-link-${gi}`}
            >
              {group.packet_no}
            </Link>
          ) : (
            <span className="font-mono text-[12px]">{group.packet_no}</span>
          )}
          <span className="text-[11px] text-slate-500 ml-2" data-testid={`${testIdPrefix}-packet-entry-count-${gi}`}>
            {group.entries.length} entr{group.entries.length === 1 ? 'y' : 'ies'}
          </span>
          {/* Wave 3.26 — Comment callout for this specific packet */}
          {group.packet_id && (
            <span className="ml-2 inline-block" onClick={(e) => e.stopPropagation()}>
              <CommentCalloutBadge
                filterKey={`packet:${group.packet_id}`}
                spec={{ topic: 'raw_material', subtopic_id: materialSlug, sub_subtopic_id: group.packet_id }}
                label={`View comments on packet ${group.packet_no}`}
                testId={`${testIdPrefix}-packet-comments-callout-${gi}`}
                size="xs"
              />
            </span>
          )}
        </td>
        <td className="px-2 py-1.5 text-right font-mono tabular-nums" data-testid={`${testIdPrefix}-packet-issued-cts-${gi}`}>
          {totals.issuedC > 0 ? formatCarats(totals.issuedC) : ''}
        </td>
        <td className="px-2 py-1.5 text-right font-mono tabular-nums" data-testid={`${testIdPrefix}-packet-issued-pcs-${gi}`}>
          {totals.issuedP > 0 ? totals.issuedP : ''}
        </td>
        <td className="px-2 py-1.5 text-right font-mono tabular-nums text-amber-700" data-testid={`${testIdPrefix}-packet-consumed-cts-${gi}`}>
          {totals.consumedC > 0 ? formatCarats(totals.consumedC) : ''}
        </td>
        <td className="px-2 py-1.5 text-right font-mono tabular-nums text-amber-700" data-testid={`${testIdPrefix}-packet-consumed-pcs-${gi}`}>
          {totals.consumedP > 0 ? totals.consumedP : ''}
        </td>
        <td className="px-2 py-1.5 text-right font-mono tabular-nums text-rose-700" data-testid={`${testIdPrefix}-packet-returned-cts-${gi}`}>
          {totals.returnedC > 0 ? formatCarats(totals.returnedC) : ''}
        </td>
        <td className="px-2 py-1.5 text-right font-mono tabular-nums text-rose-700" data-testid={`${testIdPrefix}-packet-returned-pcs-${gi}`}>
          {totals.returnedP > 0 ? totals.returnedP : ''}
        </td>
        <td className="px-2 py-1.5 text-right font-mono tabular-nums font-semibold text-[var(--ink-navy)]" data-testid={`${testIdPrefix}-packet-balance-cts-${gi}`}>
          {formatCarats(totals.balanceC)}
        </td>
        <td className="px-2 py-1.5 text-right font-mono tabular-nums font-semibold text-[var(--ink-navy)]" data-testid={`${testIdPrefix}-packet-balance-pcs-${gi}`}>
          {totals.balanceP}
        </td>
        {showValues && (
          <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-700" data-testid={`${testIdPrefix}-packet-balance-value-${gi}`}>
            {formatINR(totals.balanceV)}
          </td>
        )}
      </tr>
      {/* Wave 3.26 — Inline filtered comments for this specific packet */}
      {group.packet_id && (
        <tr data-testid={`${testIdPrefix}-packet-comments-${gi}`}>
          <td></td>
          <td colSpan={showValues ? 10 : 9} className="p-0">
            <InlineCommentList
              filterKey={`packet:${group.packet_id}`}
              spec={{ topic: 'raw_material', subtopic_id: materialSlug, sub_subtopic_id: group.packet_id }}
              emptyHint="No comments on this packet yet."
              testIdPrefix={`${testIdPrefix}-packet-comment-${gi}`}
            />
          </td>
        </tr>
      )}
      {open && (
        <tr data-testid={`${testIdPrefix}-packet-details-${gi}`}>
          <td></td>
          <td colSpan={showValues ? 10 : 9} className="p-0">
            <div className="bg-slate-50/40 border-l-2 border-l-[var(--diamond-icon)] my-1 mx-2 rounded">
              <table className="w-full text-xs">
                <thead className="text-slate-500">
                  <tr>
                    <th className="text-left px-2 py-1.5">Date</th>
                    <th className="text-left px-2 py-1.5">Memo</th>
                    <th className="text-right px-2 py-1.5">Issued Cts</th>
                    <th className="text-right px-2 py-1.5">Issued Pcs</th>
                    <th className="text-right px-2 py-1.5 text-amber-700">Consumed Cts</th>
                    <th className="text-right px-2 py-1.5 text-amber-700">Consumed Pcs</th>
                    <th className="text-right px-2 py-1.5 text-rose-700">Returned Cts</th>
                    <th className="text-right px-2 py-1.5 text-rose-700">Returned Pcs</th>
                    <th className="text-right px-2 py-1.5">Balance Cts</th>
                    <th className="text-right px-2 py-1.5">Balance Pcs</th>
                    {showValues && <th className="text-right px-2 py-1.5">Value</th>}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Per-packet running balance (signed sum: positive issues
                    // less consumed + returned, both of which arrive as negative
                    // entries from the backend).
                    let bC = 0, bP = 0;
                    return group.entries.map((r, i) => {
                      const c = Number(r.carats || 0);
                      const p = Number(r.pieces || 0);
                      const v = Number(r.value || 0);
                      const kind = classifyRmlEntry(r);
                      const isConsumed = kind === 'consumed';
                      const isReturned = kind === 'returned';
                      const isIssued = kind === 'issued';
                      bC += c; bP += p;
                      return (
                        <tr
                          key={r.entry_id || i}
                          className={`border-t border-[var(--divider)] ${isConsumed ? 'bg-amber-50/50' : isReturned ? 'bg-rose-50/40' : ''}`}
                          data-testid={`${testIdPrefix}-packet-${gi}-entry-${i}`}
                        >
                          <td className="px-2 py-1.5 text-[11px] text-slate-500 whitespace-nowrap">{formatDateTime(r.timestamp)}</td>
                          <td className="px-2 py-1.5">
                            {isConsumed ? (
                              <span className="text-[11px] text-amber-700 italic">Embedded in jewellery</span>
                            ) : r.source === 'transfer_out' ? (
                              <span className="text-[11px] text-indigo-700 italic" data-testid={`${testIdPrefix}-packet-${gi}-entry-${i}-transfer-out`}>↗ Transferred to {r.transfer_to_order_no || '—'}</span>
                            ) : r.source === 'transfer_in' ? (
                              <span className="text-[11px] text-emerald-700 italic" data-testid={`${testIdPrefix}-packet-${gi}-entry-${i}-transfer-in`}>↙ Transferred from {r.transfer_from_order_no || '—'}</span>
                            ) : isReturned && r.source === 'jewellery_overflow_to_unattributed' ? (
                              <span className="text-[11px] text-rose-700 italic">↺ Returned to goldsmith pool</span>
                            ) : r.source === 'jewellery_ghisai_loss' ? (
                              <span className="text-[11px] text-rose-700 italic" data-testid={`${testIdPrefix}-packet-${gi}-entry-${i}-ghisai-loss`}>✂ Ghisai Loss (write-off)</span>
                            ) : (r.issue_memo_no || r.goldsmith_issue_no) ? (
                              <MemoLink
                                id={r.goldsmith_issue_id}
                                memoNo={r.issue_memo_no || r.goldsmith_issue_no}
                                testId={`${testIdPrefix}-packet-${gi}-entry-${i}-memo`}
                              />
                            ) : (
                              <span className="text-[11px] text-slate-400">—</span>
                            )}
                            {r.return_memo_no && (
                              <ReturnMemoLink
                                id={r.return_id}
                                memoNo={r.return_memo_no}
                                className="ml-1"
                                testId={`${testIdPrefix}-packet-${gi}-entry-${i}-return-memo`}
                              />
                            )}
                            {r.note ? (
                              <div
                                className="mt-0.5 flex items-start gap-1 text-[11px] text-slate-500 italic max-w-[260px] whitespace-normal break-words"
                                title={r.note}
                                data-testid={`${testIdPrefix}-packet-${gi}-entry-${i}-note`}
                              >
                                <StickyNote size={11} className="mt-[1px] shrink-0 text-slate-400" />
                                <span>{r.note}</span>
                              </div>
                            ) : null}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono tabular-nums">{isIssued && Math.abs(c) > 0 ? formatCarats(Math.abs(c)) : ''}</td>
                          <td className="px-2 py-1.5 text-right font-mono tabular-nums">{isIssued && Math.abs(p) > 0 ? Math.abs(p) : ''}</td>
                          <td className="px-2 py-1.5 text-right font-mono tabular-nums text-amber-700">{isConsumed && Math.abs(c) > 0 ? formatCarats(Math.abs(c)) : ''}</td>
                          <td className="px-2 py-1.5 text-right font-mono tabular-nums text-amber-700">{isConsumed && Math.abs(p) > 0 ? Math.abs(p) : ''}</td>
                          <td className="px-2 py-1.5 text-right font-mono tabular-nums text-rose-700">{isReturned && Math.abs(c) > 0 ? formatCarats(Math.abs(c)) : ''}</td>
                          <td className="px-2 py-1.5 text-right font-mono tabular-nums text-rose-700">{isReturned && Math.abs(p) > 0 ? Math.abs(p) : ''}</td>
                          <td className="px-2 py-1.5 text-right font-mono tabular-nums">{formatCarats(bC)}</td>
                          <td className="px-2 py-1.5 text-right font-mono tabular-nums">{bP}</td>
                          {showValues && (
                            <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-500">
                              {formatINR(Math.abs(v))}{v < 0 ? (isConsumed ? ' ⚒' : ' ↺') : ''}
                            </td>
                          )}
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function GoldLedgerTable({ rows, orderPurityName, orderPurityPercent, mode, currentRate999, showValues = true }) {
  let bal999 = 0, balV = 0;
  const isCurrent = mode === 'current_rate';
  // Effective per-g999 rate when in current_rate mode (re-value EVERY row at the same rate)
  const currentRate = Number(currentRate999 || 0);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="order-gold-ledger-table">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="text-left px-2 py-1.5">Date</th>
            <th className="text-left px-2 py-1.5">Memo</th>
            <th className="text-left px-2 py-1.5">Issued Purity</th>
            <th className="text-right px-2 py-1.5">Issued 999 (g)</th>
            <th className="text-left px-2 py-1.5">→ Jewellery Purity</th>
            <th className="text-right px-2 py-1.5 text-amber-700">Consumed 999 (g)</th>
            <th className="text-right px-2 py-1.5 text-rose-700">Returned 999 (g)</th>
            <th className="text-right px-2 py-1.5">Balance 999 (g)</th>
            <th className="text-left px-2 py-1.5">→ Balance @ Jewellery Purity</th>
            {showValues && <th className="text-right px-2 py-1.5" title={isCurrent ? 'Re-valued at the latest 999 rate from Gold Rates master' : 'Ledger cost captured at the time of each issue (running average)'}>{isCurrent ? 'Value (Current Rate)' : 'Value (Ledger Cost)'}</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            // Phase 8.2: Prefer per-line snapshot, fall back to order-level snapshot.
            const purityName = r.order_jewellery_purity_name || orderPurityName || '';
            const purityPercent = Number(r.order_jewellery_purity_percent || orderPurityPercent || 0);
            // ──────────────────────────────────────────────────────────
            // Phase 9 hotfix: gold ledger entries may carry only `grams`
            // (raw issue weight) without `grams_999` (pure weight) when
            // the entry was inserted via a legacy code path. In those
            // cases derive grams_999 from the entry's own issued-purity
            // (or from the order-level purity as a final fallback) so
            // the Gold 999 (g), running balance, and conversion columns
            // all render correctly.
            // ──────────────────────────────────────────────────────────
            const rawGrams = Number(r.grams ?? r.weight_grams ?? 0);
            const issuedPurityPct = Number(r.purity_percent ?? r.purity?.percent ?? 0);
            let g999 = Number(r.grams_999 ?? r.pure_grams_999 ?? 0);
            if (!g999 && rawGrams) {
              const pp = issuedPurityPct || purityPercent || 0;
              if (pp > 0) g999 = +(rawGrams * (pp / 100)).toFixed(3);
              else g999 = rawGrams; // last resort — treat as 999
            }
            const ledgerValue = Number(r.value || 0);
            // Wave 3.25 — classify Gold entries with the shared helper so
            // 'jewellery_consumed' lands in the Consumed column instead of
            // Returned.
            const kind = classifyRmlEntry({ ...r, grams_999: g999 });
            const isConsumed = kind === 'consumed';
            const isReturned = kind === 'returned';
            const isIssued = kind === 'issued';
            // Phase 9.4 — Recompute value in current_rate mode.
            // current_value = grams_999 × current_rate (sign preserved for returns)
            const computedValue = isCurrent ? (g999 * currentRate) : ledgerValue;
            // Convert from 999 to jewellery purity: jewellery_grams = grams_999 / (purity_percent / 100)
            const factor = purityPercent > 0 ? (purityPercent / 100) : null;
            const convertedIssue = factor ? (Math.abs(g999) / factor) : null;
            bal999 += g999; balV += computedValue;
            // Phase 9.11: Balance shown also in converted (jewellery purity) grams
            const balConverted = factor ? (bal999 / factor) : null;
            return (
              <tr
                key={`${r.entry_id || i}`}
                className={`border-t border-[var(--divider)] ${isConsumed ? 'bg-amber-50/40' : isReturned ? 'bg-rose-50/40' : ''}`}
                data-testid={`order-gold-row-${i}`}
              >
                <td className="px-2 py-1.5 text-xs text-slate-500 whitespace-nowrap">{formatDateTime(r.timestamp)}</td>
                <td className="px-2 py-1.5">
                  {isConsumed ? <span className="text-[11px] text-amber-700 italic">Embedded in jewellery</span>
                    : r.source === 'transfer_out' ? <span className="text-[11px] text-indigo-700 italic" data-testid={`order-gold-row-${i}-transfer-out`}>↗ Transferred to {r.transfer_to_order_no || '—'}</span>
                    : r.source === 'transfer_in' ? <span className="text-[11px] text-emerald-700 italic" data-testid={`order-gold-row-${i}-transfer-in`}>↙ Transferred from {r.transfer_from_order_no || '—'}</span>
                    : isReturned && r.source === 'jewellery_overflow_to_unattributed' ? <span className="text-[11px] text-rose-700 italic">↺ Returned to goldsmith pool</span>
                    : (r.issue_memo_no || r.goldsmith_issue_no)
                      ? <MemoLink id={r.goldsmith_issue_id} memoNo={r.issue_memo_no || r.goldsmith_issue_no} testId={`order-gold-memo-${i}`} />
                      : <span className="text-[11px] text-slate-400">—</span>}
                  {r.return_memo_no && (
                    <ReturnMemoLink
                      id={r.return_id}
                      memoNo={r.return_memo_no}
                      className="ml-1"
                      testId={`order-gold-return-memo-${i}`}
                    />
                  )}
                  {r.note ? (
                    <div
                      className="mt-0.5 flex items-start gap-1 text-[11px] text-slate-500 italic max-w-[260px] whitespace-normal break-words"
                      title={r.note}
                      data-testid={`order-gold-note-${i}`}
                    >
                      <StickyNote size={11} className="mt-[1px] shrink-0 text-slate-400" />
                      <span>{r.note}</span>
                    </div>
                  ) : null}
                </td>
                <td className="px-2 py-1.5 text-xs">{r.purity_name || (r.purity_percent ? `${r.purity_percent}%` : '—')}</td>
                <td className="px-2 py-1.5 text-right font-mono tabular-nums">{isIssued && Math.abs(g999) > 0 ? formatGrams(Math.abs(g999)) : ''}</td>
                <td className="px-2 py-1.5 text-xs whitespace-nowrap" data-testid={`order-gold-conversion-${i}`}>
                  {convertedIssue != null && Math.abs(g999) > 0 ? (
                    <span className="font-mono text-slate-700">
                      <span className="text-slate-400">→ </span>
                      {purityName ? <span className="text-[var(--ink-navy)] font-medium">{purityName}</span> : <span className="text-slate-500">{purityPercent.toFixed(2)}%</span>}
                      {' = '}
                      <span className={isReturned ? 'text-rose-700' : (isConsumed ? 'text-amber-700' : '')}>{formatGrams(convertedIssue)} g</span>
                    </span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
                <td className="px-2 py-1.5 text-right font-mono tabular-nums text-amber-700" data-testid={`order-gold-consumed-${i}`}>
                  {isConsumed && Math.abs(g999) > 0 ? formatGrams(Math.abs(g999)) : ''}
                </td>
                <td className="px-2 py-1.5 text-right font-mono tabular-nums text-rose-700" data-testid={`order-gold-returned-${i}`}>
                  {isReturned && Math.abs(g999) > 0 ? formatGrams(Math.abs(g999)) : ''}
                </td>
                <td className="px-2 py-1.5 text-right font-mono tabular-nums">{formatGrams(bal999)}</td>
                <td className="px-2 py-1.5 text-xs whitespace-nowrap" data-testid={`order-gold-balance-conv-${i}`}>
                  {balConverted != null ? (
                    <span className="font-mono text-slate-700">
                      <span className="text-slate-400">→ </span>
                      {purityName ? <span className="text-[var(--ink-navy)] font-medium">{purityName}</span> : <span className="text-slate-500">{purityPercent.toFixed(2)}%</span>}
                      {' = '}
                      <span>{formatGrams(balConverted)} g</span>
                    </span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
                {showValues && (
                  <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-500" data-testid={`order-gold-value-${i}`}>
                    {formatINR(Math.abs(computedValue))}{computedValue < 0 ? (isConsumed ? ' ⚒' : ' ↺') : ''}
                  </td>
                )}
              </tr>
            );
          })}
          {/* Totals row */}
          {rows.length > 0 && showValues && (
            <tr className="border-t-2 border-[var(--ink-navy)] bg-slate-50">
              <td colSpan={9} className="px-2 py-1.5 text-right text-xs text-slate-700 font-medium">{isCurrent ? 'Total Value @ Current Rate' : 'Total Value (Ledger Cost)'}</td>
              <td className="px-2 py-1.5 text-right font-mono tabular-nums font-medium text-[var(--ink-navy)]" data-testid="order-gold-total-value">{formatINR(Math.abs(balV))}</td>
            </tr>
          )}
        </tbody>
      </table>
      {orderPurityPercent > 0 && (
        <div className="text-[10px] text-slate-500 mt-2 px-2">
          Conversion: <span className="font-mono">{orderPurityName || `${orderPurityPercent.toFixed(2)}%`} grams = 999 grams ÷ ({orderPurityPercent.toFixed(2)}/100)</span>
          {showValues && isCurrent && currentRate > 0 && (
            <> · Value = grams (999) × <span className="font-mono">{formatINR(currentRate)}</span> = grams ({orderPurityName || `${orderPurityPercent.toFixed(2)}%`}) × {formatINR(currentRate * (orderPurityPercent / 100))}</>
          )}
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Wave 3.99z — Mountings ledger table (per order). Shows every mounting issue
// and any voids applied to this order. Grams-only, no valuation.
// ----------------------------------------------------------------------------
function MountingLedgerTable({ rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="order-mounting-ledger-table">
        <thead className="bg-slate-50 text-slate-600 text-xs">
          <tr className="text-left">
            <th className="px-2 py-2 font-medium">When</th>
            <th className="px-2 py-2 font-medium">Mounting</th>
            <th className="px-2 py-2 font-medium">Purity</th>
            <th className="px-2 py-2 font-medium text-right">Weight (g)</th>
            <th className="px-2 py-2 font-medium">Notes / Flags</th>
            <th className="px-2 py-2 font-medium">By</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r) => {
            const isVoid = r.entry_type === 'void';
            const grams = Math.abs(Number(r.weight_grams_delta || 0));
            return (
              <tr
                key={r.id}
                className={isVoid ? 'bg-rose-50/40' : ''}
                data-testid={`order-mounting-row-${r.id}`}
              >
                <td className="px-2 py-1.5 text-xs text-slate-600 whitespace-nowrap">
                  {formatDateTime(r.timestamp)}
                </td>
                <td className="px-2 py-1.5">
                  <div className="inline-flex items-center gap-1.5">
                    <Component size={12} className="text-amber-700" />
                    <span className="font-medium">{r.mounting_name}</span>
                  </div>
                </td>
                <td className="px-2 py-1.5">
                  <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-[10px]">
                    {r.purity_name || '—'}
                  </Badge>
                </td>
                <td
                  className={`px-2 py-1.5 text-right font-mono tabular-nums ${
                    isVoid ? 'text-rose-700' : 'text-emerald-700'
                  }`}
                >
                  {isVoid ? `↺ +${grams.toFixed(3)}` : grams.toFixed(3)}
                </td>
                <td className="px-2 py-1.5 text-xs text-slate-700">
                  {isVoid && (
                    <Badge className="bg-rose-50 text-rose-700 border-rose-200 text-[10px] mr-1">
                      voided
                    </Badge>
                  )}
                  {r.purity_mismatch && (
                    <Badge
                      className="bg-orange-50 text-orange-700 border-orange-200 text-[10px] mr-1 inline-flex items-center gap-1"
                      title="Mounting purity did not match the order form purity when this row was recorded"
                    >
                      <AlertTriangle size={9} /> purity mismatch
                    </Badge>
                  )}
                  {r.notes && (
                    <span
                      className="text-[11px] text-slate-600 truncate inline-block max-w-[220px] align-middle"
                      title={r.notes}
                    >
                      {r.notes}
                    </span>
                  )}
                </td>
                <td
                  className="px-2 py-1.5 text-xs text-slate-500 truncate max-w-[180px]"
                  title={r.created_by_email || ''}
                >
                  {r.created_by_email || <span className="text-slate-400">—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ImagesSection({ order, reload, erpCapKb = 500 }) {
  const { isSalesRole } = useAuth();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [delIdx, setDelIdx] = useState(null);
  // Wave 4.0 — per-file upload progress: [{name, kind, status, progress, error, slow}]
  // status: 'pending' | 'reading' | 'uploading' | 'done' | 'error'
  const [uploadItems, setUploadItems] = useState([]);
  // Wave 3.84 — Drag-drop upload state + inline title-edit state.
  const [isDragging, setIsDragging] = useState(false);
  const [titleEditIdx, setTitleEditIdx] = useState(null);
  const [titleDraft, setTitleDraft] = useState('');
  const [savingTitleIdx, setSavingTitleIdx] = useState(null);
  // Sales roles are view-only: they may open/view reference files but cannot
  // upload, remove, or rename them.
  const isLocked = ['Completed', 'Cancelled'].includes((order.stage || '').trim()) || isSalesRole;

  // Wave 3.74 — Order attachments now accept PDFs in addition to images.
  // The reader still uses base64 data URLs — PDFs simply carry the
  // ``data:application/pdf;base64,`` prefix which the backend routes
  // through a dedicated "store as-is, no PIL" path.
  const isPdfSrc = (src) => typeof src === 'string' && src.startsWith('data:application/pdf');

  // Wave 4.1 — reference files are capped per the Super-Admin ERP attachment
  // cap (default 500 KB; configured in Portal Access → Attachment Size Caps).
  // Oversized IMAGES are auto-compressed client-side to fit; oversized PDFs
  // are blocked (browsers can't reliably shrink them). We surface the byte
  // size of every stored file so users can spot heavy assets at a glance.
  const MAX_FILE_BYTES = (Number(erpCapKb) || 500) * 1024;
  const MAX_FILE_LABEL = `${Number(erpCapKb) || 500} KB`;
  // Estimate the decoded byte size of a base64 data URL (payload only, minus
  // padding). Non-data URLs (already-remote http/https) return 0 → no label.
  const dataUrlBytes = (s) => {
    if (typeof s !== 'string') return 0;
    const comma = s.indexOf(',');
    if (comma < 0 || !s.startsWith('data:')) return 0;
    const b64 = s.slice(comma + 1);
    const len = b64.length;
    if (!len) return 0;
    const pad = b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0;
    return Math.max(0, Math.floor((len * 3) / 4) - pad);
  };
  const formatBytes = (n) => {
    if (!n || n <= 0) return '';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Wave 3.84 — Shared uploader (used by <input type=file> AND drag-drop).
  // Wave 4.0 — uploads run one-file-at-a-time so we can show a real per-file
  // progress bar (axios onUploadProgress) and surface clear per-file errors /
  // a slow-connection warning. Full images now stream to object storage on the
  // server, so this progress reflects the browser -> server transfer.
  const uploadFileList = async (files) => {
    if (!files || files.length === 0) return;
    // Pre-validate: block unsupported types + oversized PDFs; auto-compress
    // oversized images so they fit under the cap (only blocked if impossible).
    const valid = [];
    for (const f of files) {
      const isImage = f.type.startsWith('image/');
      const isPdf = f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf');
      if (!isImage && !isPdf) { toast.error(`${f.name} is not an image or PDF — skipped`); continue; }
      if (isPdf) {
        if (f.size > MAX_FILE_BYTES) {
          toast.error(`“${f.name}” is ${formatBytes(f.size)} — PDFs must be ${MAX_FILE_LABEL} or smaller. Please compress it below ${MAX_FILE_LABEL} and re-upload.`);
          continue;
        }
        valid.push({ file: f, isPdf: true });
        continue;
      }
      // Image: keep as-is if already under the cap, else auto-compress.
      let outFile = f;
      if (f.size > MAX_FILE_BYTES) {
        try {
          const blob = await compressImageBlob(f, MAX_FILE_BYTES);
          if (!blob || blob.size > MAX_FILE_BYTES) throw new Error('too_large');
          outFile = blob instanceof File ? blob : blobToFile(blob, f.name);
        } catch (_e) {
          toast.error(`“${f.name}” (${formatBytes(f.size)}) couldn’t be compressed under ${MAX_FILE_LABEL} — please use a smaller image.`);
          continue;
        }
      }
      valid.push({ file: outFile, isPdf: false });
    }
    if (valid.length === 0) return;

    setUploading(true);
    setUploadItems(valid.map((v) => ({
      name: v.file.name, kind: v.isPdf ? 'pdf' : 'image',
      status: 'pending', progress: 0, error: '', slow: false,
    })));
    const patchItem = (i, patch) => setUploadItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));

    let renderPdfThumb = null;
    let succeeded = 0;
    let failed = 0;

    for (let i = 0; i < valid.length; i++) {
      const { file: f, isPdf } = valid[i];
      let slowTimer = null;
      try {
        patchItem(i, { status: 'reading', progress: 0 });
        const dataUrl = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result);
          r.onerror = () => rej(new Error('Could not read file'));
          r.readAsDataURL(f);
        });
        let thumb = null;
        if (isPdf) {
          if (!renderPdfThumb) {
            const mod = await import('@/lib/pdfThumbnail');
            renderPdfThumb = mod.renderPdfThumbnailDataUrl;
          }
          try { thumb = await renderPdfThumb(dataUrl); } catch (_e) { thumb = null; }
        }
        const title = (f.name || '').replace(/\.[^./\\]+$/, '').slice(0, 180);

        patchItem(i, { status: 'uploading', progress: 0 });
        // Slow-connection watchdog: if the transfer is still going after 12s,
        // flag it inline and warn once so the user knows it isn't stuck.
        slowTimer = setTimeout(() => {
          patchItem(i, { slow: true });
          toast.warning(`Slow connection — still uploading “${f.name}”…`);
        }, 12000);

        await api.post(
          `/orders/${order.id}/images`,
          { images: [dataUrl], thumbnails: [thumb], titles: [title] },
          {
            timeout: 120000,
            onUploadProgress: (evt) => {
              const pct = evt.total
                ? Math.round((evt.loaded / evt.total) * 100)
                : (evt.loaded ? 90 : 0);
              patchItem(i, { progress: Math.min(99, pct) });
            },
          },
        );
        clearTimeout(slowTimer);
        patchItem(i, { status: 'done', progress: 100, slow: false });
        succeeded += 1;
      } catch (err) {
        if (slowTimer) clearTimeout(slowTimer);
        const msg = extractError(err, 'Upload failed');
        patchItem(i, { status: 'error', error: msg, slow: false });
        toast.error(`Failed to upload “${f.name}”: ${msg}`);
        failed += 1;
      }
    }

    if (succeeded > 0) {
      toast.success(`${succeeded} file(s) uploaded`);
      reload();
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
    // Auto-dismiss the panel on a fully clean run; keep it visible when any
    // file failed so the user can read the error.
    if (failed === 0) {
      setTimeout(() => setUploadItems([]), 2200);
    }
  };

  const onFiles = (e) => uploadFileList(Array.from(e.target.files || []));

  const onDrop = async (e) => {
    e.preventDefault(); e.stopPropagation();
    setIsDragging(false);
    if (isLocked || uploading) return;
    const dt = e.dataTransfer;
    if (!dt) return;
    const files = Array.from(dt.files || []);
    if (files.length === 0) return;
    await uploadFileList(files);
  };
  const onDragEnter = (e) => {
    if (isLocked || uploading) return;
    e.preventDefault(); e.stopPropagation();
    setIsDragging(true);
  };
  const onDragOver = (e) => {
    if (isLocked || uploading) return;
    e.preventDefault(); e.stopPropagation();
  };
  const onDragLeave = (e) => {
    e.preventDefault(); e.stopPropagation();
    // Only reset when leaving the wrapper (relatedTarget outside)
    if (e.currentTarget && e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };

  const beginTitleEdit = (i, cur) => {
    setTitleEditIdx(i);
    setTitleDraft(cur || '');
  };
  const cancelTitleEdit = () => {
    setTitleEditIdx(null);
    setTitleDraft('');
  };
  const saveTitle = async (i) => {
    setSavingTitleIdx(i);
    try {
      await api.patch(`/orders/${order.id}/images/${i}`, { title: (titleDraft || '').trim() });
      toast.success('Title updated');
      setTitleEditIdx(null);
      setTitleDraft('');
      reload();
    } catch (err) {
      toast.error(extractError(err, 'Rename failed'));
    } finally {
      setSavingTitleIdx(null);
    }
  };

  const imgs = order.images || [];
  const thumbs = order.images_thumb || [];
  const titles = order.image_titles || [];
  return (
    <Card
      className={`p-5 transition-colors ${isDragging ? 'ring-2 ring-[var(--gold-accent)] ring-offset-2 bg-[var(--paper-warm)]/40' : ''}`}
      onDrop={onDrop}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      data-testid="jewellery-files-card"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-medium text-[var(--ink-navy)] flex items-center gap-2"><Sparkles size={14} className="text-[var(--jewellery-icon)]" /> Jewellery Files</div>
          <div className="text-xs text-slate-500">
            Upload reference images or PDF tiles for this order. Images auto-compress to ≤{MAX_FILE_LABEL}; PDFs must be ≤{MAX_FILE_LABEL}.
            {' '}<span className="text-[var(--gold-accent)]">Drag &amp; drop files anywhere on this card.</span>
          </div>
        </div>
        {/* Wave 3.74 — accept picks up PDF in addition to images. The
            explicit `application/pdf` MIME saves us from typing hunts
            on OSes where a raw ".pdf" extension isn't enough. */}
        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf,.pdf"
          multiple
          hidden
          onChange={onFiles}
          data-testid="order-images-input"
        />
        <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading || isLocked} data-testid="order-images-upload-button" className={isSalesRole ? 'hidden' : ''}>
          <Upload size={14} className="mr-1.5" /> {uploading ? 'Uploading…' : 'Upload Files'}
        </Button>
      </div>
      {/* Wave 4.0 — per-file upload progress panel (browser -> object storage). */}
      {uploadItems.length > 0 && (
        <div
          className="mb-3 rounded-md border border-[var(--divider)] bg-[var(--paper-warm)]/40 p-3 space-y-2"
          data-testid="upload-progress-panel"
        >
          <div className="flex items-center justify-between">
            <div className="text-xs font-medium text-[var(--ink-navy)]" data-testid="upload-progress-heading">
              Uploading files — {uploadItems.filter((x) => x.status === 'done').length}/{uploadItems.length} done
              {uploadItems.some((x) => x.status === 'error') && (
                <span className="ml-1 text-rose-600">({uploadItems.filter((x) => x.status === 'error').length} failed)</span>
              )}
            </div>
            {uploadItems.every((x) => x.status === 'done' || x.status === 'error') && (
              <button
                type="button"
                onClick={() => setUploadItems([])}
                className="text-xs text-slate-500 hover:text-[var(--ink-navy)] transition-colors"
                data-testid="upload-progress-dismiss"
              >
                Dismiss
              </button>
            )}
          </div>
          {uploadItems.map((it, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`upload-item-${i}`}>
              {it.kind === 'pdf'
                ? <FileText size={14} className="shrink-0 text-slate-500" />
                : <ImageIcon size={14} className="shrink-0 text-slate-500" />}
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-xs text-[var(--ink-navy)]" title={it.name}>{it.name}</span>
                  <span className="shrink-0 text-[11px] tabular-nums text-slate-500" data-testid={`upload-item-${i}-status`}>
                    {it.status === 'done' ? 'Done'
                      : it.status === 'error' ? 'Failed'
                      : it.status === 'reading' ? 'Reading…'
                      : it.status === 'uploading' ? `${it.progress}%`
                      : 'Queued'}
                  </span>
                </div>
                <Progress
                  value={it.status === 'done' ? 100 : it.status === 'error' ? 100 : it.progress}
                  className={`h-1.5 mt-1 ${it.status === 'error' ? '[&>div]:bg-rose-500' : it.status === 'done' ? '[&>div]:bg-emerald-500' : '[&>div]:bg-[var(--gold-accent)]'}`}
                  data-testid={`upload-item-${i}-progress`}
                />
                {it.status === 'error' && (
                  <div className="mt-0.5 text-[11px] text-rose-600" data-testid={`upload-item-${i}-error`}>{it.error}</div>
                )}
                {it.slow && it.status === 'uploading' && (
                  <div className="mt-0.5 text-[11px] text-[var(--gold-accent)]">Slow connection — still uploading…</div>
                )}
              </div>
              <div className="shrink-0">
                {it.status === 'done' && <CheckCircle2 size={15} className="text-emerald-600" />}
                {it.status === 'error' && <XCircle size={15} className="text-rose-600" />}
                {(it.status === 'uploading' || it.status === 'reading') && <Loader2 size={15} className="animate-spin text-[var(--gold-accent)]" />}
                {it.status === 'pending' && <CircleIcon size={15} className="text-slate-300" />}
              </div>
            </div>
          ))}
        </div>
      )}
      {imgs.length === 0 ? (
        <div className={`text-sm py-8 text-center border border-dashed rounded-md ${isDragging ? 'border-[var(--gold-accent)] text-[var(--gold-accent)] bg-white' : 'border-[var(--divider)] text-slate-500'}`}>
          {isDragging ? 'Drop files here…' : 'No files yet — click Upload Files or drag & drop images / PDFs here'}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="order-images-grid">
          {imgs.map((src, i) => {
            const pdf = isPdfSrc(src);
            const pdfThumb = pdf ? (thumbs[i] || null) : null;
            const onPdfClick = async (ev) => {
              ev.preventDefault();
              const mod = await import('@/lib/pdfThumbnail');
              mod.openPdfDataUrlInNewTab(src);
            };
            return (
              <div
                key={i}
                className="relative group rounded-md border border-[var(--divider)] bg-white overflow-hidden flex flex-col"
                data-testid={`order-image-${i}`}
                data-attachment-kind={pdf ? 'pdf' : 'image'}
              >
                <div className="relative aspect-square overflow-hidden bg-white">
                {order.image_is_design?.[i] && (
                  <span
                    className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-white/90 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-tight text-indigo-700 shadow-sm backdrop-blur-sm"
                    data-testid={`order-image-from-design-${i}`}
                    title={`This tile is shared from Design Master${order.design_no ? ` (${order.design_no})` : ''}`}
                  >
                    <Gem size={9} className="text-indigo-500" />
                    from Design
                  </span>
                )}
                {pdf ? (
                  // Wave 3.75 — clickable overlay. We use a <button>
                  // (rather than an <a href="data:…">) because Chrome
                  // blocks top-level navigation to data:application/pdf
                  // URLs, so a plain link would just open about:blank.
                  // The click handler converts to a blob URL first.
                  <button
                    type="button"
                    onClick={onPdfClick}
                    className="relative flex flex-col items-center justify-center h-full w-full text-slate-500 hover:text-[var(--ink-navy)] transition-colors gap-1 p-0 bg-white overflow-hidden"
                    data-testid={`order-image-pdf-link-${i}`}
                    title="Open PDF in new tab"
                  >
                    {pdfThumb ? (
                      <>
                        {/* First-page rendering (rendered client-side
                            via pdfjs at upload time). */}
                        <img
                          src={pdfThumb}
                          alt={`PDF preview ${i + 1}`}
                          className="w-full h-full object-cover"
                          data-testid={`order-image-pdf-thumb-${i}`}
                        />
                        {/* Small PDF badge in the corner so the tile
                            is still identifiable as a PDF at a glance. */}
                        <span className="absolute bottom-1.5 left-1.5 inline-flex items-center gap-1 rounded-sm bg-rose-500/90 px-1.5 py-0.5 text-[9px] font-semibold text-white uppercase tracking-wide">
                          <FileText size={9} /> PDF
                        </span>
                      </>
                    ) : (
                      // Fallback: no thumbnail was produced (older
                      // upload, pdfjs failure, etc.) — render the
                      // classic icon-only tile.
                      <div className="flex flex-col items-center justify-center h-full w-full gap-1 p-3">
                        <FileText size={40} className="text-rose-500" />
                        <div className="text-[11px] font-medium text-[var(--ink-navy)] uppercase tracking-wide">PDF</div>
                        <div className="text-[10px] text-slate-400">Click to open</div>
                      </div>
                    )}
                  </button>
                ) : (
                  // Wave 3.78 — Clicking an image should open the full-size
                  // version in a new tab. Same safety pattern we use for
                  // PDFs: convert the data: URL to a blob: URL before
                  // ``window.open`` so Chrome doesn't block navigation
                  // (data: URLs at top-level are treated as untrusted).
                  // For already-remote URLs (http/https) the conversion
                  // is a no-op passthrough.
                  <button
                    type="button"
                    onClick={async (ev) => {
                      ev.preventDefault();
                      try {
                        const mod = await import('@/lib/pdfThumbnail');
                        // openPdfDataUrlInNewTab works for any data:*
                        // URL — the helper only checks the media type
                        // via the URL prefix, so passing image/png or
                        // image/jpeg data URLs converts them to
                        // blob:image/png etc. before window.open.
                        if (typeof mod.openDataUrlInNewTab === 'function') {
                          mod.openDataUrlInNewTab(src);
                        } else {
                          // Fallback for older helper builds.
                          window.open(src, '_blank', 'noopener,noreferrer');
                        }
                      } catch {
                        window.open(src, '_blank', 'noopener,noreferrer');
                      }
                    }}
                    className="block w-full h-full p-0 bg-white cursor-zoom-in"
                    data-testid={`order-image-link-${i}`}
                    title="Open image in new tab"
                  >
                    <img src={src} alt={`Order ${order.order_no} ${i+1}`} className="w-full h-full object-cover" />
                  </button>
                )}
                {!isSalesRole && (
                <button
                  onClick={() => setDelIdx(i)}
                  className="absolute top-2 right-2 bg-white/90 hover:bg-white text-rose-600 p-1 rounded-full shadow opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`order-image-remove-${i}`}
                  title={pdf ? 'Remove PDF' : 'Remove image'}
                >
                  <Trash2 size={12} />
                </button>
                )}
                </div>
                {/* Wave 3.84 — Title strip under the thumbnail */}
                <div className="px-2 py-1.5 border-t border-[var(--divider)] bg-slate-50/60 min-h-[36px] flex flex-col justify-center gap-0.5">
                  <div className="flex items-center gap-1">
                  {titleEditIdx === i ? (
                    <>
                      <Input
                        autoFocus
                        value={titleDraft}
                        onChange={(e) => setTitleDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); saveTitle(i); }
                          if (e.key === 'Escape') { e.preventDefault(); cancelTitleEdit(); }
                        }}
                        placeholder="Add a title…"
                        className="h-6 text-[11px] px-1.5"
                        data-testid={`order-image-title-input-${i}`}
                      />
                      <button
                        onClick={() => saveTitle(i)}
                        disabled={savingTitleIdx === i}
                        className="p-1 text-emerald-600 hover:text-emerald-700 disabled:opacity-50"
                        data-testid={`order-image-title-save-${i}`}
                        title="Save"
                      >
                        <CheckCircle2 size={12} />
                      </button>
                      <button
                        onClick={cancelTitleEdit}
                        disabled={savingTitleIdx === i}
                        className="p-1 text-slate-500 hover:text-slate-700 disabled:opacity-50"
                        data-testid={`order-image-title-cancel-${i}`}
                        title="Cancel"
                      >
                        <XIcon size={12} />
                      </button>
                    </>
                  ) : (
                    <>
                      <span
                        className={`flex-1 text-[11px] truncate ${titles[i] ? 'text-[var(--ink-navy)]' : 'text-slate-400 italic'}`}
                        title={titles[i] || 'Untitled'}
                        data-testid={`order-image-title-${i}`}
                      >
                        {titles[i] || 'Untitled'}
                      </span>
                      {!isLocked && (
                        <button
                          onClick={() => beginTitleEdit(i, titles[i])}
                          className="p-1 text-slate-400 hover:text-[var(--ink-navy)]"
                          data-testid={`order-image-title-edit-${i}`}
                          title="Edit title"
                        >
                          <Pencil size={11} />
                        </button>
                      )}
                    </>
                  )}
                  </div>
                  {(() => { const sz = formatBytes(dataUrlBytes(src)); return sz ? (
                    <span className="text-[10px] text-slate-400 tabular-nums leading-none" data-testid={`order-image-size-${i}`}>{sz}</span>
                  ) : null; })()}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {delIdx !== null && (
        <DeleteConfirmDialog
          open={delIdx !== null}
          onOpenChange={(v) => { if (!v) setDelIdx(null); }}
          entity_type="order_image"
          entity_id={`${order.id}:${delIdx}`}
          entity_label={`${order.order_no} attachment #${delIdx + 1}`}
          executor_kwargs={{ order_id: order.id, idx: delIdx }}
          company_id={order.company_id}
          onSuccess={() => { setDelIdx(null); reload(); }}
        />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Wave 3.63 — Tabular Requirements
// ---------------------------------------------------------------------------
// The old card-per-row layout was replaced with two dedicated tables:
//   1) Stones/Pearls table  (Material · Shape · Size · Sieves · Pieces · Issued
//      · Balance · [Req/Iss/Bal Carats] · Remarks · Status(+Age) · Comments)
//   2) Gold table           (Purity · Grams Required · Metal Issued · CAD · CAM
//      · Ghat · Status · Comments)
// Every row starts with a “>” caret that toggles a per-row fulfillment log
// with `timestamp · qty · by_email` entries so the operator can trace who
// entered each fulfillment.  Gold cells (CAD/CAM/Ghat) expose an inline edit
// button that opens a small dialog posting to /gold-measurement.
//
// Two header actions accompany the existing “Add Requirement” button:
//   • Download Template (GET /orders/{id}/requirements/excel-template)
//   • Upload Requirements (POST /orders/{id}/requirements/bulk-upload) → shows
//     a per-row result dialog and refreshes the order on success.
// ---------------------------------------------------------------------------

const MATERIAL_LABEL = {
  diamond: 'Diamond',
  color_stone: 'Colour Stone',
  pearl: 'Pearl',
  gold: 'Gold',
};
const MATERIAL_TONE = {
  diamond: 'bg-sky-50 text-sky-800 border-sky-200',
  color_stone: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  pearl: 'bg-purple-50 text-purple-800 border-purple-200',
  gold: 'bg-amber-50 text-amber-800 border-amber-200',
};

function requirementAgeBadge(r) {
  if ((r.status || '').toLowerCase() === 'complete') return null;
  const d = Number(r.days_since_requested || 0);
  const cls =
    d >= 3 ? 'bg-rose-50 text-rose-700 border-rose-200'
    : d >= 1 ? 'bg-amber-50 text-amber-700 border-amber-200'
    : 'bg-slate-50 text-slate-600 border-slate-200';
  const label = d <= 0 ? 'Age: Today' : d === 1 ? 'Age: 1 Day' : `Age: ${d} Days`;
  return (
    <Badge
      className={`text-[10px] uppercase tracking-wide border ${cls}`}
      title={r.requested_at_effective ? `Raised on ${formatDate(r.requested_at_effective)}` : 'Raised today'}
      data-testid={`requirement-aging-${r.id}`}
    >
      {label}
    </Badge>
  );
}

function requirementStatusBadge(r) {
  const s = (r.status || 'pending').toLowerCase();
  // Wave 3.89 — When a requirement was auto-completed via a Raw Material
  // tick, surface a small "Auto (Raw Material)" badge alongside the
  // Complete chip so operators know it wasn't naturally fulfilled.
  const autoRm = !!r.auto_completed_via_rm;
  if (s === 'complete' || s === 'completed' || s === 'done') {
    return (
      <span className="inline-flex items-center gap-1">
        <Badge className="text-[10px] uppercase tracking-wide bg-emerald-50 text-emerald-700 border border-emerald-200">
          Complete
        </Badge>
        {autoRm && (
          <Badge
            className="text-[10px] uppercase tracking-wide bg-indigo-50 text-indigo-700 border border-indigo-200"
            title="Auto-completed via Raw Material tick on the Production Checklist"
            data-testid={`req-auto-rm-badge-${r.id}`}
          >
            Auto (Raw Material)
          </Badge>
        )}
      </span>
    );
  }
  if (s === 'partial') {
    return <Badge className="text-[10px] uppercase tracking-wide bg-amber-50 text-amber-700 border border-amber-200">Partial</Badge>;
  }
  return <Badge className="text-[10px] uppercase tracking-wide bg-slate-50 text-slate-600 border border-slate-200">Pending</Badge>;
}

function RequirementsSection({ order, companyId, reload }) {
  const { hasMaterialAccess, materialScope, isSalesRole } = useAuth();
  // Wave 3.99l — Compute editable material scope for this section.
  const editableMaterials = React.useMemo(() => materialScope('edit_requirement'), [materialScope]);
  const canEditAnyRequirement = editableMaterials.length > 0;
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [classifications, setClassifications] = useState([]);
  // Phase 16 — Color Stones & Pearls classification masters (separate per material)
  const [csClassifications, setCsClassifications] = useState([]);
  const [pearlClassifications, setPearlClassifications] = useState([]);
  const [purities, setPurities] = useState([]);
  const [delTarget, setDelTarget] = useState(null);
  // Phase 12 — Pending Requirements PDF dialog
  const [pendingPdfOpen, setPendingPdfOpen] = useState(false);
  // Wave 3.30 — Inline "+ New Classification" dialog launched from the
  // Add Requirement dialog. ``classDlgMaterial`` carries the currently
  // selected material so the nested dialog hits the right endpoint and
  // we can re-fetch the corresponding list on success.
  const [classDlgOpen, setClassDlgOpen] = useState(false);
  const [classDlgMaterial, setClassDlgMaterial] = useState('diamond');
  // Wave 3.63 — table-scoped UI state.
  const [showCarats, setShowCarats] = useState(true);
  const [expanded, setExpanded] = useState({}); // {reqId: bool}
  const [goldEdit, setGoldEdit] = useState({ open: false, req: null });
  const uploadInputRef = useRef(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadResult, setUploadResult] = useState(null); // { summary, rows }
  const [uploadOpen, setUploadOpen] = useState(false);
  // Wave 3.64 — Copy Requirements from another order dialog state.
  const [copyOpen, setCopyOpen] = useState(false);
  // Wave 3.84 — Karigar Form dialog state.
  const [karigarOpen, setKarigarOpen] = useState(false);
  // Wave 3.64 — Per-table fulfillment filter (all | pending | partial | complete).
  const [stonesFilter, setStonesFilter] = useState('all');
  const [goldFilter, setGoldFilter] = useState('all');
  // Wave 3.77 — Per-material Shape / Size / Sieves master pools that back
  // the Add/Edit Requirement dialog comboboxes. Shape structure:
  //   { diamond: {shapes, sizes, sieves}, color_stone: {...}, pearl: {...} }
  const [attributeMasters, setAttributeMasters] = useState({
    diamond: { shapes: [], sizes: [], sieves: [] },
    color_stone: { shapes: [], sizes: [], sieves: [] },
    pearl: { shapes: [], sizes: [], sieves: [] },
  });
  const [form, setForm] = useState({
    material_type: 'diamond',
    classification_id: '', classification_label: '',
    description: '',
    required_carats: '', required_pieces: '',
    required_grams: '', required_purity: '',
    shape: '', size: '', sieves: '',
    note: '',
  });

  // Wave 4.x — A gold requirement's purity is ALWAYS the order form's own
  // jewellery purity; the operator no longer picks it. We pre-fill and lock
  // the Purity field to the order's purity (stored as a percent number).
  const orderGoldPurityPercent = (
    order?.jewellery_purity_percent !== undefined
    && order?.jewellery_purity_percent !== null
    && order?.jewellery_purity_percent !== ''
  ) ? String(order.jewellery_purity_percent) : '';
  const orderGoldPurityName = order?.jewellery_purity_name || '';
  const hasOrderGoldPurity = !!orderGoldPurityPercent;

  useEffect(() => {
    if (!companyId) return;
    api.get('/diamonds/classifications', { params: { company_id: companyId } }).then(r => setClassifications(r.data)).catch(() => { /* ignore */ });
    api.get('/color-stones/classifications', { params: { company_id: companyId } }).then(r => setCsClassifications(r.data)).catch(() => setCsClassifications([]));
    api.get('/pearls/classifications', { params: { company_id: companyId } }).then(r => setPearlClassifications(r.data)).catch(() => setPearlClassifications([]));
    api.get('/gold/purities', { params: { company_id: companyId } }).then(r => setPurities(r.data)).catch(() => { /* ignore */ });
    // Wave 3.77 — Fetch per-material Shape/Size/Sieves masters. The
    // endpoint auto-seeds default option lists on first call so
    // operators never see an empty combobox.
    api.get('/masters/requirement-attributes', { params: { company_id: companyId } })
      .then(r => {
        if (r.data && typeof r.data === 'object') setAttributeMasters(r.data);
      })
      .catch(() => { /* ignore — combobox degrades to free-text */ });
  }, [companyId]);

  // Wave 3.77 — Refresh a single material's attribute pool after we
  // auto-add newly-typed values on submit so the dropdown reflects
  // them without a full page reload.
  const refreshAttributeMasters = React.useCallback(async (materialType) => {
    if (!companyId || !materialType) return;
    try {
      const r = await api.get('/masters/requirement-attributes', {
        params: { company_id: companyId, material_type: materialType },
      });
      if (r.data && typeof r.data === 'object') {
        setAttributeMasters(prev => ({ ...prev, [materialType]: r.data }));
      }
    } catch { /* silent */ }
  }, [companyId]);

  const openAdd = (mat = 'diamond') => {
    setEditing(null);
    setForm({ material_type: mat, classification_id: '', classification_label: '', description: '', required_carats: '', required_pieces: '', required_grams: '', required_purity: mat === 'gold' ? orderGoldPurityPercent : '', shape: '', size: '', sieves: '', note: '' });
    setAddOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      material_type: r.material_type, classification_id: r.classification_id || '',
      classification_label: r.classification_label || '',
      description: r.description || '',
      required_carats: r.required_carats || '', required_pieces: r.required_pieces || '',
      required_grams: r.required_grams || '', required_purity: r.required_purity || '',
      shape: r.shape || '', size: r.size || '', sieves: r.sieves || '',
      note: r.note || '',
    });
    setAddOpen(true);
  };

  const submit = async () => {
    try {
      // Wave 4.x — Gold requirement purity is inherited from the order form.
      // If the order has no jewellery purity yet, adding a gold requirement
      // is blocked until one is set (so grams-at-purity maths stays valid).
      if (form.material_type === 'gold' && !editing && !form.required_purity) {
        toast.error("Set this order's Jewellery Purity first — the gold requirement purity is taken from it");
        return;
      }
      let label = form.classification_label;
      const pickClassPool = () => (
        form.material_type === 'diamond' ? classifications
        : form.material_type === 'color_stone' ? csClassifications
        : form.material_type === 'pearl' ? pearlClassifications
        : []
      );
      if ((form.material_type === 'diamond' || form.material_type === 'color_stone' || form.material_type === 'pearl') && form.classification_id) {
        const c = pickClassPool().find(x => x.id === form.classification_id);
        if (c) label = `${c.schema_code} — ${c.shape_name}`;
      } else if (form.material_type === 'gold' && form.required_purity) {
        const p = purities.find(x => Number(x.percent) === Number(form.required_purity));
        label = p ? `${p.name} (${p.percent}%)` : `${form.required_purity}%`;
      }
      const payload = {
        material_type: form.material_type,
        classification_id: form.classification_id || null,
        classification_label: label,
        description: form.description,
        required_carats: Number(form.required_carats) || 0,
        required_pieces: Number(form.required_pieces) || 0,
        required_grams: Number(form.required_grams) || 0,
        required_purity: form.required_purity ? Number(form.required_purity) : null,
        shape: form.shape || '',
        size: form.size || '',
        sieves: form.sieves || '',
        note: form.note,
      };
      if (editing) {
        await api.put(`/orders/${order.id}/requirements/${editing.id}`, payload);
        toast.success('Requirement updated');
      } else {
        await api.post(`/orders/${order.id}/requirements`, payload);
        toast.success('Requirement added');
      }

      // Wave 3.77 — Silently push any newly-typed Shape / Size / Sieves
      // values into the per-company master so subsequent template
      // downloads + other users' comboboxes include them. Only for
      // stone material types (Gold has no shape/size/sieves).
      if (form.material_type === 'diamond' || form.material_type === 'color_stone' || form.material_type === 'pearl') {
        const pool = attributeMasters[form.material_type] || { shapes: [], sizes: [], sieves: [] };
        const contains = (list, value) => {
          if (!value) return true;
          const lower = String(value).trim().toLowerCase();
          return (list || []).some(v => String(v).trim().toLowerCase() === lower);
        };
        const newItems = [];
        if (form.shape && !contains(pool.shapes, form.shape)) {
          newItems.push({ material_type: form.material_type, attribute: 'shape', value: form.shape });
        }
        if (form.size && !contains(pool.sizes, form.size)) {
          newItems.push({ material_type: form.material_type, attribute: 'size', value: form.size });
        }
        if (form.sieves && !contains(pool.sieves, form.sieves)) {
          newItems.push({ material_type: form.material_type, attribute: 'sieves', value: form.sieves });
        }
        if (newItems.length > 0) {
          try {
            await api.post('/masters/requirement-attributes', { company_id: companyId, items: newItems });
            refreshAttributeMasters(form.material_type);
          } catch { /* silent — save already succeeded */ }
        }
      }

      setAddOpen(false); reload();
    } catch (e) { toast.error(extractError(e, 'Failed')); }
  };

  const del = (r) => setDelTarget(r);

  const toggleComplete = async (r) => {
    try {
      await api.post(`/orders/${order.id}/requirements/${r.id}/complete`, { completed: r.status !== 'complete' });
      toast.success(r.status !== 'complete' ? 'Marked complete' : 'Reopened'); reload();
    } catch (e) { toast.error(extractError(e, 'Failed')); }
  };

  // Wave 3.25 hot-fix — Display requirements in a stable, material-first
  // hierarchy so users always see Gold first, then Diamonds, then Colour
  // Stones, then Pearls. Inside each material we preserve insertion order
  // (created_at ascending) so the operator's mental model of "first added,
  // first shown within a group" still holds.
  // Wave 3.99l — Additionally filter by the user's ``materials.<mat>.see``
  // scope so requirements of hidden materials are stripped entirely.
  const MATERIAL_ORDER = { gold: 0, diamond: 1, color_stone: 2, pearl: 3 };
  const seeScope = React.useMemo(() => materialScope('see'), [materialScope]);
  const canSeeGold = seeScope.includes('gold');
  const canSeeDiamond = seeScope.includes('diamonds');
  const canSeeColorStone = seeScope.includes('color_stones');
  const canSeePearl = seeScope.includes('pearls');
  const canSeeAnyMaterial = canSeeGold || canSeeDiamond || canSeeColorStone || canSeePearl;
  const isMaterialVisible = React.useCallback((materialType) => {
    switch (materialType) {
      case 'gold': return canSeeGold;
      case 'diamond': return canSeeDiamond;
      case 'color_stone': return canSeeColorStone;
      case 'pearl': return canSeePearl;
      default: return true;
    }
  }, [canSeeGold, canSeeDiamond, canSeeColorStone, canSeePearl]);
  const reqs = React.useMemo(() => {
    const raw = order.requirements || [];
    const scoped = raw.filter((r) => isMaterialVisible(r.material_type));
    return scoped.sort((a, b) => {
      const am = MATERIAL_ORDER[a.material_type] ?? 99;
      const bm = MATERIAL_ORDER[b.material_type] ?? 99;
      if (am !== bm) return am - bm;
      // tie-break: created_at asc, then id for stability
      const at = (a.created_at || '').localeCompare(b.created_at || '');
      if (at !== 0) return at;
      return (a.id || '').localeCompare(b.id || '');
    });
  }, [order.requirements, isMaterialVisible]);

  // Wave 3.99l — helper for row-level action guards.
  const canEditRow = React.useCallback((r) => {
    if (!r?.material_type) return canEditAnyRequirement;
    return hasMaterialAccess(r.material_type, 'edit_requirement');
  }, [hasMaterialAccess, canEditAnyRequirement]);

  // Wave 3.63 — Split reqs into stones/pearls table and gold table.
  const stoneReqs = React.useMemo(() => {
    // Wave 3.80 — Enforce canonical Diamond sub-category order across
    // the stones/pearls table:
    //   Diamonds (Polki → Diamonds → Rose Cuts → Brown Diamonds → Chakri
    //     → Other, secondary by sieves/size)
    //   → Color Stones (original order)
    //   → Pearls (original order)
    const filtered = reqs.filter((r) => r.material_type !== 'gold');
    const diamonds = sortDiamondItems(filtered.filter((r) => r.material_type === 'diamond'));
    const colorStones = filtered.filter((r) => r.material_type === 'color_stone');
    const pearlsList = filtered.filter((r) => r.material_type === 'pearl');
    const otherStones = filtered.filter((r) => !['diamond', 'color_stone', 'pearl'].includes(r.material_type));
    return [...diamonds, ...colorStones, ...pearlsList, ...otherStones];
  }, [reqs]);
  const goldReqs = React.useMemo(() => reqs.filter((r) => r.material_type === 'gold'), [reqs]);

  const toggleExpand = (rid) => setExpanded((prev) => ({ ...prev, [rid]: !prev[rid] }));

  // Wave 3.63 — Download the pre-formatted xlsx template.  Order-scoped so
  // the filename embeds the order number.
  const downloadTemplate = async () => {
    try {
      const res = await api.get(`/orders/${order.id}/requirements/excel-template`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `requirements-template-${(order.order_no || order.id).replace(/[\\/]/g, '-')}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success('Template downloaded');
    } catch (e) { toast.error(extractError(e, 'Failed to download template')); }
  };

  // Wave 4.x — Download this order's Requirement List as the SAME xlsx
  // upload template, PRE-FILLED with the current requirements. On a repeat
  // order the operator can re-upload this exact file (via "Upload
  // Requirements") to recreate the list without re-keying every row.
  const downloadRequirementsExcel = async () => {
    try {
      const res = await api.get(`/orders/${order.id}/requirements/excel-export`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `requirements-${(order.order_no || order.id).replace(/[\\/]/g, '-')}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success('Requirements exported to Excel');
    } catch (e) { toast.error(extractError(e, 'Failed to export requirements')); }
  };

  const onUploadPick = () => uploadInputRef.current?.click();
  const onUploadFile = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (!file) return;
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/orders/${order.id}/requirements/bulk-upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadResult(res.data);
      setUploadOpen(true);
      const created = res.data?.summary?.created || 0;
      const errored = res.data?.summary?.errored || 0;
      if (created > 0 && errored === 0) toast.success(`Added ${created} requirement(s)`);
      else if (created > 0 && errored > 0) toast.warning(`Added ${created}, ${errored} row(s) failed — see report`);
      else if (created === 0 && errored > 0) toast.error(`Upload failed: ${errored} row error(s)`);
      else toast.info('No requirements found in the file');
      if (created > 0) reload();
    } catch (e) {
      toast.error(extractError(e, 'Bulk upload failed'));
    } finally { setUploadBusy(false); }
  };

  // Phase 12 \u2014 compute count of still-pending requirements (not 'complete')
  const pendingCount = reqs.filter(
    (r) => (r.status || 'pending').toLowerCase() !== 'complete'
      && (r.status || '').toLowerCase() !== 'completed'
      && (r.status || '').toLowerCase() !== 'done'
  ).length;

  const disabledCompletedTitle = order.stage === 'Completed' ? 'Order is Completed — cannot add requirements' : undefined;

  // Wave 3.99l — Default material for "Add Requirement" must be one the
  // user actually has ``edit_requirement`` for; otherwise the dialog
  // would open on a forbidden material.
  const editableMaterialLabels = React.useMemo(() => {
    // ``materialScope`` returns canonical DB keys (``diamonds`` etc.),
    // but the requirement dialog uses singular UI keys (``diamond``).
    return editableMaterials.map((m) => (m === 'diamonds' ? 'diamond' : m === 'color_stones' ? 'color_stone' : m === 'pearls' ? 'pearl' : m));
  }, [editableMaterials]);
  const defaultAddMaterial = editableMaterialLabels[0] || 'diamond';

  // Wave 3.99l — If the user has no ``see`` access to any material,
  // render an explicit access notice and skip the whole Requirements
  // affordance. This keeps the sticky nav honest without leaving an
  // empty card behind.
  if (!canSeeAnyMaterial) {
    return (
      <Card className="p-5" data-testid="order-requirements-no-access">
        <div className="font-medium text-[var(--ink-navy)] mb-2">Requirement List</div>
        <div className="text-sm text-slate-500 py-6 text-center border border-dashed rounded-md">
          You don&apos;t have access to view requirements for any material on this order.
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="font-medium text-[var(--ink-navy)]">Requirement List</div>
          <div className="text-xs text-slate-500">What materials this order needs. Each requirement tracks fulfillment from issues.</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => setShowCarats((s) => !s)}
            data-testid="req-toggle-carats-button"
            title={showCarats ? 'Hide carats columns' : 'Show carats columns'}
          >
            {showCarats ? <EyeOff size={14} className="mr-1.5" /> : <Eye size={14} className="mr-1.5" />}
            {showCarats ? 'Hide Carats' : 'Show Carats'}
          </Button>
          {!isSalesRole && (
          <Button
            variant="outline"
            onClick={() => setKarigarOpen(true)}
            data-testid="order-karigar-form-button"
            title="Generate a print-ready Job Card (images + requirements + milestones)"
          >
            <FileText size={14} className="mr-1.5" /> Job Card
          </Button>
          )}
          {!isSalesRole && (
          <Button
            variant="outline"
            onClick={() => setPendingPdfOpen(true)}
            disabled={pendingCount === 0}
            data-testid="order-pending-requirements-pdf-button"
            title={pendingCount === 0 ? 'No pending requirements to export' : 'Generate PDF of pending requirements'}
          >
            <FileText size={14} className="mr-1.5" /> Pending Requirements PDF
            {pendingCount > 0 && (
              <Badge className="ml-2 bg-rose-50 text-rose-700 text-[10px]">{pendingCount}</Badge>
            )}
          </Button>
          )}
          {!isSalesRole && (
          <Button
            variant="outline"
            onClick={downloadRequirementsExcel}
            disabled={reqs.length === 0}
            data-testid="req-download-excel-button"
            title={reqs.length === 0 ? 'No requirements to export' : 'Download the full requirement list as an Excel file (re-uploadable for repeat orders)'}
          >
            <FileSpreadsheet size={14} className="mr-1.5" /> Download Excel
          </Button>
          )}
          {!isSalesRole && (
          <Button
            variant="outline"
            onClick={downloadTemplate}
            data-testid="req-download-template-button"
            title="Download the Excel template for bulk requirement upload"
          >
            <FileText size={14} className="mr-1.5" /> Download Template
          </Button>
          )}
          {canEditAnyRequirement && (
            <>
              <Button
                variant="outline"
                onClick={onUploadPick}
                disabled={uploadBusy || order.stage === 'Completed'}
                data-testid="req-upload-button"
                title={order.stage === 'Completed' ? disabledCompletedTitle : 'Upload filled requirements template'}
              >
                <Upload size={14} className="mr-1.5" /> {uploadBusy ? 'Uploading…' : 'Upload Requirements'}
              </Button>
              <input
                ref={uploadInputRef}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                hidden
                onChange={onUploadFile}
                data-testid="req-upload-file-input"
              />
              <Button
                variant="outline"
                onClick={() => setCopyOpen(true)}
                disabled={order.stage === 'Completed'}
                data-testid="req-copy-from-order-button"
                title={order.stage === 'Completed' ? disabledCompletedTitle : 'Copy all requirements from another order'}
              >
                <Copy size={14} className="mr-1.5" /> Copy from Order
              </Button>
              <Button
                className="luxury-button"
                onClick={() => openAdd(defaultAddMaterial)}
                data-testid="order-add-requirement-button"
                disabled={order.stage === 'Completed'}
                title={disabledCompletedTitle}
              >
                <Plus size={14} className="mr-1.5" /> Add Requirement
              </Button>
            </>
          )}
        </div>
      </div>

      {reqs.length === 0 ? (
        <div className="text-sm text-slate-500 py-6 text-center border border-dashed rounded-md" data-testid="order-requirements-empty">
          {canEditAnyRequirement
            ? 'No requirements yet. Use “Add Requirement”, “Copy from Order”, or “Upload Requirements” (with the downloaded template) to begin.'
            : 'No requirements to display for the materials you have access to.'}
        </div>
      ) : (
        <div className="space-y-6" data-testid="order-requirements-list">
          {stoneReqs.length > 0 && (
            <StonesRequirementsTable
              reqs={stoneReqs}
              showCarats={showCarats}
              expanded={expanded}
              onToggleExpand={toggleExpand}
              onEdit={openEdit}
              onDelete={del}
              onToggleComplete={toggleComplete}
              orderCompleted={order.stage === 'Completed'}
              filter={stonesFilter}
              onFilterChange={setStonesFilter}
              canEditRow={canEditRow}
            />
          )}
          {goldReqs.length > 0 && (
            <GoldRequirementsTable
              order={order}
              reqs={goldReqs}
              expanded={expanded}
              onToggleExpand={toggleExpand}
              onEdit={openEdit}
              onDelete={del}
              onToggleComplete={toggleComplete}
              onEditMeasurement={(r) => setGoldEdit({ open: true, req: r })}
              orderCompleted={order.stage === 'Completed'}
              filter={goldFilter}
              onFilterChange={setGoldFilter}
              reload={reload}
              canEditRow={canEditRow}
            />
          )}
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Requirement' : 'Add Requirement'}</DialogTitle>
          <DialogDescription>Specify a material need for this order. Fulfillment is auto-tracked when you issue against this requirement.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Material Type</Label>
              <Select value={form.material_type} onValueChange={(v) => setForm({ ...form, material_type: v, classification_id: '', required_purity: v === 'gold' ? orderGoldPurityPercent : '' })}>
                <SelectTrigger data-testid="req-material-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {hasMaterialAccess('gold', 'edit_requirement') && <SelectItem value="gold">Gold</SelectItem>}
                  {hasMaterialAccess('diamond', 'edit_requirement') && <SelectItem value="diamond">Diamond</SelectItem>}
                  {hasMaterialAccess('color_stone', 'edit_requirement') && <SelectItem value="color_stone">Colour Stone</SelectItem>}
                  {hasMaterialAccess('pearl', 'edit_requirement') && <SelectItem value="pearl">Pearl</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            {form.material_type === 'diamond' && (
              <div>
                <Label>Classification</Label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <SearchableSelect
                      items={[{ id: 'none', schema_code: '', shape_name: 'None (any)' }, ...classifications]}
                      value={form.classification_id || 'none'}
                      onValueChange={(v) => setForm({ ...form, classification_id: v === 'none' ? '' : (v || '') })}
                      itemToLabel={(c) => (c.id === 'none' ? 'None (any)' : `${c.schema_code} — ${c.shape_name}`)}
                      searchKeys={['schema_code', 'shape_name']}
                      placeholder="None (any)"
                      searchPlaceholder="Search shape / code…"
                      testId="req-classification-select"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="shrink-0 h-10 px-3"
                    onClick={() => { setClassDlgMaterial('diamond'); setClassDlgOpen(true); }}
                    data-testid="req-classification-new-button"
                    title="Create a new Diamond classification"
                  >
                    <Plus size={14} className="mr-1" /> New
                  </Button>
                </div>
              </div>
            )}
            {form.material_type === 'color_stone' && (
              <div>
                <Label>Colour Stone Classification</Label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <SearchableSelect
                      items={[{ id: 'none', schema_code: '', shape_name: 'None (any)' }, ...csClassifications]}
                      value={form.classification_id || 'none'}
                      onValueChange={(v) => setForm({ ...form, classification_id: v === 'none' ? '' : (v || '') })}
                      itemToLabel={(c) => (c.id === 'none' ? 'None (any)' : `${c.schema_code} — ${c.shape_name}`)}
                      searchKeys={['schema_code', 'shape_name']}
                      placeholder="None (any)"
                      searchPlaceholder="Search shape / code…"
                      testId="req-cs-classification-select"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="shrink-0 h-10 px-3"
                    onClick={() => { setClassDlgMaterial('color_stone'); setClassDlgOpen(true); }}
                    data-testid="req-cs-classification-new-button"
                    title="Create a new Colour Stone classification"
                  >
                    <Plus size={14} className="mr-1" /> New
                  </Button>
                </div>
              </div>
            )}
            {form.material_type === 'pearl' && (
              <div>
                <Label>Pearl Classification</Label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <SearchableSelect
                      items={[{ id: 'none', schema_code: '', shape_name: 'None (any)' }, ...pearlClassifications]}
                      value={form.classification_id || 'none'}
                      onValueChange={(v) => setForm({ ...form, classification_id: v === 'none' ? '' : (v || '') })}
                      itemToLabel={(c) => (c.id === 'none' ? 'None (any)' : `${c.schema_code} — ${c.shape_name}`)}
                      searchKeys={['schema_code', 'shape_name']}
                      placeholder="None (any)"
                      searchPlaceholder="Search shape / code…"
                      testId="req-pearl-classification-select"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="shrink-0 h-10 px-3"
                    onClick={() => { setClassDlgMaterial('pearl'); setClassDlgOpen(true); }}
                    data-testid="req-pearl-classification-new-button"
                    title="Create a new Pearl classification"
                  >
                    <Plus size={14} className="mr-1" /> New
                  </Button>
                </div>
              </div>
            )}
            {form.material_type === 'gold' && (
              <div>
                <Label>Purity</Label>
                {(hasOrderGoldPurity || (editing && form.required_purity)) ? (
                  <>
                    <div
                      className="flex h-10 items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700"
                      data-testid="req-purity-locked"
                      title="Gold purity is fixed to this order form's jewellery purity and cannot be changed"
                    >
                      <span className="font-medium">
                        {(() => {
                          const pct = form.required_purity || orderGoldPurityPercent;
                          const p = purities.find((x) => Number(x.percent) === Number(pct));
                          if (p) return `${p.name} (${p.percent}%)`;
                          if (String(pct) === '100') return '999 (100% pure)';
                          if (orderGoldPurityName) return `${orderGoldPurityName}${pct ? ` (${pct}%)` : ''}`;
                          return pct ? `${pct}%` : '—';
                        })()}
                      </span>
                      <Lock size={12} className="ml-auto text-slate-400" />
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1">Auto-set to this order form&apos;s jewellery purity.</p>
                  </>
                ) : (
                  <div
                    className="flex min-h-10 items-center rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700"
                    data-testid="req-purity-missing"
                  >
                    Set this order&apos;s Jewellery Purity first (top of the page) — the gold requirement purity auto-fills from it.
                  </div>
                )}
              </div>
            )}
            {(form.material_type === 'diamond' || form.material_type === 'color_stone' || form.material_type === 'pearl') && (
              <>
                {/* Wave 3.77 — Shape combobox (pick from master or type new) */}
                <div>
                  <Label>Shape</Label>
                  <Input
                    list={`req-shapes-${form.material_type}`}
                    value={form.shape}
                    onChange={(e) => setForm({ ...form, shape: e.target.value })}
                    placeholder="Pick or type (e.g. Round Brilliant)"
                    data-testid="req-shape-input"
                  />
                  <datalist id={`req-shapes-${form.material_type}`}>
                    {(attributeMasters[form.material_type]?.shapes || []).map((s) => (
                      <option key={s} value={s} />
                    ))}
                  </datalist>
                  <p className="text-[11px] text-slate-500 mt-1">Values not in the list are auto-added to the master on save.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Size</Label>
                    <Input
                      list={`req-sizes-${form.material_type}`}
                      value={form.size}
                      onChange={(e) => setForm({ ...form, size: e.target.value })}
                      placeholder="Pick or type (e.g. 2.0 mm)"
                      data-testid="req-size-input"
                    />
                    <datalist id={`req-sizes-${form.material_type}`}>
                      {(attributeMasters[form.material_type]?.sizes || []).map((s) => (
                        <option key={s} value={s} />
                      ))}
                    </datalist>
                  </div>
                  <div>
                    <Label>Sieves / Grade</Label>
                    <Input
                      list={`req-sieves-${form.material_type}`}
                      value={form.sieves}
                      onChange={(e) => setForm({ ...form, sieves: e.target.value })}
                      placeholder="Pick or type (e.g. AAA)"
                      data-testid="req-sieves-input"
                    />
                    <datalist id={`req-sieves-${form.material_type}`}>
                      {(attributeMasters[form.material_type]?.sieves || []).map((s) => (
                        <option key={s} value={s} />
                      ))}
                    </datalist>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Required carats</Label><Input type="number" step="0.01" value={form.required_carats} onChange={(e) => setForm({ ...form, required_carats: e.target.value })} data-testid="req-carats-input" /></div>
                  <div><Label>Required pieces</Label><Input type="number" step="1" value={form.required_pieces} onChange={(e) => setForm({ ...form, required_pieces: e.target.value })} data-testid="req-pieces-input" /></div>
                </div>
              </>
            )}
            {form.material_type === 'gold' && (
              <div><Label>Required grams (at purity)</Label><Input type="number" step="0.001" value={form.required_grams} onChange={(e) => setForm({ ...form, required_grams: e.target.value })} data-testid="req-grams-input" /></div>
            )}
            <div><Label>Description (optional)</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. for centre stone of pendant" data-testid="req-description-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={submit} className="luxury-button" data-testid="req-submit-button">{editing ? 'Save' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {delTarget && (
        <DeleteConfirmDialog
          open={!!delTarget}
          onOpenChange={(v) => { if (!v) setDelTarget(null); }}
          entity_type="order_requirement"
          entity_id={delTarget.id}
          entity_label={`${order.order_no} - ${delTarget.classification_label || delTarget.description || 'requirement'}`}
          executor_kwargs={{ order_id: order.id }}
          company_id={order.company_id}
          onSuccess={() => { setDelTarget(null); reload(); }}
        />
      )}

      {/* Phase 12 — Pending Requirements PDF download dialog */}
      <PdfDownloadDialog
        open={pendingPdfOpen}
        onOpenChange={setPendingPdfOpen}
        title={`Pending Requirements \u2014 ${order.order_no}`}
        description="Choose whether to include the company logo and name on this requirement list PDF."
        companyId={order.company_id}
        endpoint={`/orders/${order.id}/pending-requirements/pdf`}
        filename={`PendingRequirements_${(order.order_no || order.id).replace(/\//g, '-')}.pdf`}
        extraParams={{ status: 'pending' }}
        mode="download"
      />

      {/* Wave 3.84 — Karigar Form dialog (multi-select images + Balance-only toggle + branding). */}
      <KarigarFormDialog
        open={karigarOpen}
        onOpenChange={setKarigarOpen}
        order={order}
      />

      {/* Wave 3.30 — Inline "+ New Classification" dialog launched from
          the Add Requirement dialog. On success the relevant classification
          list is refetched and the newly created classification is
          auto-selected on the requirement form. */}
      <ClassificationFormDialog
        open={classDlgOpen}
        onOpenChange={setClassDlgOpen}
        companyId={companyId}
        material={classDlgMaterial}
        onCreated={async (data) => {
          try {
            if (classDlgMaterial === 'diamond') {
              const r = await api.get('/diamonds/classifications', { params: { company_id: companyId } });
              setClassifications(r.data);
            } else if (classDlgMaterial === 'color_stone') {
              const r = await api.get('/color-stones/classifications', { params: { company_id: companyId } });
              setCsClassifications(r.data);
            } else if (classDlgMaterial === 'pearl') {
              const r = await api.get('/pearls/classifications', { params: { company_id: companyId } });
              setPearlClassifications(r.data);
            }
            // Auto-select the freshly created classification so the user
            // doesn't have to hunt for it in the dropdown.
            if (data && data.id) {
              setForm((f) => ({ ...f, classification_id: data.id }));
            }
          } catch (e) { /* ignore refresh errors */ }
        }}
      />

      {/* Wave 3.63 — Gold measurement (CAD / CAM / Ghat) inline edit dialog */}
      <GoldMeasurementDialog
        open={goldEdit.open}
        req={goldEdit.req}
        onOpenChange={(v) => setGoldEdit((s) => ({ ...s, open: v }))}
        orderId={order.id}
        onSaved={() => { setGoldEdit({ open: false, req: null }); reload(); }}
      />

      {/* Wave 3.63 — Bulk upload per-row result dialog */}
      <BulkUploadResultDialog
        open={uploadOpen}
        result={uploadResult}
        onOpenChange={setUploadOpen}
      />

      {/* Wave 3.64 — Copy Requirements from another order */}
      <CopyRequirementsDialog
        open={copyOpen}
        onOpenChange={setCopyOpen}
        orderId={order.id}
        onCopied={() => { setCopyOpen(false); reload(); }}
      />
    </Card>
  );
}

function fmtNum(v, digits = 2) {
  const n = Number(v || 0);
  if (!n) return '—';
  return n.toFixed(digits);
}

function ExpandCaret({ open, count, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 text-slate-500 hover:text-[var(--ink-navy)] transition-colors"
      title={open ? 'Hide fulfillment log' : `Show fulfillment log (${count} entr${count === 1 ? 'y' : 'ies'})`}
      data-testid={testId}
    >
      {open
        ? <ChevronDown size={14} className="shrink-0" />
        : <ChevronRight size={14} className="shrink-0" />}
      <Badge
        className={`text-[10px] font-mono ${count > 0 ? 'bg-slate-100 text-slate-700 border-slate-200' : 'bg-slate-50 text-slate-400 border-slate-200'}`}
        title={count > 0 ? `${count} fulfillment entr${count === 1 ? 'y' : 'ies'}` : 'No fulfillments yet'}
      >
        {count}
      </Badge>
    </button>
  );
}

function FulfillmentLogSubRow({ r, colSpan, isGold }) {
  const list = r.fulfillments || [];
  return (
    <tr className="bg-slate-50/70">
      <td colSpan={colSpan} className="px-4 py-3">
        <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2 font-semibold">Fulfillment log ({list.length})</div>
        {list.length === 0 ? (
          <div className="text-[12px] text-slate-500 italic py-2 border border-dashed border-slate-200 rounded-md text-center">
            No fulfillments recorded yet. This requirement will auto-track when material is issued against it.
          </div>
        ) : (
          <div className="relative pl-4">
            <div className="absolute left-1.5 top-1 bottom-1 w-px bg-slate-200" aria-hidden="true" />
            <ul className="space-y-2">
              {list.map((f, i) => {
                const qtyLabel = isGold
                  ? `${formatGrams(f.grams || 0)}g${f.grams_999 ? ` (999: ${formatGrams(f.grams_999)}g)` : ''}${f.fulfilled_delta_grams ? ` → ${formatGrams(f.fulfilled_delta_grams)}g @ target` : ''}`
                  : `${formatCarats(f.carats || 0)} ct · ${f.pieces || 0} pcs`;
                return (
                  <li key={f.entry_id || i} className="relative">
                    <div className="absolute -left-3 top-1.5 h-2 w-2 rounded-full bg-[var(--gold-accent)] ring-2 ring-white" aria-hidden="true" />
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-[12px]">
                      <span className="font-mono text-slate-700">{formatDateTime(f.timestamp)}</span>
                      <span className="font-mono tabular-nums text-[var(--ink-navy)]">{qtyLabel}</span>
                      {f.marked_complete_here && <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[9px] uppercase">marked done here</Badge>}
                      <span className="text-slate-500">by <span className="text-slate-700">{f.by_email || 'unknown'}</span></span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {r.completed_at && (
          <div className="mt-2 text-[11px] text-emerald-700">
            Completed {formatDateTime(r.completed_at)} by {r.completed_by_email || 'unknown'}
          </div>
        )}
        <div className="mt-1 text-[10px] text-slate-400">
          Created {formatDate(r.created_at)} by {r.created_by_email || 'unknown'}
        </div>
      </td>
    </tr>
  );
}

// Wave 3.68 — Comments sub-row for a requirement row. Mounted directly
// below the requirement row (independent of the fulfillment log expand
// caret) when the comment callout badge for this requirement is open.
// Includes an "Add Comment" shortcut that pre-scopes the main composer
// to this requirement and scrolls/focuses it.
function RequirementCommentsSubRow({ r, colSpan }) {
  const { isOpen, applyComposerPreset, closeFilter } = useOrderComments();
  const filterKey = `requirement:${r.id}`;
  if (!isOpen(filterKey)) return null;
  const onAdd = () => {
    applyComposerPreset({ topic: 'requirement', subtopicId: r.id });
    // Keep the callout open so the user can watch their post appear.
  };
  return (
    <tr className="bg-amber-50/40" data-testid={`requirement-comments-subrow-${r.id}`}>
      <td colSpan={colSpan} className="px-4 py-3">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
            Requirement comments
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onAdd}
              className="inline-flex items-center gap-1 rounded-md border border-[var(--ink-navy)] bg-[var(--ink-navy)] text-white text-[11px] font-medium px-2 py-1 hover:opacity-90 transition-opacity"
              data-testid={`requirement-comments-add-${r.id}`}
              title="Post a new comment on this requirement"
            >
              <MessageSquare size={11} />
              Add Comment
            </button>
            <button
              type="button"
              onClick={() => closeFilter(filterKey)}
              className="text-slate-400 hover:text-slate-700"
              aria-label="Close requirement comments"
              data-testid={`requirement-comments-close-${r.id}`}
              title="Close"
            >
              <XIcon size={14} />
            </button>
          </div>
        </div>
        <InlineCommentListNoHeader
          filterKey={filterKey}
          spec={{ topic: 'requirement', subtopic_id: r.id }}
          emptyHint="No comments on this requirement yet. Click 'Add Comment' to post the first one."
          testIdPrefix={`requirement-comment-${r.id}`}
        />
      </td>
    </tr>
  );
}

// Wave 3.68 — variant of InlineCommentList that skips the built-in header
// (title + close X) since RequirementCommentsSubRow provides its own
// header with an Add Comment action.
function InlineCommentListNoHeader({ filterKey, spec, emptyHint, testIdPrefix }) {
  const { filter, isOpen } = useOrderComments();
  if (!isOpen(filterKey)) return null;
  const items = filter(spec);
  if (items.length === 0) {
    return (
      <div
        className="text-[11px] italic text-slate-500 py-3 text-center border border-dashed border-slate-200 rounded-md"
        data-testid={`${testIdPrefix}-empty`}
      >
        {emptyHint}
      </div>
    );
  }
  return (
    <div className="space-y-1.5" data-testid={`${testIdPrefix}-list`}>
      {items.map((c) => (
        <RequirementCommentTile key={c.id} c={c} testIdPrefix={testIdPrefix} />
      ))}
    </div>
  );
}

// Wave 3.68 — Compact comment tile (mirrors the shared CommentTileCompact
// but co-located so we don't couple this file to internal exports of
// CommentCalloutBadge.jsx). Renders in the sub-row.
function RequirementCommentTile({ c, testIdPrefix }) {
  const isCA = c.created_by_role === 'company_admin';
  const isSA = c.created_by_role === 'super_admin';
  const initials = (c.created_by_name || c.created_by_email || '?').slice(0, 2).toUpperCase();
  return (
    <div
      id={`comment-${c.id}`}
      data-testid={`${testIdPrefix}-tile-${c.id}`}
      className="flex gap-2 p-2 rounded border border-[var(--divider)] bg-white"
    >
      <div className={`h-7 w-7 rounded-full grid place-items-center text-[10px] font-medium shrink-0 ${isSA ? 'bg-violet-100 text-violet-700' : isCA ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}`}>
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap text-[10px]">
          <span className="font-medium text-[var(--ink-navy)]">{c.created_by_name || c.created_by_email}</span>
          {isSA && <Badge className="bg-violet-50 text-violet-700 text-[9px] py-0 px-1">SA</Badge>}
          {isCA && <Badge className="bg-blue-50 text-blue-700 text-[9px] py-0 px-1">CA</Badge>}
          <span className="font-mono text-[9px] bg-slate-100 text-slate-600 px-1 rounded">{c.comment_no}</span>
          <span className="text-slate-400 text-[9px]">{formatDateTime(c.created_at)}</span>
        </div>
        <div className="text-[12px] text-slate-700 mt-1 whitespace-pre-wrap break-words" data-testid={`${testIdPrefix}-tile-body-${c.id}`}>{c.body}</div>
      </div>
    </div>
  );
}

function FilterChips({ counts, value, onChange, testIdPrefix }) {
  const chips = [
    { key: 'all', label: 'All', count: counts.all },
    { key: 'pending', label: 'Pending', count: counts.pending },
    { key: 'partial', label: 'Partial', count: counts.partial },
    { key: 'complete', label: 'Complete', count: counts.complete },
  ];
  return (
    <div className="inline-flex items-center gap-1 rounded-md border border-[var(--divider)] bg-white p-0.5" role="tablist" aria-label="Filter by fulfillment status">
      {chips.map((c) => {
        const active = value === c.key;
        return (
          <button
            key={c.key}
            role="tab"
            aria-selected={active}
            type="button"
            onClick={() => onChange(c.key)}
            data-testid={`${testIdPrefix}-${c.key}`}
            className={`px-2.5 py-1 rounded text-[11px] font-medium uppercase tracking-wide transition-colors border ${
              active
                ? 'bg-[var(--ink-navy)] text-white border-[var(--ink-navy)]'
                : 'text-slate-600 border-transparent hover:bg-slate-50'
            }`}
            title={`Show ${c.label.toLowerCase()} requirements`}
          >
            {c.label}
            <span className={`ml-1.5 font-mono text-[10px] tabular-nums ${active ? 'text-white/80' : 'text-slate-400'}`}>
              {c.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function classifyReq(r) {
  const s = (r.status || 'pending').toLowerCase();
  if (s === 'complete') return 'complete';
  if (s === 'partial') return 'partial';
  return 'pending';
}

function computeFilterCounts(reqs) {
  const c = { all: reqs.length, pending: 0, partial: 0, complete: 0 };
  for (const r of reqs) {
    c[classifyReq(r)] += 1;
  }
  return c;
}

function applyFilter(reqs, filter) {
  if (!filter || filter === 'all') return reqs;
  return reqs.filter((r) => classifyReq(r) === filter);
}

function StonesRequirementsTable({ reqs, showCarats, expanded, onToggleExpand, onEdit, onDelete, onToggleComplete, orderCompleted, filter, onFilterChange, canEditRow }) {
  const colSpan = showCarats ? 14 : 11;
  const counts = React.useMemo(() => computeFilterCounts(reqs), [reqs]);
  const filteredReqs = React.useMemo(() => applyFilter(reqs, filter), [reqs, filter]);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Stones &amp; Pearls</div>
          <FilterChips counts={counts} value={filter} onChange={onFilterChange} testIdPrefix="stones-filter" />
        </div>
        <div className="text-[11px] text-slate-400">
          Showing <span className="font-mono tabular-nums text-slate-600">{filteredReqs.length}</span> of{' '}
          <span className="font-mono tabular-nums text-slate-600">{reqs.length}</span> requirement{reqs.length === 1 ? '' : 's'}
        </div>
      </div>
      <div className="overflow-x-auto border border-[var(--divider)] rounded-md">
        <table className="w-full text-sm" data-testid="requirements-stones-table">
          <thead className="bg-[var(--paper-warm)] text-[11px] uppercase tracking-wide text-slate-600">
            <tr>
              <th className="text-left px-2 py-2 font-semibold w-10"></th>
              <th className="text-left px-3 py-2 font-semibold">Material</th>
              <th className="text-left px-3 py-2 font-semibold">Shape</th>
              <th className="text-left px-3 py-2 font-semibold">Size</th>
              <th className="text-left px-3 py-2 font-semibold">Sieves</th>
              <th className="text-right px-3 py-2 font-semibold">Pieces</th>
              <th className="text-right px-3 py-2 font-semibold">Issued</th>
              <th className="text-right px-3 py-2 font-semibold">Balance</th>
              {showCarats && (
                <>
                  <th className="text-right px-3 py-2 font-semibold border-l border-[var(--divider)]">Req Carats</th>
                  <th className="text-right px-3 py-2 font-semibold">Iss Carats</th>
                  <th className="text-right px-3 py-2 font-semibold">Bal Carats</th>
                </>
              )}
              <th className="text-left px-3 py-2 font-semibold border-l border-[var(--divider)]">Remarks</th>
              <th className="text-left px-3 py-2 font-semibold">Status</th>
              <th className="text-center px-3 py-2 font-semibold">Comments</th>
              <th className="text-center px-3 py-2 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--divider)]">
            {filteredReqs.length === 0 && (
              <tr>
                <td colSpan={colSpan} className="px-3 py-6 text-center text-[13px] text-slate-500 italic" data-testid="stones-filter-empty">
                  No requirements match the current filter.
                </td>
              </tr>
            )}
            {filteredReqs.map((r) => (
              <StoneRequirementRow
                key={r.id}
                r={r}
                showCarats={showCarats}
                colSpan={colSpan}
                open={!!expanded[r.id]}
                onToggle={() => onToggleExpand(r.id)}
                onEdit={() => onEdit(r)}
                onDelete={() => onDelete(r)}
                onToggleComplete={() => onToggleComplete(r)}
                orderCompleted={orderCompleted}
                canEdit={canEditRow ? canEditRow(r) : true}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StoneRequirementRow({ r, showCarats, colSpan, open, onToggle, onEdit, onDelete, onToggleComplete, orderCompleted, canEdit = true }) {
  // Wave 3.77 — Prefer the explicit ``r.shape`` field (captured on the
  // requirement itself, independent of the classification master). Fall
  // back to the trailing portion of ``classification_label`` (which is
  // usually formatted as "CODE — Shape Name") for legacy requirements
  // that were created before Wave 3.77.
  const shapeFromLabel = r.classification_label?.includes('—')
    ? r.classification_label.split('—').slice(1).join('—').trim()
    : '';
  const shape = (r.shape && r.shape.trim()) || shapeFromLabel;
  const reqPcs = Number(r.required_pieces || 0);
  const issPcs = Number(r.fulfilled_pieces || 0);
  const balPcs = Math.max(0, reqPcs - issPcs);
  const reqCt = Number(r.required_carats || 0);
  const issCt = Number(r.fulfilled_carats || 0);
  const balCt = Math.max(0, reqCt - issCt);
  const fulCount = (r.fulfillments || []).length;
  const isDone = (r.status || '').toLowerCase() === 'complete';
  return (
    <>
      <tr className={`hover:bg-[var(--paper-warm)] align-top ${open ? 'bg-[var(--paper-warm)]' : ''}`} data-testid={`requirement-row-${r.id}`}>
        <td className="px-2 py-2 align-middle">
          <ExpandCaret open={open} count={fulCount} onClick={onToggle} testId={`requirement-expand-${r.id}`} />
        </td>
        <td className="px-3 py-2">
          <Badge className={`text-[10px] uppercase tracking-wide border ${MATERIAL_TONE[r.material_type]}`}>
            {MATERIAL_LABEL[r.material_type] || r.material_type}
          </Badge>
          {r.classification_label && (
            <div className="mt-1 font-mono text-[11px] text-slate-500">{r.classification_label}</div>
          )}
        </td>
        <td className="px-3 py-2 text-slate-800">{shape || '—'}</td>
        <td className="px-3 py-2 font-mono text-[13px]">{r.size || '—'}</td>
        <td className="px-3 py-2 font-mono text-[13px]">{r.sieves || '—'}</td>
        <td className="px-3 py-2 text-right font-mono tabular-nums">{reqPcs || '—'}</td>
        <td className="px-3 py-2 text-right font-mono tabular-nums req-issued">{issPcs || '—'}</td>
        <td className="px-3 py-2 text-right font-mono tabular-nums">
          <span className={balPcs === 0 && reqPcs > 0 ? 'text-emerald-700 font-semibold' : 'req-balance'}>
            {reqPcs > 0 ? balPcs : '—'}
          </span>
        </td>
        {showCarats && (
          <>
            <td className="px-3 py-2 text-right font-mono tabular-nums border-l border-[var(--divider)]">{fmtNum(reqCt)}</td>
            <td className="px-3 py-2 text-right font-mono tabular-nums req-issued">{fmtNum(issCt)}</td>
            <td className="px-3 py-2 text-right font-mono tabular-nums">
              <span className={balCt === 0 && reqCt > 0 ? 'text-emerald-700 font-semibold' : 'req-balance'}>
                {reqCt > 0 ? fmtNum(balCt) : '—'}
              </span>
            </td>
          </>
        )}
        <td className="px-3 py-2 text-[13px] text-slate-700 max-w-[260px] border-l border-[var(--divider)]">
          {r.description || <span className="text-slate-400 italic">—</span>}
        </td>
        <td className="px-3 py-2">
          <div className="flex items-center gap-1.5 flex-wrap">
            {requirementStatusBadge(r)}
            {requirementAgeBadge(r)}
          </div>
        </td>
        <td className="px-3 py-2 text-center">
          <CommentCalloutBadge
            filterKey={`requirement:${r.id}`}
            spec={{ topic: 'requirement', subtopic_id: r.id }}
            label="View comments on this requirement"
            testId={`requirement-comments-callout-${r.id}`}
            tone="amber"
            size="xs"
          />
        </td>
        <td className="px-3 py-2">
          <div className="flex items-center justify-center gap-1">
            {canEdit ? (
              <>
                <button
                  onClick={onToggleComplete}
                  disabled={orderCompleted}
                  className={`p-1.5 rounded-md border transition-colors ${isDone ? 'border-amber-200 text-amber-700 hover:bg-amber-50' : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'} disabled:opacity-40 disabled:cursor-not-allowed`}
                  title={isDone ? 'Reopen requirement' : 'Mark complete'}
                  data-testid={`requirement-toggle-${r.id}`}
                >
                  <CheckCircle2 size={12} />
                </button>
                <button
                  onClick={onEdit}
                  disabled={orderCompleted}
                  className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Edit"
                  data-testid={`requirement-edit-${r.id}`}
                >
                  <Pencil size={12} />
                </button>
                <button
                  onClick={onDelete}
                  disabled={orderCompleted}
                  className="p-1.5 rounded-md text-rose-500 hover:bg-rose-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Delete"
                  data-testid={`requirement-delete-${r.id}`}
                >
                  <Trash2 size={12} />
                </button>
              </>
            ) : (
              <span className="text-[10px] text-slate-400 italic" data-testid={`requirement-readonly-${r.id}`}>Read-only</span>
            )}
          </div>
        </td>
      </tr>
      {open && <FulfillmentLogSubRow r={r} colSpan={colSpan} isGold={false} />}
      <RequirementCommentsSubRow r={r} colSpan={colSpan} />
    </>
  );
}

function GoldRequirementsTable({ order, reqs, expanded, onToggleExpand, onEdit, onDelete, onToggleComplete, onEditMeasurement, orderCompleted, filter, onFilterChange, reload, canEditRow }) {
  const colSpan = 10;
  const counts = React.useMemo(() => computeFilterCounts(reqs), [reqs]);
  const filteredReqs = React.useMemo(() => applyFilter(reqs, filter), [reqs, filter]);
  // Wave 3.97 — Persistent CAD-vs-Gold shortfall badge. Uses the same
  // computation as the backend so the toast + badge always agree.
  const shortfall = React.useMemo(() => computeGoldShortfallClient(order), [order]);
  const [reconciling, setReconciling] = React.useState(false);
  const handleReconcile = async (action) => {
    if (!order?.id || reconciling) return;
    setReconciling(true);
    try {
      await reconcileGoldFromCad(order.id, action, reload);
    } finally { setReconciling(false); }
  };
  const showShortfallBadge =
    !!shortfall
    && !orderCompleted
    && shortfall.has_gold_requirements
    && !!shortfall.action_suggested;
  const isTopUp = shortfall?.action_suggested === 'top_up';
  const shortfallDeltaAbs = Math.abs(Number(shortfall?.delta_grams || 0));
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Gold</div>
          <FilterChips counts={counts} value={filter} onChange={onFilterChange} testIdPrefix="gold-filter" />
          {showShortfallBadge && (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => handleReconcile(isTopUp ? 'top_up' : 'reduce')}
                    disabled={reconciling}
                    data-testid="gold-shortfall-badge"
                    className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full border transition-colors ${
                      isTopUp
                        ? 'bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100'
                        : 'bg-blue-50 border-blue-200 text-blue-800 hover:bg-blue-100'
                    } ${reconciling ? 'opacity-60 cursor-wait' : ''}`}
                  >
                    {reconciling ? (
                      <Loader2 size={11} className="animate-spin" />
                    ) : (
                      <AlertTriangle size={11} />
                    )}
                    {isTopUp
                      ? `Top-up +${formatGrams(shortfallDeltaAbs)} g`
                      : `Reduce −${formatGrams(shortfallDeltaAbs)} g`}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <div className="text-[11px] leading-snug">
                    {isTopUp ? (
                      <>
                        CAD weight is <b>{formatGrams(shortfall.cad_weight_grams)} g</b> but current Gold required totals <b>{formatGrams(shortfall.gold_required_total_grams)} g</b>.
                        Click to add <b>{formatGrams(shortfallDeltaAbs)} g</b> to the latest pending Gold requirement (or create a new line if none matches).
                      </>
                    ) : (
                      <>
                        CAD weight is <b>{formatGrams(shortfall.cad_weight_grams)} g</b> but current Gold required totals <b>{formatGrams(shortfall.gold_required_total_grams)} g</b>.
                        Click to reduce the latest pending Gold requirement by <b>{formatGrams(shortfallDeltaAbs)} g</b> (capped so already-issued gold is preserved).
                      </>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <div className="text-[11px] text-slate-400">
          Showing <span className="font-mono tabular-nums text-slate-600">{filteredReqs.length}</span> of{' '}
          <span className="font-mono tabular-nums text-slate-600">{reqs.length}</span> requirement{reqs.length === 1 ? '' : 's'}
        </div>
      </div>
      <div className="overflow-x-auto border border-[var(--divider)] rounded-md">
        <table className="w-full text-sm" data-testid="requirements-gold-table">
          <thead className="bg-[var(--paper-warm)] text-[11px] uppercase tracking-wide text-slate-600">
            <tr>
              <th className="text-left px-2 py-2 font-semibold w-10"></th>
              <th className="text-left px-3 py-2 font-semibold">Purity</th>
              <th className="text-right px-3 py-2 font-semibold">Grams Required</th>
              <th className="text-right px-3 py-2 font-semibold">Metal Issued</th>
              <th className="text-right px-3 py-2 font-semibold border-l border-[var(--divider)]">CAD Weight</th>
              <th className="text-right px-3 py-2 font-semibold">CAM Weight</th>
              <th className="text-right px-3 py-2 font-semibold">Ghat Weight</th>
              <th className="text-left px-3 py-2 font-semibold border-l border-[var(--divider)]">Status</th>
              <th className="text-center px-3 py-2 font-semibold">Comments</th>
              <th className="text-center px-3 py-2 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--divider)]">
            {filteredReqs.length === 0 && (
              <tr>
                <td colSpan={colSpan} className="px-3 py-6 text-center text-[13px] text-slate-500 italic" data-testid="gold-filter-empty">
                  No gold requirements match the current filter.
                </td>
              </tr>
            )}
            {filteredReqs.map((r) => (
              <GoldRequirementRow
                key={r.id}
                r={r}
                colSpan={colSpan}
                open={!!expanded[r.id]}
                onToggle={() => onToggleExpand(r.id)}
                onEdit={() => onEdit(r)}
                onDelete={() => onDelete(r)}
                onToggleComplete={() => onToggleComplete(r)}
                onEditMeasurement={() => onEditMeasurement(r)}
                orderCompleted={orderCompleted}
                canEdit={canEditRow ? canEditRow(r) : true}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GoldMeasurementCell({ value, at, byEmail, reference, testId }) {
  const has = value !== null && value !== undefined && value !== '';
  const tip = has
    ? `by ${byEmail || 'unknown'} on ${at ? formatDate(at) : '—'}${reference ? ` (ref: ${reference})` : ''}`
    : 'Not entered yet';
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="inline-flex flex-col items-end" data-testid={testId}>
            <span className={`font-mono tabular-nums ${has ? 'text-[var(--ink-navy)]' : 'text-slate-400 italic'}`}>
              {has ? `${formatGrams(value)} g` : '—'}
            </span>
            {has && (
              <span className="text-[10px] text-slate-400 mt-0.5">
                {at ? formatDate(at) : ''}{byEmail ? ` · ${byEmail.split('@')[0]}` : ''}
              </span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top">
          <div className="text-[11px]">{tip}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function GoldRequirementRow({ r, colSpan, open, onToggle, onEdit, onDelete, onToggleComplete, onEditMeasurement, orderCompleted, canEdit = true }) {
  const reqG = Number(r.required_grams || 0);
  const filG = Number(r.fulfilled_grams || 0);
  const isDone = (r.status || '').toLowerCase() === 'complete';
  const purityText = r.required_purity ? `${r.required_purity}%` : (r.classification_label || 'Any');
  const fulCount = (r.fulfillments || []).length;
  return (
    <>
      <tr className={`hover:bg-[var(--paper-warm)] align-top ${open ? 'bg-[var(--paper-warm)]' : ''}`} data-testid={`requirement-row-${r.id}`}>
        <td className="px-2 py-2 align-middle">
          <ExpandCaret open={open} count={fulCount} onClick={onToggle} testId={`requirement-expand-${r.id}`} />
        </td>
        <td className="px-3 py-2">
          <Badge className={`text-[10px] uppercase tracking-wide border ${MATERIAL_TONE.gold}`}>Gold</Badge>
          <div className="mt-1 font-mono text-[11px] text-slate-500">{purityText}</div>
        </td>
        <td className="px-3 py-2 text-right font-mono tabular-nums">{reqG > 0 ? `${formatGrams(reqG)} g` : '—'}</td>
        <td className="px-3 py-2 text-right">
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="inline-flex flex-col items-end">
                  <span className={`font-mono tabular-nums ${filG > 0 ? 'text-[var(--ink-navy)]' : 'text-slate-400 italic'}`}>
                    {filG > 0 ? `${formatGrams(filG)} g` : '—'}
                  </span>
                  {fulCount > 0 && (
                    <span className="text-[10px] text-slate-400 mt-0.5">{fulCount} issue{fulCount === 1 ? '' : 's'}</span>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent side="top">
                <div className="text-[11px]">
                  Net metal issued (issues − returns), auto-tracked from the Raw Material Ledger.
                  {fulCount > 0 ? ` Expand the row to see all ${fulCount} entr${fulCount === 1 ? 'y' : 'ies'}.` : ''}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </td>
        <td className="px-3 py-2 text-right border-l border-[var(--divider)]">
          <GoldMeasurementCell
            value={r.cad_weight_grams}
            at={r.cad_weight_at}
            byEmail={r.cad_weight_by_email}
            reference={r.cad_weight_reference}
            testId={`req-cad-value-${r.id}`}
          />
        </td>
        <td className="px-3 py-2 text-right">
          <GoldMeasurementCell
            value={r.cam_weight_grams}
            at={r.cam_weight_at}
            byEmail={r.cam_weight_by_email}
            reference={r.cam_weight_reference}
            testId={`req-cam-value-${r.id}`}
          />
        </td>
        <td className="px-3 py-2 text-right">
          <GoldMeasurementCell
            value={r.ghat_weight_grams}
            at={r.ghat_weight_at}
            byEmail={r.ghat_weight_by_email}
            reference={r.ghat_weight_reference}
            testId={`req-ghat-value-${r.id}`}
          />
        </td>
        <td className="px-3 py-2 border-l border-[var(--divider)]">
          <div className="flex items-center gap-1.5 flex-wrap">
            {requirementStatusBadge(r)}
            {requirementAgeBadge(r)}
          </div>
        </td>
        <td className="px-3 py-2 text-center">
          <CommentCalloutBadge
            filterKey={`requirement:${r.id}`}
            spec={{ topic: 'requirement', subtopic_id: r.id }}
            label="View comments on this requirement"
            testId={`requirement-comments-callout-${r.id}`}
            tone="amber"
            size="xs"
          />
        </td>
        <td className="px-3 py-2">
          <div className="flex items-center justify-center gap-1">
            {canEdit ? (
              <>
                <button
                  onClick={onEditMeasurement}
                  disabled={orderCompleted}
                  className="p-1.5 rounded-md border border-amber-200 text-amber-700 hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Edit CAD / CAM / Ghat measurements"
                  data-testid={`req-gold-measurement-edit-${r.id}`}
                >
                  <Sparkles size={12} />
                </button>
                <button
                  onClick={onToggleComplete}
                  disabled={orderCompleted}
                  className={`p-1.5 rounded-md border transition-colors ${isDone ? 'border-amber-200 text-amber-700 hover:bg-amber-50' : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'} disabled:opacity-40 disabled:cursor-not-allowed`}
                  title={isDone ? 'Reopen requirement' : 'Mark complete'}
                  data-testid={`requirement-toggle-${r.id}`}
                >
                  <CheckCircle2 size={12} />
                </button>
                <button
                  onClick={onEdit}
                  disabled={orderCompleted}
                  className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Edit"
                  data-testid={`requirement-edit-${r.id}`}
                >
                  <Pencil size={12} />
                </button>
                <button
                  onClick={onDelete}
                  disabled={orderCompleted}
                  className="p-1.5 rounded-md text-rose-500 hover:bg-rose-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Delete"
                  data-testid={`requirement-delete-${r.id}`}
                >
                  <Trash2 size={12} />
                </button>
              </>
            ) : (
              <span className="text-[10px] text-slate-400 italic" data-testid={`requirement-readonly-${r.id}`}>Read-only</span>
            )}
          </div>
        </td>
      </tr>
      {open && <FulfillmentLogSubRow r={r} colSpan={colSpan} isGold={true} />}
      <RequirementCommentsSubRow r={r} colSpan={colSpan} />
    </>
  );
}

function GoldMeasurementDialog({ open, req, onOpenChange, orderId, onSaved }) {
  const [cad, setCad] = useState('');
  const [cam, setCam] = useState('');
  const [ghat, setGhat] = useState('');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (open && req) {
      setCad(req.cad_weight_grams != null ? String(req.cad_weight_grams) : '');
      setCam(req.cam_weight_grams != null ? String(req.cam_weight_grams) : '');
      setGhat(req.ghat_weight_grams != null ? String(req.ghat_weight_grams) : '');
      setReference('');
    }
  }, [open, req]);

  const submit = async () => {
    if (!req) return;
    // Only send changed / non-blank fields.
    const payload = {};
    if (cad !== '' && Number(cad) !== req.cad_weight_grams) payload.cad_weight_grams = Number(cad);
    if (cam !== '' && Number(cam) !== req.cam_weight_grams) payload.cam_weight_grams = Number(cam);
    if (ghat !== '' && Number(ghat) !== req.ghat_weight_grams) payload.ghat_weight_grams = Number(ghat);
    if (Object.keys(payload).length === 0) {
      toast.info('No changes to save.');
      return;
    }
    payload.reference = reference || '';
    setBusy(true);
    try {
      await api.post(`/orders/${orderId}/requirements/${req.id}/gold-measurement`, payload);
      toast.success('Measurement updated');
      onSaved?.();
    } catch (e) {
      toast.error(extractError(e, 'Failed to update measurement'));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="req-gold-measurement-dialog">
        <DialogHeader>
          <DialogTitle>Gold Measurements</DialogTitle>
          <DialogDescription>
            Update CAD / CAM / Ghat weights for this gold requirement. Each entry is stamped with your email and the current timestamp for audit.
          </DialogDescription>
        </DialogHeader>
        {req && (
          <div className="space-y-3">
            <div className="text-[12px] text-slate-500">
              Requirement: <span className="font-mono text-slate-700">{req.classification_label || '—'}</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>CAD Weight (g)</Label>
                <Input
                  type="number"
                  step="0.001"
                  value={cad}
                  onChange={(e) => setCad(e.target.value)}
                  data-testid="req-gold-measurement-cad"
                />
                {req.cad_weight_grams != null && (
                  <div className="text-[10px] text-slate-400 mt-1">
                    Was {formatGrams(req.cad_weight_grams)} g · {req.cad_weight_at ? formatDate(req.cad_weight_at) : ''}{req.cad_weight_by_email ? ` · ${req.cad_weight_by_email}` : ''}
                  </div>
                )}
              </div>
              <div>
                <Label>CAM Weight (g)</Label>
                <Input
                  type="number"
                  step="0.001"
                  value={cam}
                  onChange={(e) => setCam(e.target.value)}
                  data-testid="req-gold-measurement-cam"
                />
                {req.cam_weight_grams != null && (
                  <div className="text-[10px] text-slate-400 mt-1">
                    Was {formatGrams(req.cam_weight_grams)} g · {req.cam_weight_at ? formatDate(req.cam_weight_at) : ''}{req.cam_weight_by_email ? ` · ${req.cam_weight_by_email}` : ''}
                  </div>
                )}
              </div>
              <div>
                <Label>Ghat Weight (g)</Label>
                <Input
                  type="number"
                  step="0.001"
                  value={ghat}
                  onChange={(e) => setGhat(e.target.value)}
                  data-testid="req-gold-measurement-ghat"
                />
                {req.ghat_weight_grams != null && (
                  <div className="text-[10px] text-slate-400 mt-1">
                    Was {formatGrams(req.ghat_weight_grams)} g · {req.ghat_weight_at ? formatDate(req.ghat_weight_at) : ''}{req.ghat_weight_by_email ? ` · ${req.ghat_weight_by_email}` : ''}
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label>Reference (optional)</Label>
              <Input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="e.g. Rev-2, per approval on 22-Jul, etc."
                data-testid="req-gold-measurement-reference"
              />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="luxury-button" onClick={submit} disabled={busy} data-testid="req-gold-measurement-save">
            {busy ? 'Saving…' : 'Save Measurements'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkUploadResultDialog({ open, result, onOpenChange }) {
  const rows = result?.report || [];
  const summary = result?.summary || {};
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="req-bulk-upload-result-dialog">
        <DialogHeader>
          <DialogTitle>Bulk Upload Report</DialogTitle>
          <DialogDescription>
            Row-by-row outcome of your requirements upload.
            {typeof summary.total_rows === 'number' && (
              <span className="ml-1">
                Processed <strong>{summary.total_rows}</strong> row(s):{' '}
                <span className="text-emerald-700 font-medium">{summary.created || 0} created</span>
                {(summary.errored || 0) > 0 && (
                  <>, <span className="text-rose-700 font-medium">{summary.errored} error(s)</span></>
                )}.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-x-auto border border-[var(--divider)] rounded-md">
          <table className="w-full text-sm">
            <thead className="bg-[var(--paper-warm)] text-[11px] uppercase tracking-wide text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Sheet</th>
                <th className="text-left px-3 py-2">Row</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--divider)]">
              {rows.length === 0 && (
                <tr><td colSpan={4} className="px-3 py-4 text-slate-500 text-center italic">No rows processed.</td></tr>
              )}
              {rows.map((r, i) => (
                <tr key={i}>
                  <td className="px-3 py-2 font-mono text-[12px]">{r.sheet}</td>
                  <td className="px-3 py-2 font-mono text-[12px]">{r.row}</td>
                  <td className="px-3 py-2">
                    {r.status === 'created' && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border text-[10px] uppercase">Created</Badge>}
                    {r.status === 'error' && <Badge className="bg-rose-50 text-rose-700 border-rose-200 border text-[10px] uppercase">Error</Badge>}
                    {r.status === 'skipped' && <Badge className="bg-slate-50 text-slate-600 border-slate-200 border text-[10px] uppercase">Skipped</Badge>}
                  </td>
                  <td className="px-3 py-2 text-[12px] text-slate-700">
                    {r.status === 'created' && <span className="font-mono text-slate-500">id: {r.requirement_id?.slice(0, 8) || '—'}</span>}
                    {r.status === 'error' && <span className="text-rose-700">{r.error}</span>}
                    {r.status === 'skipped' && <span className="text-slate-400 italic">Blank row</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} className="luxury-button" data-testid="req-bulk-upload-close">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Wave 3.64 — Copy Requirements from another order
// ---------------------------------------------------------------------------
// Loads the list of candidate source orders (same company, at least one
// requirement) from `/orders/{id}/requirements/copy-source-candidates` and
// lets the operator pick one via a SearchableSelect. Shows a small preview
// of the source's requirement breakdown (per material) before confirming.
// On submit, POSTs to `/copy-from/{sourceId}` and refreshes the parent order.
// ---------------------------------------------------------------------------
function CopyRequirementsDialog({ open, onOpenChange, orderId, onCopied }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (!open) return;
    setSelectedId('');
    setLoading(true);
    api.get(`/orders/${orderId}/requirements/copy-source-candidates`)
      .then((r) => setCandidates(Array.isArray(r.data) ? r.data : []))
      .catch((e) => toast.error(extractError(e, 'Failed to load candidate orders')))
      .finally(() => setLoading(false));
  }, [open, orderId]);

  const selected = candidates.find((c) => c.id === selectedId);

  const submit = async () => {
    if (!selectedId) {
      toast.warning('Pick a source order to copy from.');
      return;
    }
    setBusy(true);
    try {
      const res = await api.post(`/orders/${orderId}/requirements/copy-from/${selectedId}`);
      const copied = res.data?.summary?.copied_count || 0;
      const srcNo = res.data?.summary?.source_order_no || '';
      toast.success(`Copied ${copied} requirement${copied === 1 ? '' : 's'} from ${srcNo}`);
      onCopied?.();
    } catch (e) {
      toast.error(extractError(e, 'Failed to copy requirements'));
    } finally { setBusy(false); }
  };

  const stageTone = (stage) => {
    const s = (stage || '').toLowerCase();
    if (s === 'completed') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (s === 'issued') return 'bg-sky-50 text-sky-700 border-sky-200';
    if (s === 'in production') return 'bg-amber-50 text-amber-800 border-amber-200';
    if (s === 'quality check') return 'bg-purple-50 text-purple-700 border-purple-200';
    return 'bg-slate-50 text-slate-600 border-slate-200';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="req-copy-dialog">
        <DialogHeader>
          <DialogTitle>Copy Requirements from another Order</DialogTitle>
          <DialogDescription>
            Duplicate all requirements from a previous order into this one. Fulfillment state,
            gold audit weights (CAD/CAM/Ghat), and completion flags are reset — the copied
            requirements start fresh.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Source Order</Label>
            {loading ? (
              <div className="text-sm text-slate-500 py-3 italic">Loading candidate orders…</div>
            ) : candidates.length === 0 ? (
              <div className="text-sm text-slate-500 py-3 italic border border-dashed rounded-md text-center px-3">
                No other orders in this company have requirements to copy from.
              </div>
            ) : (
              <SearchableSelect
                items={candidates}
                value={selectedId}
                onValueChange={setSelectedId}
                itemToLabel={(o) => `${o.order_no}${o.title ? ' — ' + o.title : ''} · ${o.requirement_count} req${o.requirement_count === 1 ? '' : 's'}`}
                searchKeys={['order_no', 'title', 'customer_name', 'goldsmith_name']}
                placeholder="Select an order to copy from…"
                searchPlaceholder="Search by order no / title / customer / goldsmith…"
                testId="req-copy-source-select"
              />
            )}
          </div>
          {selected && (
            <div className="rounded-md border border-[var(--divider)] p-3 bg-[var(--paper-warm)]" data-testid="req-copy-source-preview">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span className="font-mono text-sm text-[var(--ink-navy)]">{selected.order_no}</span>
                <Badge className={`text-[10px] uppercase tracking-wide border ${stageTone(selected.stage)}`}>{selected.stage || 'Unknown'}</Badge>
                {selected.title && <span className="text-sm text-slate-700">· {selected.title}</span>}
              </div>
              <div className="text-[11px] text-slate-500 mb-2">
                {selected.customer_name ? <>Customer: <span className="text-slate-700">{selected.customer_name}</span> · </> : null}
                {selected.goldsmith_name ? <>Goldsmith: <span className="text-slate-700">{selected.goldsmith_name}</span> · </> : null}
                Created: {formatDate(selected.created_at)}
              </div>
              <div className="flex flex-wrap gap-2 text-[11px]">
                {['gold','diamond','color_stone','pearl'].map((m) => {
                  const n = selected.requirement_breakdown?.[m] || 0;
                  if (n === 0) return null;
                  return (
                    <Badge key={m} className={`border ${MATERIAL_TONE[m]}`}>
                      {MATERIAL_LABEL[m]}: <span className="font-mono ml-1">{n}</span>
                    </Badge>
                  );
                })}
                <Badge className="border bg-slate-100 text-slate-700 border-slate-300">
                  Total: <span className="font-mono ml-1">{selected.requirement_count}</span>
                </Badge>
              </div>
              <div className="mt-2 text-[10px] text-slate-500 italic">
                All copied requirements will start with 0 issued and status = Pending. CAD/CAM/Ghat weights are cleared.
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button
            onClick={submit}
            className="luxury-button"
            disabled={busy || !selectedId || loading}
            data-testid="req-copy-submit"
          >
            {busy ? 'Copying…' : (selected ? `Copy ${selected.requirement_count} Requirement${selected.requirement_count === 1 ? '' : 's'}` : 'Copy Requirements')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Multi-Row Issue Dialog (per Order) ----------
function MultiRowIssueDialog({ order, companyId, reload, open: externalOpen, onOpenChange: externalOnOpenChange, hideTrigger = false }) {
  // Wave 3.21 — allow this dialog to be triggered externally (e.g. from the
  // header Actions dropdown). When `open`/`onOpenChange` props are provided
  // we use them; otherwise we fall back to internal state + the inline
  // trigger button.
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = externalOpen !== undefined;
  const open = isControlled ? externalOpen : internalOpen;
  const setOpen = isControlled ? (externalOnOpenChange || (() => {})) : setInternalOpen;
  // Wave 3.99l — Read the user's issue-scope so we only expose Add-Row
  // affordances for materials they can actually issue.
  const { hasMaterialAccess, hasPermission } = useAuth();
  const navigate = useNavigate();
  const canIssueGold = hasMaterialAccess('gold', 'issue');
  const canIssueDiamond = hasMaterialAccess('diamond', 'issue');
  const canIssueColorStone = hasMaterialAccess('color_stone', 'issue');
  const canIssuePearl = hasMaterialAccess('pearl', 'issue');
  // Wave 3.99z — Mountings: either the explicit issue permission OR the
  // Karigar preset (orders.issue_to_goldsmith) unlocks issuing.
  const canIssueMounting = hasPermission('materials.mountings.issue') || hasPermission('orders.issue_to_goldsmith');
  // Phase 16 — separate pools per carat material; gold pool is purities only.
  const [packetsByMat, setPacketsByMat] = useState({ diamond: [], color_stone: [], pearl: [] });
  const [goldEntries, setGoldEntries] = useState([]); // gold rate + purities for issuing gold
  const [mountings, setMountings] = useState([]); // Wave 3.99z
  const [rateInfo, setRateInfo] = useState(null);
  const [rows, setRows] = useState([]);
  // Wave 3.99z — Purity-mismatch confirmation. Set when at least one
  // mounting row's purity does not match the order's jewellery purity.
  // Holds the list of mismatch entries so the operator can review each
  // one before confirming. On confirm we resubmit the FULL payload
  // (goldsmith + mountings) with `acknowledged_purity_mismatch=true`
  // stamped onto every mismatching mounting line so a single memo PDF
  // is generated in one atomic POST.
  const [purityMismatch, setPurityMismatch] = useState(null); // { entries: [ { row, mounting, orderPurityName } ] }
  const [submitting, setSubmitting] = useState(false);

  const loadStocks = async () => {
    const [p, cs, pr, purities, mts] = await Promise.all([
      api.get('/diamonds/packets', { params: { company_id: companyId } }),
      api.get('/color-stones/packets', { params: { company_id: companyId } }).catch(() => ({ data: [] })),
      api.get('/pearls/packets', { params: { company_id: companyId } }).catch(() => ({ data: [] })),
      api.get('/gold/purities', { params: { company_id: companyId } }),
      // Wave 3.99z — Only load mountings if the caller can actually issue them.
      canIssueMounting
        ? api.get('/mountings', { params: { company_id: companyId } }).catch(() => ({ data: { items: [] } }))
        : Promise.resolve({ data: { items: [] } }),
    ]);
    const hasStock = (x) => (x.balance_carats > 0 || x.balance_pieces > 0);
    setPacketsByMat({
      diamond: (p.data || []).filter(hasStock),
      color_stone: (cs.data || []).filter(hasStock),
      pearl: (pr.data || []).filter(hasStock),
    });
    setGoldEntries(purities.data);
    // Only mountings with positive stock are pickable.
    const mtItems = Array.isArray(mts.data?.items) ? mts.data.items : [];
    setMountings(mtItems.filter((m) => m.active !== false && Number(m.stock_grams || 0) > 0));
    // Lookup current gold rate
    try {
      const { data } = await api.get('/gold/rates/lookup', { params: { date: new Date().toISOString(), company_id: companyId } });
      setRateInfo(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { if (open && companyId) loadStocks(); }, [open, companyId]);

  const addRow = (type) => {
    if (type === 'diamond' || type === 'color_stone' || type === 'pearl') {
      setRows([...rows, { _id: Math.random(), type, packet_id: '', carats: '', pieces: '', requirement_id: '', mark_complete: false, note: '', _reqAutoFilled: false }]);
    } else if (type === 'gold') {
      const def = (goldEntries || [])[0];
      setRows([...rows, { _id: Math.random(), type: 'gold', weight_grams: '', purity_id: def?.id || '', requirement_id: '', mark_complete: false, note: '', _reqAutoFilled: false }]);
    } else if (type === 'mounting') {
      // Wave 3.99z — Mounting row: pick a mounting master + issue weight
      // in grams. Sent as a `material_type: 'mounting'` line inside the
      // same `/goldsmith-issues/` payload so a single combined memo PDF
      // is produced. Mountings ADD to jewellery net weight but are
      // EXCLUDED from karigar labour by the backend jewellery module.
      setRows([...rows, { _id: Math.random(), type: 'mounting', mounting_id: '', weight_grams: '', note: '', _purityAck: false }]);
    }
  };
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const updateRow = (i, patch) => setRows(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));

  const orderReqs = (order.requirements || []).filter(r => r.status !== 'complete');
  const isCaratType = (t) => t === 'diamond' || t === 'color_stone' || t === 'pearl';
  const isMountingType = (t) => t === 'mounting';

  // Wave 3.69 — Shape Auto-Fill helper. Given a packet and a material
  // type, return the best-matching pending requirement in this order.
  // Match ranking (best first):
  //   1. Exact `classification_id` match (same shape+carat class).
  //   2. Case-insensitive shape-name match on
  //      `packet.classification.shape_name` vs the shape extracted from
  //      the requirement's `classification_label`.
  // Requirements with no pending balance are ignored (issuing would be
  // pointless). Returns null if nothing matches — caller keeps None.
  const findMatchingRequirement = (packet, materialType) => {
    if (!packet) return null;
    const candidates = orderReqs.filter((rq) => {
      const rqMat = rq.material_type || 'diamond';
      if (rqMat !== materialType) return false;
      const balC = Math.max(0, Number(rq.required_carats || 0) - Number(rq.fulfilled_carats || 0));
      const balP = Math.max(0, Number(rq.required_pieces || 0) - Number(rq.fulfilled_pieces || 0));
      return balC > 0 || balP > 0;
    });
    if (candidates.length === 0) return null;
    // Priority 1: classification_id match.
    const packetClsId = packet.classification_id || packet.classification?.id || null;
    if (packetClsId) {
      const hit = candidates.find((rq) => rq.classification_id && rq.classification_id === packetClsId);
      if (hit) return hit;
    }
    // Priority 2: shape-name match (case-insensitive, trimmed).
    const packetShape = (
      packet.classification?.shape_name
      || packet.classification_shape
      || ''
    ).trim().toLowerCase();
    if (packetShape) {
      const hit = candidates.find((rq) => {
        // Wave 3.77 — Prefer the explicit ``rq.shape`` field, fall
        // back to the trailing part of ``classification_label`` for
        // legacy requirements.
        let shape = (rq.shape || '').trim();
        if (!shape) {
          const label = (rq.classification_label || '').trim();
          shape = label.includes('—')
            ? label.split('—').slice(1).join('—').trim()
            : label;
        }
        return shape && shape.toLowerCase() === packetShape;
      });
      if (hit) return hit;
    }
    return null;
  };

  const submit = async () => {
    if (rows.length === 0) { toast.error('Add at least one row'); return; }
    // Validate each row
    for (const r of rows) {
      if (isCaratType(r.type)) {
        const pool = packetsByMat[r.type] || [];
        const p = pool.find(x => x.id === r.packet_id);
        if (!p) { toast.error('Pick a packet for each row'); return; }
        const ct = Number(r.carats) || 0;
        const pcs = Number(r.pieces) || 0;
        // Wave 3.49 Rule 1 + Rule 2 — both qty > 0; balance check; the
        // source packet's post-issue state must remain consistent
        // ((carats > 0) IFF (pieces > 0)).
        const ruleErr = validateCaratMovement({
          txnCarats: ct, txnPieces: pcs,
          availableCarats: p.balance_carats || 0,
          availablePieces: p.balance_pieces || 0,
          packetNo: p.packet_no,
          context: `issue of ${r.type.replace('_', ' ')} to goldsmith`,
        });
        if (ruleErr) { toast.error(ruleErr); return; }
      } else if (r.type === 'gold') {
        if (!r.purity_id) { toast.error('Gold row: select purity'); return; }
        const g = Number(r.weight_grams) || 0;
        if (g <= 0) { toast.error('Gold row: enter weight'); return; }
      } else if (isMountingType(r.type)) {
        // Wave 3.99z — Mounting row validation.
        if (!r.mounting_id) { toast.error('Mounting row: select a mounting'); return; }
        const m = mountings.find((x) => x.id === r.mounting_id);
        if (!m) { toast.error('Mounting row: pick a valid mounting'); return; }
        const g = Number(r.weight_grams) || 0;
        if (g <= 0) { toast.error('Mounting row: enter weight in grams'); return; }
        if (g - Number(m.stock_grams || 0) > 1e-6) {
          toast.error(`Mounting "${m.name}": not enough stock (${Number(m.stock_grams).toFixed(3)} g available)`);
          return;
        }
      }
    }
    setSubmitting(true);
    // Wave 3.99z — Memo parity: mountings are sent as `material_type: 'mounting'`
    // lines inside the SAME `/goldsmith-issues/` payload as gold + carats,
    // so a single Goldsmith Issue Memo PDF is produced for the whole batch.
    // Before submitting we detect purity mismatches client-side so the
    // operator can acknowledge them up-front (avoids partial persistence
    // + gives a single confirm dialog).
    const goldsmithLines = [];
    const mountingRowRefs = []; // parallel array of the source rows for post-submit reporting
    for (const r of rows) {
      if (isMountingType(r.type)) {
        const m = mountings.find((x) => x.id === r.mounting_id);
        goldsmithLines.push({
          material_type: 'mounting',
          mounting_id: r.mounting_id,
          weight_grams: Number(r.weight_grams),
          order_id: order.id, order_no: order.order_no,
          note: r.note || '',
          // Pre-flight ack: set only when the operator has already
          // confirmed via the mismatch dialog (see acknowledgeAllMismatches).
          acknowledged_purity_mismatch: !!r._purityAck,
          // Non-payload debug refs kept locally so we can compare vs
          // response.lines during finalise (they're stripped below).
          _m: m,
        });
        mountingRowRefs.push(r);
      } else if (isCaratType(r.type)) {
        goldsmithLines.push({
          material_type: r.type, packet_id: r.packet_id,
          carats: Number(r.carats) || 0, pieces: Number(r.pieces) || 0,
          order_id: order.id, order_no: order.order_no,
          requirement_id: r.requirement_id || null,
          mark_requirement_complete: !!r.mark_complete,
          note: r.note || '',
        });
      } else {
        // Gold row.
        goldsmithLines.push({
          material_type: 'gold', purity_id: r.purity_id,
          grams: Number(r.weight_grams),
          order_id: order.id, order_no: order.order_no,
          requirement_id: r.requirement_id || null,
          mark_requirement_complete: !!r.mark_complete,
          note: r.note || '',
        });
      }
    }

    // Wave 3.99z — Client-side purity-mismatch pre-flight. Resolves
    // against the order's jewellery purity (with legacy `gold_purity_id`
    // as fallback). If any mounting row mismatches AND has not already
    // been acknowledged, surface the confirm dialog before hitting the
    // backend. Backend still enforces this on its own (defence in depth).
    const orderPurityId = order.jewellery_purity_id || order.gold_purity_id || null;
    const orderPurityName = order.jewellery_purity_name || order.gold_purity_name || 'unspecified';
    const mismatchEntries = [];
    for (const ln of goldsmithLines) {
      if (ln.material_type !== 'mounting') continue;
      if (ln.acknowledged_purity_mismatch) continue;
      const m = ln._m;
      if (!m || !orderPurityId) continue;
      if (m.purity_id && m.purity_id !== orderPurityId) {
        mismatchEntries.push({
          mounting_id: ln.mounting_id,
          mounting_name: m.name,
          mounting_purity_name: m.purity_name || m.purity_karat || '—',
          order_purity_name: orderPurityName,
        });
      }
    }
    if (mismatchEntries.length > 0) {
      // Park the pending submission on state and let the confirm dialog
      // resume it via acknowledgeAllMismatches().
      setSubmitting(false);
      setPurityMismatch({ entries: mismatchEntries });
      return;
    }

    // Strip local-only debug refs before shipping to the backend.
    const cleanLines = goldsmithLines.map(({ _m, ...ln }) => ln);
    try {
      const payload = {
        goldsmith_id: order.goldsmith_id, company_id: companyId,
        reference: `From order ${order.order_no}`, note: '',
        lines: cleanLines,
      };
      const { data } = await api.post('/goldsmith-issues/', payload);
      finalizeAfterIssue(data, mountingRowRefs.length);
    } catch (e) {
      setSubmitting(false);
      // Backend may still 409 in a race condition (e.g. concurrent
      // purity change). Show the same confirm dialog so the operator
      // can acknowledge & retry.
      const detail = e?.response?.data?.detail;
      const isMismatch = e?.response?.status === 409
        && typeof detail === 'object'
        && detail?.code === 'purity_mismatch';
      if (isMismatch) {
        setPurityMismatch({
          entries: [{
            mounting_id: detail.mounting_id,
            mounting_name: detail.mounting_name,
            mounting_purity_name: detail.mounting_purity_name,
            order_purity_name: detail.order_purity_name || orderPurityName,
          }],
        });
        return;
      }
      toast.error(extractError(e, 'Issue failed'));
    }
  };

  const finalizeAfterIssue = (issueDoc, mountingCount) => {
    setSubmitting(false);
    if (issueDoc && issueDoc.id) {
      const memoNo = issueDoc.issue_memo_no || issueDoc.issue_no || '';
      const mountingsFragment = mountingCount > 0
        ? ` · ${mountingCount} mounting${mountingCount === 1 ? '' : 's'}`
        : '';
      toast.success(`Issue Memo ${memoNo} created${mountingsFragment}`, {
        action: {
          label: 'View Memo',
          onClick: () => navigate(`/issue-memos/${issueDoc.id}`),
        },
      });
    }
    setOpen(false); setRows([]); reload();
  };

  // Wave 3.99z — Operator confirmed the purity mismatch. Re-run submit()
  // with the ack flag pre-stamped on each mismatching row so the whole
  // batch can flow through /goldsmith-issues/ as a single memo.
  const acknowledgeAllMismatches = async () => {
    if (!purityMismatch) return;
    const mismatchIds = new Set((purityMismatch.entries || []).map((e) => e.mounting_id));
    setRows((prev) => prev.map((r) => (
      isMountingType(r.type) && mismatchIds.has(r.mounting_id)
        ? { ...r, _purityAck: true }
        : r
    )));
    setPurityMismatch(null);
    // Defer submit() to the next tick so state updates flush first.
    setTimeout(() => { submit(); }, 0);
  };

  // Helpers for the carat-row block (used for diamond/color_stone/pearl)
  const labelForType = (t) => {
    if (t === 'color_stone') return 'Colour Stone';
    if (t === 'pearl') return 'Pearl';
    if (t === 'gold') return 'Gold';
    if (t === 'mounting') return 'Mounting';
    return 'Diamond';
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button variant="outline" data-testid="order-issue-button">
            <ArrowUpFromLine size={14} className="mr-1.5" /> Issue Material
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>Issue Materials to {order.goldsmith_name}</DialogTitle>
          <DialogDescription>For order <span className="font-mono">{order.order_no}</span>. Add multiple rows — each row issues from a specific item. Only items with available balance are listed.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[65vh] overflow-y-auto">
          {rows.length === 0 && <div className="text-sm text-slate-500 text-center py-8 border border-dashed rounded">No rows yet — add a Diamond / Colour Stone / Pearl / Gold / Mounting row below.</div>}
          {rows.map((r, i) => {
            const isCarat = isCaratType(r.type);
            const isMounting = isMountingType(r.type);
            const currentPool = isCarat ? (packetsByMat[r.type] || []) : [];
            const mountingDoc = isMounting ? mountings.find((m) => m.id === r.mounting_id) : null;
            return (
            <Card key={r._id} className="p-3" data-testid={`mri-row-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <Badge
                  variant="secondary"
                  className={`capitalize ${isMounting ? 'bg-amber-50 text-amber-800 border-amber-200' : ''}`}
                >
                  {isMounting && <Component size={11} className="inline mr-1 -mt-0.5" />}
                  {labelForType(r.type)}
                </Badge>
                <button onClick={() => removeRow(i)} className="text-rose-500 hover:bg-rose-50 p-1 rounded" data-testid={`mri-row-remove-${i}`}><Trash2 size={12} /></button>
              </div>
              {isMounting ? (
                <div className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-6">
                    <Label className="text-[11px]">Mounting</Label>
                    <SearchableSelect
                      items={mountings}
                      value={r.mounting_id}
                      onValueChange={(v) => updateRow(i, { mounting_id: v || '' })}
                      itemToLabel={(m) => `${m.name} · ${m.purity_name || '—'} · ${Number(m.stock_grams || 0).toFixed(3)} g avail`}
                      searchKeys={['name', 'purity_name', 'description']}
                      placeholder={mountings.length === 0 ? 'No mountings with stock' : 'Pick mounting (with stock)'}
                      searchPlaceholder="Search mounting name / purity…"
                      disabled={mountings.length === 0}
                      contentClassName="min-w-[420px] max-w-[620px]"
                      testId={`mri-mounting-${i}`}
                    />
                    {mountingDoc && (
                      <div className="mt-1 text-[10.5px] text-slate-500">
                        Purity: <span className="font-medium text-amber-800">{mountingDoc.purity_name || '—'}</span>
                        {' · '}Stock: <span className="font-mono">{Number(mountingDoc.stock_grams || 0).toFixed(3)} g</span>
                      </div>
                    )}
                  </div>
                  <div className="col-span-3">
                    <Label className="text-[11px]">Weight (g)</Label>
                    <Input
                      type="number"
                      step="0.001"
                      value={r.weight_grams}
                      onChange={(e) => updateRow(i, { weight_grams: e.target.value })}
                      data-testid={`mri-mounting-grams-${i}`}
                      placeholder="e.g., 3.250"
                    />
                  </div>
                  <div className="col-span-3">
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-[10.5px] text-amber-800 leading-tight">
                      <div className="flex items-center gap-1 font-medium mb-0.5">
                        <AlertTriangle size={11} /> Labour excluded
                      </div>
                      Added to jewellery net weight; not charged as karigar labour.
                    </div>
                  </div>
                </div>
              ) : isCarat ? (
                <div className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-5">
                    <Label className="text-[11px]">{labelForType(r.type)} Packet</Label>
                    <SearchableSelect
                      items={currentPool}
                      value={r.packet_id}
                      onValueChange={(v) => {
                        // Wave 3.69 — Shape Auto-Fill. When the operator
                        // picks a packet AND no requirement has been
                        // manually chosen yet, look up a matching-shape
                        // pending requirement and pre-select it. Manual
                        // picks are never overwritten. Clearing the
                        // packet clears the auto-fill flag but preserves
                        // the requirement selection (operator can still
                        // keep the tie explicit).
                        const patch = { packet_id: v || '' };
                        if (v && !r.requirement_id) {
                          const packet = currentPool.find((p) => p.id === v);
                          const match = findMatchingRequirement(packet, r.type);
                          if (match) {
                            patch.requirement_id = match.id;
                            patch._reqAutoFilled = true;
                          }
                        }
                        if (!v) {
                          patch._reqAutoFilled = false;
                        }
                        updateRow(i, patch);
                      }}
                      itemToLabel={(p) => `${p.packet_no} · ${p.classification?.shape_name || p.classification_shape || ''} · ${formatCarats(p.balance_carats)}ct / ${p.balance_pieces}pcs avail`}
                      searchKeys={['packet_no', 'classification_shape', 'description', (p) => p?.classification?.shape_name || '']}
                      placeholder={currentPool.length === 0 ? 'No packets with balance' : 'Pick packet (with balance)'}
                      searchPlaceholder="Search packet no / shape…"
                      disabled={currentPool.length === 0}
                      contentClassName="min-w-[420px] max-w-[620px]"
                      testId={`mri-packet-${i}`}
                    />
                  </div>
                  <div className="col-span-2"><Label className="text-[11px]">Carats</Label><Input type="number" step="0.01" value={r.carats} onChange={(e) => updateRow(i, { carats: e.target.value })} data-testid={`mri-carats-${i}`} /></div>
                  <div className="col-span-2"><Label className="text-[11px]">Pieces</Label><Input type="number" step="1" value={r.pieces} onChange={(e) => updateRow(i, { pieces: e.target.value })} data-testid={`mri-pieces-${i}`} /></div>
                  <div className="col-span-3">
                    <Label className="text-[11px]">Requirement</Label>
                    {(() => {
                      // Wave 3.99c — Diamond requirements shown in the
                      // classification hierarchy order (Polki → Diamonds
                      // → Rose Cuts → Brown Diamonds → Chakri → Other)
                      // so operators see them in the same order as the
                      // Job Card and the Requirements table.
                      const matched = orderReqs
                        .filter(rq => rq.material_type === r.type || (!rq.material_type && r.type === 'diamond'));
                      const ordered = r.type === 'diamond' ? sortDiamondItems(matched) : matched;
                      const reqOptions = ordered
                        .map((rq) => ({
                          // Wave 3.68 — Shape - Size - Sieves · pending X ct / Y pcs
                          id: rq.id,
                          label: formatRequirementDropdownLabel(rq),
                        }));
                      const items = [{ id: '__none__', label: 'None' }, ...reqOptions];
                      return (
                        <SearchableSelect
                          items={items}
                          value={r.requirement_id || '__none__'}
                          onValueChange={(v) => updateRow(i, {
                            requirement_id: v === '__none__' ? '' : v,
                            // Wave 3.69 — Any manual change clears the
                            // auto-fill flag so the hint disappears.
                            _reqAutoFilled: false,
                          })}
                          itemToValue={(it) => it.id}
                          itemToLabel={(it) => it.label}
                          searchKeys={['label']}
                          placeholder="None"
                          searchPlaceholder="Search requirement…"
                          contentClassName="min-w-[420px] max-w-[560px]"
                          testId={`mri-req-${i}`}
                        />
                      );
                    })()}
                    {/* Wave 3.69 — subtle hint after Shape Auto-Fill */}
                    {r._reqAutoFilled && r.requirement_id && (
                      <div
                        className="mt-1 flex items-center gap-1 text-[10px] text-emerald-700"
                        data-testid={`mri-req-automatch-${i}`}
                      >
                        <Sparkles size={10} className="text-emerald-600" />
                        Matched by shape
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-3">
                    <Label className="text-[11px]">Weight (g)</Label>
                    <Input type="number" step="0.001" value={r.weight_grams} onChange={(e) => updateRow(i, { weight_grams: e.target.value })} data-testid={`mri-grams-${i}`} />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-[11px]">Purity</Label>
                    <SearchableSelect
                      items={goldEntries}
                      value={r.purity_id}
                      onValueChange={(v) => updateRow(i, { purity_id: v || '' })}
                      itemToLabel={(p) => `${p.name} (${p.percent}%)`}
                      searchKeys={['name']}
                      placeholder={goldEntries.length === 0 ? 'No purities defined' : 'Select purity'}
                      searchPlaceholder="Search purity…"
                      disabled={goldEntries.length === 0}
                      testId={`mri-purity-${i}`}
                    />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-[11px]">Rate available</Label>
                    <div className="text-xs font-mono text-slate-600">{rateInfo?.available ? `₹${rateInfo.rate_per_gram_999}/g${!rateInfo.is_exact_date ? ' (fallback)' : ''}` : 'No rate'}</div>
                  </div>
                  <div className="col-span-3">
                    <Label className="text-[11px]">Requirement</Label>
                    {(() => {
                      const reqOptions = orderReqs
                        .filter(rq => rq.material_type === 'gold')
                        .map((rq) => {
                          // Wave 3.17.1 — show pending grams alongside requirement name
                          // to disambiguate identical classifications.
                          const label = rq.classification_label || rq.description || 'Gold requirement';
                          const remG = Math.max(0, Number(rq.required_grams || 0) - Number(rq.fulfilled_grams || 0));
                          const suffix = remG > 0
                            ? ` · pending ${formatGrams(remG)} g${rq.required_purity ? ` (${rq.required_purity})` : ''}`
                            : ' · pending none';
                          return { id: rq.id, label: `${label}${suffix}` };
                        });
                      const items = [{ id: '__none__', label: 'None' }, ...reqOptions];
                      return (
                        <SearchableSelect
                          items={items}
                          value={r.requirement_id || '__none__'}
                          onValueChange={(v) => updateRow(i, { requirement_id: v === '__none__' ? '' : v })}
                          itemToValue={(it) => it.id}
                          itemToLabel={(it) => it.label}
                          searchKeys={['label']}
                          placeholder="None"
                          searchPlaceholder="Search requirement…"
                          contentClassName="min-w-[420px] max-w-[560px]"
                          testId={`mri-req-${i}`}
                        />
                      );
                    })()}
                  </div>
                </div>
              )}
              {r.requirement_id && (
                <label className="flex items-center gap-2 mt-2 text-xs">
                  <Checkbox checked={r.mark_complete} onCheckedChange={(v) => updateRow(i, { mark_complete: !!v })} data-testid={`mri-mark-complete-${i}`} />
                  <span>Mark requirement complete after this issue</span>
                </label>
              )}
              <Input className="mt-2" placeholder="Note (optional)" value={r.note} onChange={(e) => updateRow(i, { note: e.target.value })} />
            </Card>
            );
          })}
          <div className="flex gap-2 pt-2 flex-wrap">
            {canIssueGold && (
              <Button variant="outline" size="sm" onClick={() => addRow('gold')} data-testid="mri-add-gold-button"><Coins size={14} className="mr-1.5 text-[var(--gold-icon)]" /> Add Gold Row</Button>
            )}
            {canIssueMounting && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => addRow('mounting')}
                data-testid="mri-add-mounting-button"
                title={mountings.length === 0 ? 'No mountings with stock available' : 'Add a mounting (semi-finished gold piece). Added to net weight, excluded from karigar labour.'}
              >
                <Component size={14} className="mr-1.5 text-[var(--gold-accent,#B08D57)]" /> Add Mounting Row
              </Button>
            )}
            {canIssueDiamond && (
              <Button variant="outline" size="sm" onClick={() => addRow('diamond')} data-testid="mri-add-diamond-button"><Gem size={14} className="mr-1.5 text-[var(--diamond-icon)]" /> Add Diamond Row</Button>
            )}
            {canIssueColorStone && (
              <Button variant="outline" size="sm" onClick={() => addRow('color_stone')} data-testid="mri-add-color-stone-button"><Sparkles size={14} className="mr-1.5 text-[var(--colorstone-icon,#C84A6F)]" /> Add Colour Stone Row</Button>
            )}
            {canIssuePearl && (
              <Button variant="outline" size="sm" onClick={() => addRow('pearl')} data-testid="mri-add-pearl-button"><CircleIcon size={14} className="mr-1.5 text-[var(--pearl-icon,#6B7280)]" /> Add Pearl Row</Button>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} className="luxury-button" data-testid="mri-submit-button" disabled={rows.length === 0 || submitting}>
            {submitting ? 'Issuing…' : 'Issue All Rows'}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Wave 3.99z — Mounting purity mismatch confirmation. Purity
          mismatches are detected client-side (with a defensive backend
          check as safety net). Operator can Cancel (keep the batch
          unissued so they can adjust the row) or Proceed (which stamps
          `acknowledged_purity_mismatch=true` on each mismatching row
          and resubmits the whole batch through /goldsmith-issues/ so a
          single memo PDF is produced). */}
      <AlertDialog
        open={!!purityMismatch}
        onOpenChange={(v) => !v && setPurityMismatch(null)}
      >
        <AlertDialogContent data-testid="mri-mounting-purity-mismatch-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="text-amber-600" size={18} />
              Confirm purity mismatch
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  {(purityMismatch?.entries || []).length === 1
                    ? 'One mounting has a different gold purity from this order.'
                    : `${(purityMismatch?.entries || []).length} mountings have a different gold purity from this order.`}
                  {' '}Proceeding will still issue them and adjust net weight, but the mismatch is recorded permanently in the memo and ledger.
                </p>
                <ul className="mt-2 space-y-1 text-[12.5px] text-slate-700 max-h-40 overflow-y-auto border border-slate-200 rounded-md p-2 bg-slate-50/50">
                  {(purityMismatch?.entries || []).map((entry, idx) => (
                    <li key={idx} className="flex items-start gap-2" data-testid={`mri-mismatch-item-${idx}`}>
                      <Component size={12} className="mt-0.5 text-amber-700" />
                      <span>
                        <span className="font-medium">{entry.mounting_name || 'Mounting'}</span>
                        {' — is '}
                        <span className="font-medium text-amber-800">{entry.mounting_purity_name || '—'}</span>
                        {', order purity is '}
                        <span className="font-medium text-amber-800">{entry.order_purity_name || 'unspecified'}</span>
                        {'.'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={submitting}
              data-testid="mri-mounting-purity-mismatch-cancel"
              onClick={() => setPurityMismatch(null)}
            >
              Cancel — don&apos;t issue mismatched
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={submitting}
              onClick={acknowledgeAllMismatches}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="mri-mounting-purity-mismatch-proceed"
            >
              {submitting ? 'Issuing…' : 'Proceed anyway'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

// ============================================================================
// Wave 3.22 — Received Jewellery against this order
// ----------------------------------------------------------------------------
// Lists every jewellery doc whose `source_order_id` is this order. Surfaces
// a quick totals strip (gross + net + labour + total) and a "View BOM"
// link that deep-links to the JewelleryDetail page where the existing BOM
// renderer + Print BOM PDF live.
// ============================================================================
function ReceivedJewellerySection({ orderId }) {
  const { effectiveCompanyId } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get('/jewellery', {
          params: { order_id: orderId, company_id: effectiveCompanyId, limit: 50 },
        });
        if (!cancelled) setList(res.data || []);
      } catch (e) {
        if (!cancelled) {
          // Empty list on error — section just won't render
          setList([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orderId, effectiveCompanyId]);

  if (!loading && list.length === 0) {
    return null;
  }

  return (
    <Card className="p-5" data-testid="received-jewellery-section">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <div className="font-medium text-[var(--ink-navy)] flex items-center gap-2">
            <Diamond size={14} className="text-[var(--ink-navy)]" />
            Received Jewellery
            {list.length > 0 && (
              <Badge variant="outline" className="text-[10px] font-mono">
                {list.length} piece{list.length === 1 ? '' : 's'}
              </Badge>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Finished jewellery received against this order. Click <span className="font-medium">View BOM</span> for the
            full bill of materials and the printable PDF.
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 py-4 text-center" data-testid="received-jewellery-loading">
          Loading received jewellery…
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="received-jewellery-list">
          {list.map((j) => (
            <div
              key={j.id}
              className="border border-[var(--divider)] rounded-md p-3 bg-white"
              data-testid={`received-jewellery-card-${j.id}`}
            >
              <div className="flex items-start gap-3">
                {j.image ? (
                  <img
                    src={j.image}
                    alt={j.title || j.jewellery_no}
                    className="w-16 h-16 object-cover rounded border border-[var(--divider)]"
                  />
                ) : (
                  <div className="w-16 h-16 flex items-center justify-center rounded border border-[var(--divider)] bg-slate-50 text-slate-300">
                    <Diamond size={24} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <Link
                      to={`/jewellery/${j.id}`}
                      className="font-mono text-[12px] bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)] px-2 py-0.5 rounded hover:underline"
                      data-testid={`received-jewellery-no-link-${j.id}`}
                    >
                      {j.jewellery_no}
                    </Link>
                    {j.status === 'cancelled' && (
                      <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[10px]">Cancelled</Badge>
                    )}
                  </div>
                  <div className="text-sm font-medium text-[var(--ink-navy)] truncate mt-1" title={j.title || ''}>
                    {j.title || <span className="italic text-slate-400">Untitled jewellery</span>}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Received {formatDateTime(j.received_at)} by {j.received_by_email}
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                    <div className="flex items-center gap-1.5">
                      <span className="text-slate-500">Gross</span>
                      <span className="font-mono tabular-nums">{formatGrams(j.gross_weight_grams || 0)} g</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-slate-500">Net</span>
                      <span className="font-mono tabular-nums">{formatGrams(j.net_weight_grams || 0)} g</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-slate-500">Labour</span>
                      <span className="font-mono tabular-nums text-emerald-700">{formatINR(j.labour_amount || 0)}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-slate-500">Total</span>
                      <span className="font-mono tabular-nums font-semibold text-[var(--ink-navy)]">{formatINR(j.total_amount || 0)}</span>
                    </div>
                  </div>
                  <div className="mt-2">
                    <Link
                      to={`/jewellery/${j.id}`}
                      className="inline-flex items-center gap-1 text-[11px] text-[var(--ink-navy)] hover:underline font-medium"
                      data-testid={`received-jewellery-view-bom-${j.id}`}
                    >
                      <FileText size={11} /> View BOM
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}


// ============================================================================
// Wave 3.82 / 3.83 — Material Procurement Section
// ----------------------------------------------------------------------------
// Shows the four parallel material procurement tracks (Gold / Diamond /
// Colour Stone / Pearl) with SLA status, elapsed time, delay chips, and
// per-track notify recipients. Each track card has a stable anchor id
// (`procurement-gold`, `procurement-diamond`, `procurement-color-stone`,
// `procurement-pearl`) so notifications can deep-link straight to the
// relevant section on the Order Detail page. On mount / hash change we
// smooth-scroll to the target and apply a temporary ring highlight.
// ============================================================================
const PROC_MATERIAL_META = {
  gold:        { label: 'Gold',         hint: 'Starts on CAM confirm',              icon: '🟡' },
  diamond:     { label: 'Diamond',      hint: 'Starts on first diamond requirement', icon: '💎' },
  color_stone: { label: 'Colour Stone', hint: 'Starts on first colour stone requirement', icon: '🔷' },
  pearl:       { label: 'Pearl',        hint: 'Starts on first pearl requirement',   icon: '⚪️' },
};
const PROC_KINDS = ['gold', 'diamond', 'color_stone', 'pearl'];
const PROC_ANCHORS = {
  gold:        'procurement-gold',
  diamond:     'procurement-diamond',
  color_stone: 'procurement-color-stone',
  pearl:       'procurement-pearl',
};

function formatHoursShort(hrs) {
  const n = Number(hrs || 0);
  if (n <= 0) return '—';
  if (n < 1) return `${Math.round(n * 60)}m`;
  if (n < 48) return `${n.toFixed(1)}h`;
  return `${(n / 24).toFixed(1)}d`;
}

function MaterialProcurementSection({ order, companyId, reload }) {
  const [tracks, setTracks] = useState({});
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const cardRefs = useRef({});
  // Wave 3.99l — Filter procurement track cards by the user's material
  // ``see`` scope so restricted materials disappear entirely.
  const { hasMaterialAccess } = useAuth();
  const visibleKinds = React.useMemo(
    () => PROC_KINDS.filter((k) => hasMaterialAccess(k, 'see')),
    [hasMaterialAccess],
  );

  const load = React.useCallback(async () => {
    if (!order?.id) return;
    setLoading(true);
    try {
      const [detail, us] = await Promise.all([
        api.get(`/production-timeline/orders/${order.id}`),
        companyId ? api.get('/production-timeline/users', { params: { company_id: companyId } }) : Promise.resolve({ data: [] }),
      ]);
      setTracks(detail.data?.procurement || {});
      setUsers(us.data || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [order?.id, companyId]);

  useEffect(() => { load(); }, [load, order?.updated_at]);

  // Wave 3.82 — Deep-link scroll + highlight on hash change.
  useEffect(() => {
    if (loading) return;
    const hash = (location.hash || '').replace('#', '');
    if (!hash) return;
    const el = cardRefs.current[hash];
    if (!el) return;
    // Small delay to let layout settle
    const t = setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-[var(--gold-accent)]', 'ring-offset-2');
      setTimeout(() => {
        el.classList.remove('ring-2', 'ring-[var(--gold-accent)]', 'ring-offset-2');
      }, 2400);
    }, 150);
    return () => clearTimeout(t);
  }, [loading, location.hash]);

  const scopeOn = order?.sla_scope?.procurement !== false;

  return (
    <Card className="p-5" data-testid="material-procurement-section">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="font-medium text-[var(--ink-navy)] flex items-center gap-2">
            <Package size={16} className="text-[var(--gold-accent)]" />
            Material Procurement
          </div>
          <div className="text-[11px] text-slate-500">
            Parallel raw-material sourcing tracks. Gold auto-starts on CAM confirm; other materials start on the first requirement.
          </div>
        </div>
        {!scopeOn && (
          <span
            className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded border bg-rose-50 text-rose-700 border-rose-200"
            title="Procurement SLA tracking is OFF for this order — delay alerts are suppressed"
            data-testid="procurement-scope-off-chip"
          >
            <BellOff size={10} /> Procurement SLA OFF
          </span>
        )}
      </div>

      {loading ? (
        <div className="text-center text-sm text-slate-500 py-6">Loading procurement tracks…</div>
      ) : visibleKinds.length === 0 ? (
        <div className="text-center text-sm text-slate-500 py-6" data-testid="material-procurement-no-access">
          No procurement tracks visible for your material access.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {visibleKinds.map((kind) => {
            const t = tracks[kind] || {};
            const meta = PROC_MATERIAL_META[kind];
            const anchor = PROC_ANCHORS[kind];
            const notifyIds = t.effective_notify_user_ids || t.notify_user_ids || [];
            const notifyUsersRes = users.filter((u) => notifyIds.includes(u.id));
            const status = t.status || 'not_started';
            const isActive = status === 'active';
            const isCompleted = status === 'completed';
            const isNoReq = status === 'no_requirements';
            const isDelayed = !!t.is_delayed;
            const isSevere = !!t.is_severe;
            const isAtRisk = !!t.is_at_risk;
            // Wave 3.87 — Card colour is now driven by the backend
            // ``ui_colour`` field so it can also account for Raw-Material
            // overrides. Falls back to the local classification for older
            // response shapes.
            const uiColour = t.ui_colour || (
              isCompleted ? 'green'
                : isNoReq ? 'grey'
                : isSevere ? 'red'
                : (isDelayed || isAtRisk) ? 'amber'
                : isActive ? 'blue'
                : 'grey'
            );
            const forcedComplete = isCompleted && t.override_reason === 'raw_material_ticked';

            const statusPill = isCompleted
              ? {
                  text: forcedComplete ? 'Completed · via Raw Material' : 'Completed',
                  cls:  'bg-emerald-50 text-emerald-700 border-emerald-200',
                }
              : uiColour === 'grey'
                ? { text: isNoReq ? 'No requirements' : 'Not started', cls: 'bg-slate-100 text-slate-600 border-slate-200' }
                : uiColour === 'red'
                  ? { text: 'Critical · 2× SLA', cls: 'bg-rose-50 text-rose-700 border-rose-200' }
                  : uiColour === 'amber'
                    ? { text: isDelayed ? 'Delayed' : 'At risk', cls: 'bg-amber-50 text-amber-800 border-amber-200' }
                    : { text: 'Active', cls: 'bg-blue-50 text-blue-700 border-blue-200' };

            const bg = uiColour === 'green'
              ? 'bg-emerald-50/50 border-emerald-200'
              : uiColour === 'red'
                ? 'bg-rose-50/50 border-rose-300'
                : uiColour === 'amber'
                  ? 'bg-amber-50/50 border-amber-200'
                  : uiColour === 'blue'
                    ? 'bg-blue-50/40 border-blue-200'
                    : 'bg-slate-50/50 border-slate-200';

            const rc = t.requirement_counts || { total: 0, completed: 0 };
            const startedAt = t.started_at ? formatDateTime(t.started_at) : null;
            const doneAt = t.done_at ? formatDateTime(t.done_at) : null;

            return (
              <div
                key={kind}
                id={anchor}
                ref={(el) => { cardRefs.current[anchor] = el; }}
                className={`rounded-md border ${bg} p-3 transition-shadow scroll-mt-24`}
                data-testid={`procurement-card-${kind}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[13px] font-medium text-[var(--ink-navy)]">{meta.label}</span>
                      {isDelayed && <AlertTriangle size={12} className={isSevere ? 'text-rose-600' : 'text-amber-600'} />}
                    </div>
                    <div className="text-[10px] text-slate-500">{meta.hint}</div>
                  </div>
                  <span className={`shrink-0 inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded border ${statusPill.cls}`}>
                    {statusPill.text}
                  </span>
                </div>

                <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
                  <div>
                    <div className="text-[9px] uppercase tracking-wide text-slate-400">SLA</div>
                    <div className="font-mono tabular-nums text-slate-700">{t.sla_days ?? '—'} d</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-wide text-slate-400">Elapsed</div>
                    <div className={`font-mono tabular-nums ${isSevere ? 'text-rose-700' : isDelayed ? 'text-amber-700' : 'text-slate-700'}`}>
                      {formatHoursShort(t.hours)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-wide text-slate-400">Reqs</div>
                    <div className="font-mono tabular-nums text-slate-700">
                      {rc.total > 0 ? `${rc.completed}/${rc.total}` : '—'}
                    </div>
                  </div>
                </div>

                {(startedAt || doneAt) && (
                  <div className="mt-2 text-[10px] text-slate-500 space-y-0.5">
                    {startedAt && (
                      <div>Started: <span className="text-slate-700">{startedAt}</span></div>
                    )}
                    {doneAt && (
                      <div>Completed: <span className="text-slate-700">{doneAt}</span></div>
                    )}
                  </div>
                )}

                <div className="mt-2 border-t border-[var(--divider)] pt-2 flex items-center gap-1.5 flex-wrap">
                  <UsersIcon size={11} className="text-slate-400" />
                  {notifyUsersRes.length === 0 ? (
                    <span className="text-[10px] text-slate-400 italic">No recipients configured</span>
                  ) : (
                    <>
                      <span className="text-[10px] text-slate-500">Notifies:</span>
                      {notifyUsersRes.slice(0, 4).map((u) => (
                        <span
                          key={u.id}
                          className="inline-block text-[10px] px-1.5 py-0.5 rounded border border-[var(--divider)] bg-white text-slate-700"
                          title={u.email}
                        >
                          {u.name || u.email}
                        </span>
                      ))}
                      {notifyUsersRes.length > 4 && (
                        <span className="text-[10px] text-slate-500">+{notifyUsersRes.length - 4}</span>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

